import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'sonner';
import { useDemoMode } from '../context/DemoContext';
import {
  Search, Plus, Users, Building, Mail, Phone, Globe, MapPin, 
  Target, TrendingUp, Calendar, CheckCircle, X, MoreVertical,
  Clock, Tag, Send, Trash2, Edit, DollarSign, FileText, 
  MessageSquare, Activity, RefreshCw, ChevronDown, ChevronRight, ChevronUp, ChevronLeft,
  Briefcase, Zap, Eye, Upload, Download, Filter, BarChart3,
  ArrowRight, Sparkles, ExternalLink, Loader2, Copy, Check,
  AlertCircle, GripVertical, Lock, Settings, Palette, RotateCcw,
  Folder, FolderPlus, FolderOpen, FileImage, Camera, ScanLine,
  SendHorizonal, ListChecks, FolderInput, Rocket, UserPlus,
  LayoutGrid, List as ListIcon
} from 'lucide-react';

const API_URL = window.location.origin;

// Pipeline stage configuration
const PIPELINE_STAGES = [
  { id: 'new', label: 'New', color: 'bg-blue-500', textColor: 'text-blue-400' },
  { id: 'contacted', label: 'Contacted', color: 'bg-yellow-500', textColor: 'text-yellow-400' },
  { id: 'replied', label: 'Replied', color: 'bg-green-500', textColor: 'text-green-400' },
  { id: 'qualified', label: 'Qualified', color: 'bg-cyan-500', textColor: 'text-cyan-400' },
  { id: 'call_scheduled', label: 'Call Scheduled', color: 'bg-purple-500', textColor: 'text-purple-400' },
  { id: 'proposal_sent', label: 'Proposal Sent', color: 'bg-pink-500', textColor: 'text-pink-400' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-orange-500', textColor: 'text-orange-400' },
  { id: 'closed_won', label: 'Closed Won', color: 'bg-emerald-500', textColor: 'text-emerald-400' },
  { id: 'closed_lost', label: 'Closed Lost', color: 'bg-red-500', textColor: 'text-red-400' },
];

const INDUSTRIES = [
  'All', 'Technology', 'Healthcare', 'Finance', 'Real Estate', 'Retail', 
  'Manufacturing', 'Education', 'Marketing', 'Legal', 'Consulting', 'Entertainment', 'Other'
];

const COMPANY_SIZES = [
  { value: 'All', label: 'All Sizes' },
  { value: '1-10', label: '1-10 employees' },
  { value: '10-50', label: '10-50 employees' },
  { value: '50-200', label: '50-200 employees' },
  { value: '200+', label: '200+ employees' },
];

const PRIORITIES = ['A', 'B', 'C'];

// Helper function to format time from 24-hour to 12-hour format
const formatTimeTo12Hour = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// ============== COMPONENTS ==============

const WeeklyMissionPanel = ({ mission }) => {
  return (
    <div className="bg-black border border-gray-700 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
      <h2 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
        <Target className="text-cyan-400" size={20} />
        Weekly Mission
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <div className="bg-gray-900 rounded-lg p-3 sm:p-4 border border-gray-800">
          <div className="text-2xl sm:text-3xl font-bold text-cyan-400">{mission.new_leads_this_week}</div>
          <div className="text-[10px] sm:text-xs text-gray-400 mt-1">New Leads This Week</div>
          {mission.leads_needed > 0 && (
            <div className="text-[10px] sm:text-xs text-yellow-400 mt-1">Need {mission.leads_needed} more</div>
          )}
        </div>
        <div className="bg-gray-900 rounded-lg p-3 sm:p-4 border border-gray-800">
          <div className="text-2xl sm:text-3xl font-bold text-blue-400">{mission.emails_scheduled}</div>
          <div className="text-[10px] sm:text-xs text-gray-400 mt-1">Emails Scheduled</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-3 sm:p-4 border border-gray-800">
          <div className="text-2xl sm:text-3xl font-bold text-green-400">{mission.replies}</div>
          <div className="text-[10px] sm:text-xs text-gray-400 mt-1">Replies</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-3 sm:p-4 border border-gray-800">
          <div className="text-2xl sm:text-3xl font-bold text-purple-400">{mission.calls_booked}</div>
          <div className="text-[10px] sm:text-xs text-gray-400 mt-1">Calls Booked</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-3 sm:p-4 border border-gray-800">
          <div className="text-xl sm:text-3xl font-bold text-emerald-400">
            ${(mission.pipeline_value || 0).toLocaleString()}
          </div>
          <div className="text-[10px] sm:text-xs text-gray-400 mt-1">Pipeline Value</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-3 sm:p-4 border border-gray-800">
          <div className="text-xl sm:text-3xl font-bold text-yellow-400">
            ${(mission.won_value || 0).toLocaleString()}
          </div>
          <div className="text-[10px] sm:text-xs text-gray-400 mt-1">Won Revenue</div>
        </div>
      </div>
    </div>
  );
};

const TodaysActionsPanel = ({ actions, onRefresh }) => {
  return (
    <div className="bg-black border border-gray-700 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
          <Zap className="text-yellow-400" size={20} />
          Today's Actions
        </h2>
        <button
          onClick={onRefresh}
          className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
        >
          <RefreshCw size={16} />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {/* Follow-ups Due */}
        <div className="bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/30 rounded-lg p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-2 sm:mb-3">
            <Clock className="text-red-400" size={16} />
            <span className="text-red-400 font-medium text-sm">Follow-ups Due</span>
            <span className="ml-auto bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full text-xs">
              {actions.followups_due}
            </span>
          </div>
          {actions.followup_leads?.length > 0 ? (
            <div className="space-y-1.5 sm:space-y-2">
              {actions.followup_leads.slice(0, 3).map((lead, idx) => (
                <div key={idx} className="text-xs sm:text-sm text-gray-300 truncate">
                  {lead.company?.name || 'Unknown Company'}
                </div>
              ))}
              {actions.followups_due > 3 && (
                <div className="text-xs text-gray-500">+{actions.followups_due - 3} more</div>
              )}
            </div>
          ) : (
            <div className="text-xs sm:text-sm text-gray-500">No follow-ups due today</div>
          )}
        </div>

        {/* Leads to Research */}
        <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/30 rounded-lg p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-2 sm:mb-3">
            <Search className="text-blue-400" size={16} />
            <span className="text-blue-400 font-medium text-sm">Needs Research</span>
            <span className="ml-auto bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full text-xs">
              {actions.leads_to_research}
            </span>
          </div>
          {actions.research_leads?.length > 0 ? (
            <div className="space-y-1.5 sm:space-y-2">
              {actions.research_leads.slice(0, 3).map((lead, idx) => (
                <div key={idx} className="text-xs sm:text-sm text-gray-300 truncate">
                  {lead.company?.name || 'Unknown Company'}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs sm:text-sm text-gray-500">All leads researched</div>
          )}
        </div>

        {/* Drafts Pending */}
        <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/30 rounded-lg p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-2 sm:mb-3">
            <FileText className="text-purple-400" size={16} />
            <span className="text-purple-400 font-medium text-sm">Drafts Pending</span>
            <span className="ml-auto bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full text-xs">
              {actions.drafts_pending}
            </span>
          </div>
          {actions.pending_drafts?.length > 0 ? (
            <div className="space-y-1.5 sm:space-y-2">
              {actions.pending_drafts.slice(0, 3).map((item, idx) => (
                <div key={idx} className="text-xs sm:text-sm text-gray-300 truncate">
                  {item.company?.name || 'Unknown'}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs sm:text-sm text-gray-500">No pending drafts</div>
          )}
        </div>
      </div>
    </div>
  );
};

const MetricsPanel = ({ metrics }) => {
  return (
    <div className="bg-black border border-gray-700 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
      <h2 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
        <BarChart3 className="text-green-400" size={20} />
        Performance Metrics
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
        <div className="text-center">
          <div className="text-2xl sm:text-3xl font-bold text-white">{metrics.total_leads}</div>
          <div className="text-[10px] sm:text-xs text-gray-400">Total Leads</div>
        </div>
        <div className="text-center">
          <div className="text-2xl sm:text-3xl font-bold text-blue-400">{metrics.contacted}</div>
          <div className="text-[10px] sm:text-xs text-gray-400">Contacted</div>
        </div>
        <div className="text-center">
          <div className="text-2xl sm:text-3xl font-bold text-green-400">{metrics.replied}</div>
          <div className="text-[10px] sm:text-xs text-gray-400">Replied</div>
        </div>
        <div className="text-center">
          <div className="text-2xl sm:text-3xl font-bold text-purple-400">{metrics.reply_rate}%</div>
          <div className="text-[10px] sm:text-xs text-gray-400">Reply Rate</div>
        </div>
        <div className="text-center col-span-2 sm:col-span-1">
          <div className="text-2xl sm:text-3xl font-bold text-emerald-400">{metrics.conversion_rate}%</div>
          <div className="text-[10px] sm:text-xs text-gray-400">Conversion</div>
        </div>
      </div>
    </div>
  );
};

// Company Modal
const CompanyModal = ({ isOpen, onClose, company, onSave, onDelete, mode = 'create', categoryOptions = [] }) => {
  const [form, setForm] = useState({
    name: '',
    website: '',
    industry: '',
    location: '',
    size: '',
    notes: '',
    lead_source: '',
    priority: 'B',
    budget_signals: [],
    logo_url: '',
    // Contact fields - separate first and last name
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    contact_phone: '',
    contact_title: ''
  });
  const [isEnriching, setIsEnriching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingLogo, setIsFetchingLogo] = useState(false);
  
  // Document scanning state
  const [isScanning, setIsScanning] = useState(false);
  const [scanFile, setScanFile] = useState(null);
  const [scanPreview, setScanPreview] = useState(null);
  const [extractedContacts, setExtractedContacts] = useState([]);

  // Get available categories for dropdown (exclude 'all' and 'other')
  // Use useMemo to recalculate when categoryOptions changes
  const availableCategories = useMemo(() => {
    if (!categoryOptions || !Array.isArray(categoryOptions) || categoryOptions.length === 0) {
      return [];
    }
    return categoryOptions.filter(c => c && c.id && c.id !== 'all' && c.id !== 'other');
  }, [categoryOptions]);

  useEffect(() => {
    if (company && mode === 'edit') {
      // Get primary contact if exists
      const primaryContact = company.primary_contact || company.contacts?.[0] || {};
      // Split contact name into first and last
      const contactNameParts = (primaryContact.name || '').split(' ');
      const firstName = contactNameParts[0] || '';
      const lastName = contactNameParts.slice(1).join(' ') || '';
      
      setForm({
        name: company.name || company.company_name || '',
        website: company.website || '',
        industry: company.industry || '',
        location: company.location || '',
        size: company.size || company.company_size || '',
        notes: company.notes || '',
        lead_source: company.lead_source || '',
        priority: company.priority || 'B',
        budget_signals: company.budget_signals || [],
        logo_url: company.logo_url || '',
        contact_first_name: primaryContact.first_name || firstName,
        contact_last_name: primaryContact.last_name || lastName,
        contact_email: primaryContact.email || '',
        contact_phone: primaryContact.phone || '',
        contact_title: primaryContact.title || ''
      });
    } else {
      setForm({
        name: '',
        website: '',
        industry: '',
        location: '',
        size: '',
        notes: '',
        lead_source: '',
        priority: 'B',
        budget_signals: [],
        logo_url: '',
        contact_first_name: '',
        contact_last_name: '',
        contact_email: '',
        contact_phone: '',
        contact_title: ''
      });
    }
    // Reset scan state when modal opens
    setScanFile(null);
    setScanPreview(null);
    setExtractedContacts([]);
  }, [company, mode, isOpen]);

  const handleEnrich = async () => {
    if (!company?.id) return;
    setIsEnriching(true);
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/companies/${company.id}/enrich`, {
        method: 'POST'
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Company enriched with website data!');
        if (data.scraped_data) {
          setForm(prev => ({
            ...prev,
            notes: `${prev.notes}\n\n--- Scraped Data ---\nHeadline: ${data.scraped_data.headline || 'N/A'}\nDescription: ${data.scraped_data.description || 'N/A'}`
          }));
        }
      } else {
        toast.error(data.detail || 'Failed to enrich company');
      }
    } catch (error) {
      toast.error('Failed to enrich company');
    } finally {
      setIsEnriching(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setIsSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle file selection for scanning
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, WebP image or PDF file');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    
    setScanFile(file);
    setExtractedContacts([]);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setScanPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setScanPreview(null);
    }
  };
  
  // Scan document with AI
  const handleScanDocument = async () => {
    if (!scanFile) return;
    
    setIsScanning(true);
    try {
      // Convert file to base64
      const toBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          // Remove the data URL prefix to get just the base64
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      
      const fileBase64 = await toBase64(scanFile);
      
      const response = await fetch(`${API_URL}/api/lead-gen/parse-document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_base64: fileBase64,
          file_type: scanFile.type,
          filename: scanFile.name
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        if (data.contacts && data.contacts.length > 0) {
          setExtractedContacts(data.contacts);
          toast.success(`Extracted ${data.contacts.length} contact(s)!`);
        } else {
          toast.error('No contact information found in the document');
        }
      } else {
        toast.error(data.message || data.detail || 'Failed to parse document');
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Failed to scan document');
    } finally {
      setIsScanning(false);
    }
  };
  
  // Apply extracted contact data to form
  const applyExtractedData = (contact) => {
    setForm(prev => ({
      ...prev,
      name: contact.company || prev.name,
      website: contact.website || prev.website,
      location: contact.location || prev.location,
      // Populate contact fields from extracted data
      contact_name: contact.name || prev.contact_name,
      contact_email: contact.email || prev.contact_email,
      contact_phone: contact.phone || prev.contact_phone,
      contact_title: contact.title || prev.contact_title,
      notes: prev.notes ? `${prev.notes}\n\nExtracted: ${contact.name || ''} - ${contact.email || ''}` 
        : (contact.notes || '')
    }));
    toast.success('Contact data applied!');
  };

  // Fetch company logo from website/domain
  const handleFetchLogo = async () => {
    if (!form.website && !form.name) {
      toast.error('Enter a website or company name to fetch logo');
      return;
    }
    
    setIsFetchingLogo(true);
    try {
      // Try to get domain from website or company name
      let domain = form.website;
      if (!domain) {
        // Use company name to guess domain
        domain = form.name.toLowerCase().replace(/[^a-z0-9]/g, '') + '.com';
      }
      
      const response = await fetch(`${API_URL}/api/lead-gen/fetch-logo-by-domain?domain=${encodeURIComponent(domain)}`);
      const data = await response.json();
      
      if (data.success && data.logo_url) {
        setForm(prev => ({ ...prev, logo_url: data.logo_url }));
        toast.success('Logo found!');
      } else {
        toast.error(data.message || 'Logo not found for this domain');
      }
    } catch (error) {
      toast.error('Failed to fetch logo');
    } finally {
      setIsFetchingLogo(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">
            {mode === 'create' ? 'Add New Company' : 'Edit Company'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Document Scanner Section */}
          <div className="bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <ScanLine className="text-purple-400" size={20} />
              </div>
              <div>
                <h3 className="text-sm font-medium text-white">Smart Import</h3>
                <p className="text-xs text-gray-400">Upload a business card, screenshot, or PDF to auto-extract data</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3 items-start">
              {/* File Upload Area */}
              <div className="flex-1 min-w-[200px]">
                {!scanFile ? (
                  <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-purple-500 transition-colors bg-gray-800/50">
                    <div className="flex flex-col items-center justify-center py-3">
                      <Camera className="text-gray-400 mb-1" size={24} />
                      <p className="text-xs text-gray-400">Drop image/PDF or click to browse</p>
                      <p className="text-[10px] text-gray-500 mt-1">JPG, PNG, WebP, PDF (max 10MB)</p>
                    </div>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
                      onChange={handleFileSelect}
                    />
                  </label>
                ) : (
                  <div className="relative">
                    {scanPreview ? (
                      <div className="relative h-24 bg-gray-800 rounded-lg overflow-hidden">
                        <img src={scanPreview} alt="Preview" className="w-full h-full object-contain" />
                        <button
                          onClick={() => { setScanFile(null); setScanPreview(null); setExtractedContacts([]); }}
                          className="absolute top-1 right-1 p-1 bg-black/70 rounded-full hover:bg-red-500/50 transition-colors"
                        >
                          <X size={14} className="text-white" />
                        </button>
                      </div>
                    ) : (
                      <div className="h-24 bg-gray-800 rounded-lg flex items-center justify-center relative">
                        <div className="flex items-center gap-2 text-gray-400">
                          <FileText size={20} />
                          <span className="text-sm truncate max-w-[150px]">{scanFile.name}</span>
                        </div>
                        <button
                          onClick={() => { setScanFile(null); setScanPreview(null); setExtractedContacts([]); }}
                          className="absolute top-1 right-1 p-1 bg-black/70 rounded-full hover:bg-red-500/50 transition-colors"
                        >
                          <X size={14} className="text-white" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Scan Button */}
              {scanFile && (
                <button
                  onClick={handleScanDocument}
                  disabled={isScanning}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 h-10"
                >
                  {isScanning ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      Extract Data
                    </>
                  )}
                </button>
              )}
            </div>
            
            {/* Extracted Contacts */}
            {extractedContacts.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs text-gray-400 mb-2">Extracted Contact(s):</p>
                {extractedContacts.map((contact, idx) => (
                  <div key={idx} className="bg-gray-800 rounded-lg p-3 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-white text-sm">{contact.name || 'Unknown'}</span>
                        {contact.title && <span className="text-xs text-gray-400">• {contact.title}</span>}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                        {contact.email && (
                          <span className="flex items-center gap-1">
                            <Mail size={10} />
                            {contact.email}
                          </span>
                        )}
                        {contact.phone && (
                          <span className="flex items-center gap-1">
                            <Phone size={10} />
                            {contact.phone}
                          </span>
                        )}
                        {contact.company && (
                          <span className="flex items-center gap-1">
                            <Building size={10} />
                            {contact.company}
                          </span>
                        )}
                        {contact.website && (
                          <span className="flex items-center gap-1">
                            <Globe size={10} />
                            {contact.website}
                          </span>
                        )}
                        {contact.location && (
                          <span className="flex items-center gap-1">
                            <MapPin size={10} />
                            {contact.location}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => applyExtractedData(contact)}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs rounded-lg font-medium transition-colors flex items-center gap-1 flex-shrink-0"
                    >
                      <Check size={12} />
                      Apply
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Company Logo Section */}
          <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-cyan-500/20 rounded-lg">
                <Building className="text-cyan-400" size={20} />
              </div>
              <div>
                <h3 className="text-sm font-medium text-white">Company Logo</h3>
                <p className="text-xs text-gray-400">Add or fetch company logo from the web</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Logo Preview */}
              <div className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-600 bg-gray-800/50 flex items-center justify-center overflow-hidden">
                {form.logo_url ? (
                  <img 
                    src={form.logo_url} 
                    alt="Company Logo" 
                    className="w-full h-full object-contain p-2"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling?.classList.remove('hidden');
                    }}
                  />
                ) : (
                  <Building className="text-gray-600" size={32} />
                )}
                {form.logo_url && (
                  <Building className="text-gray-600 hidden" size={32} />
                )}
              </div>
              
              {/* Logo Actions */}
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={form.logo_url}
                    onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                    placeholder="https://logo.clearbit.com/company.com"
                    className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm focus:border-cyan-500 outline-none"
                  />
                  <button
                    onClick={handleFetchLogo}
                    disabled={isFetchingLogo || (!form.website && !form.name)}
                    className="px-3 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm"
                    title="Fetch logo from website"
                  >
                    {isFetchingLogo ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <ScanLine size={16} />
                    )}
                    Fetch
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Enter URL directly or click "Fetch" to auto-detect from website/company name
                </p>
                {form.logo_url && (
                  <button
                    onClick={() => setForm({ ...form, logo_url: '' })}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    Remove logo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Company Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                placeholder="Acme Inc."
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Website</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  className="flex-1 px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                  placeholder="www.acme.com"
                />
                {mode === 'edit' && form.website && (
                  <button
                    onClick={handleEnrich}
                    disabled={isEnriching}
                    className="px-3 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 disabled:opacity-50"
                    title="Enrich with website data"
                  >
                    {isEnriching ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Category</label>
              <select
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              >
                <option value="">Select Category</option>
                {availableCategories.length > 0 ? (
                  availableCategories.map(cat => (
                    <option key={cat.id} value={cat.label}>{cat.label}</option>
                  ))
                ) : (
                  INDUSTRIES.filter(i => i !== 'All').map(industry => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Location</label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                placeholder="Dallas, TX"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Company Size</label>
              <select
                value={form.size}
                onChange={(e) => setForm({ ...form, size: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              >
                <option value="">Select Size</option>
                {COMPANY_SIZES.filter(s => s.value !== 'All').map(size => (
                  <option key={size.value} value={size.value}>{size.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              >
                {PRIORITIES.map(p => (
                  <option key={p} value={p}>Priority {p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Lead Source</label>
              <input
                type="text"
                value={form.lead_source}
                onChange={(e) => setForm({ ...form, lead_source: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                placeholder="LinkedIn, Referral, etc."
              />
            </div>
          </div>

          {/* Primary Contact Section */}
          <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Users className="text-green-400" size={20} />
              </div>
              <div>
                <h3 className="text-sm font-medium text-white">Primary Contact</h3>
                <p className="text-xs text-gray-400">Add the main contact person for this company</p>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">First Name</label>
                <input
                  type="text"
                  value={form.contact_first_name}
                  onChange={(e) => setForm({ ...form, contact_first_name: e.target.value })}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Last Name</label>
                <input
                  type="text"
                  value={form.contact_last_name}
                  onChange={(e) => setForm({ ...form, contact_last_name: e.target.value })}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                  placeholder="Smith"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Job Title</label>
                <input
                  type="text"
                  value={form.contact_title}
                  onChange={(e) => setForm({ ...form, contact_title: e.target.value })}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                  placeholder="CEO, Manager, etc."
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Contact Email</label>
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Contact Phone</label>
                <input
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none resize-none"
              rows={4}
              placeholder="Any notes about this company..."
            />
          </div>

          {company?.scraped_data && (
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
              <h4 className="text-cyan-400 font-medium mb-2 flex items-center gap-2">
                <Sparkles size={16} />
                Enriched Data
              </h4>
              <div className="text-sm text-gray-300 space-y-1">
                {company.scraped_data.headline && (
                  <p><span className="text-gray-500">Headline:</span> {company.scraped_data.headline}</p>
                )}
                {company.scraped_data.description && (
                  <p><span className="text-gray-500">Description:</span> {company.scraped_data.description}</p>
                )}
                {company.scraped_data.hiring_signals?.length > 0 && (
                  <p><span className="text-gray-500">Signals:</span> {company.scraped_data.hiring_signals.join(', ')}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-700">
          {mode === 'edit' && (
            <button
              onClick={() => onDelete(company.id)}
              className="px-4 py-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
            >
              Delete Company
            </button>
          )}
          <div className={`flex gap-3 ${mode === 'create' ? 'ml-auto' : ''}`}>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 bg-cyan-500 text-black font-medium rounded-lg hover:bg-cyan-400 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : mode === 'create' ? 'Add Company' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Email Draft Modal
const EmailDraftModal = ({ isOpen, onClose, lead, company, onApprove }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(0);
  const [selectedVersion, setSelectedVersion] = useState('short');
  const [editedContent, setEditedContent] = useState('');
  const [copiedSubject, setCopiedSubject] = useState(false);
  const [copiedBody, setCopiedBody] = useState(false);

  const generateDraft = async () => {
    if (!lead || !company) return;
    setIsGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/generate-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: lead.id,
          company_data: company,
          contact_name: lead.contact?.name || 'there'
        })
      });
      const data = await response.json();
      if (response.ok) {
        setDraft(data.draft);
        setEditedContent(data.draft.short_version);
        if (data.warning === 'rate_limit') {
          toast.warning('Using template - Add credits to enable AI generation', {
            duration: 5000,
            description: 'Go to Profile → Universal Key → Add Balance'
          });
        } else {
          toast.success('Email draft generated!');
        }
      } else {
        toast.error(data.detail || 'Failed to generate draft');
      }
    } catch (error) {
      toast.error('Failed to generate draft');
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (draft) {
      setEditedContent(selectedVersion === 'short' ? draft.short_version : draft.long_version);
    }
  }, [selectedVersion, draft]);

  const copyToClipboard = async (text, type) => {
    await navigator.clipboard.writeText(text);
    if (type === 'subject') {
      setCopiedSubject(true);
      setTimeout(() => setCopiedSubject(false), 2000);
    } else {
      setCopiedBody(true);
      setTimeout(() => setCopiedBody(false), 2000);
    }
    toast.success('Copied to clipboard!');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Sparkles className="text-purple-400" size={24} />
              AI Email Generator
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Generate personalized email for {company?.name}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {!draft ? (
            <div className="text-center py-12">
              <Sparkles className="mx-auto text-purple-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-white mb-2">
                Ready to generate personalized email
              </h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                AI will analyze {company?.name}'s website and create a customized outreach email with multiple options.
              </p>
              <button
                onClick={generateDraft}
                disabled={isGenerating}
                className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2 mx-auto"
              >
                {isGenerating ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles size={20} />
                    Generate Email Draft
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Subject Lines */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Subject Line Options</label>
                <div className="space-y-2">
                  {draft.subject_options?.map((subject, idx) => (
                    <div
                      key={idx}
                      onClick={() => setSelectedSubject(idx)}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedSubject === idx
                          ? 'bg-purple-500/20 border border-purple-500'
                          : 'bg-gray-800 border border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedSubject === idx ? 'border-purple-500' : 'border-gray-600'
                      }`}>
                        {selectedSubject === idx && <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />}
                      </div>
                      <span className="text-white flex-1">{subject}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(subject, 'subject'); }}
                        className="p-1 hover:bg-gray-700 rounded"
                      >
                        {copiedSubject && selectedSubject === idx ? <Check size={16} className="text-green-400" /> : <Copy size={16} className="text-gray-400" />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Version Toggle */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Email Version</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedVersion('short')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedVersion === 'short'
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    Short Version
                  </button>
                  <button
                    onClick={() => setSelectedVersion('long')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedVersion === 'long'
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    Long Version
                  </button>
                </div>
              </div>

              {/* Email Body */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-gray-400">Email Body</label>
                  <button
                    onClick={() => copyToClipboard(editedContent, 'body')}
                    className="flex items-center gap-1 text-sm text-gray-400 hover:text-white"
                  >
                    {copiedBody ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                    Copy
                  </button>
                </div>
                <textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none resize-none font-mono text-sm"
                  rows={12}
                />
              </div>

              {/* Personalized Line Preview */}
              {draft.personalized_line && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <h4 className="text-yellow-400 text-sm font-medium mb-1">Personalized Opening</h4>
                  <p className="text-gray-300">{draft.personalized_line}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {draft && (
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
            <button
              onClick={generateDraft}
              disabled={isGenerating}
              className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Regenerate
            </button>
            <button
              onClick={() => {
                onApprove({
                  subject: draft.subject_options[selectedSubject],
                  body: editedContent
                });
                onClose();
              }}
              className="px-6 py-2 bg-green-500 text-black font-medium rounded-lg hover:bg-green-400 flex items-center gap-2"
            >
              <Check size={18} />
              Approve & Queue
            </button>
          </div>
        )}
      </div>
    </div>
  );
};


// Bulk Email Modal - Send emails to multiple companies with template variables
const BulkEmailModal = ({ 
  isOpen, 
  onClose, 
  companies, 
  nylasAccounts, 
  onSendToQueue 
}) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('Hi {{contact_name}},\n\n');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewCompanyIdx, setPreviewCompanyIdx] = useState(0);
  
  // Template variable helper
  const templateVars = [
    { var: '{{company_name}}', desc: 'Company name' },
    { var: '{{contact_name}}', desc: 'Contact name' },
    { var: '{{contact_email}}', desc: 'Contact email' },
    { var: '{{contact_title}}', desc: 'Job title' },
    { var: '{{notes}}', desc: 'Company notes' },
  ];
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSubject('');
      setBody('Hi {{contact_name}},\n\n');
      setSelectedAccount(nylasAccounts?.[0]?.email || '');
      setScheduledDate(new Date().toISOString().split('T')[0]);
      setScheduledTime('09:00');
      setPreviewCompanyIdx(0);
    }
  }, [isOpen, nylasAccounts]);
  
  // Replace template variables with actual data
  const replaceVariables = (text, company) => {
    const contact = company?.primary_contact || company?.contacts?.[0] || {};
    return text
      .replace(/\{\{company_name\}\}/g, company?.name || company?.company_name || '[Company]')
      .replace(/\{\{contact_name\}\}/g, contact?.name || '[Contact]')
      .replace(/\{\{contact_email\}\}/g, contact?.email || '[Email]')
      .replace(/\{\{contact_title\}\}/g, contact?.title || '[Title]')
      .replace(/\{\{notes\}\}/g, company?.notes || '');
  };
  
  // Insert template variable at cursor
  const insertVariable = (varName) => {
    setBody(prev => prev + varName);
  };
  
  // Generate AI draft template
  const handleGenerateAI = async () => {
    setIsGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/generate-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_data: companies[0],
          contact_name: '{{contact_name}}',
          use_template_vars: true
        })
      });
      const data = await response.json();
      if (response.ok && data.draft) {
        // Replace actual values with template variables
        let generatedBody = data.draft.short_version || '';
        generatedBody = generatedBody.replace(new RegExp(companies[0]?.name || 'NOMATCH', 'gi'), '{{company_name}}');
        
        setSubject(data.draft.subject_options?.[0] || '');
        setBody(generatedBody);
        toast.success('AI template generated!');
      } else {
        // Fallback template
        setSubject('Quick question for {{company_name}}');
        setBody(`Hi {{contact_name}},\n\nI came across {{company_name}} and was impressed by what you're building.\n\nI'd love to explore potential synergies. Would you be open to a brief 15-minute call this week?\n\nBest regards`);
        if (data.warning === 'rate_limit') {
          toast.warning('Using fallback template - Add credits for AI generation');
        } else {
          toast.success('Template ready!');
        }
      }
    } catch (error) {
      toast.error('Failed to generate template');
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Get companies with valid contacts
  const validCompanies = companies.filter(c => {
    const contact = c?.primary_contact || c?.contacts?.[0];
    return contact?.email;
  });
  
  const invalidCompanies = companies.filter(c => {
    const contact = c?.primary_contact || c?.contacts?.[0];
    return !contact?.email;
  });
  
  // Handle send to queue
  const handleSendToQueue = () => {
    if (!subject.trim()) {
      toast.error('Please enter a subject line');
      return;
    }
    if (!body.trim()) {
      toast.error('Please enter email body');
      return;
    }
    if (!selectedAccount && nylasAccounts?.length > 0) {
      toast.error('Please select a sender account');
      return;
    }
    if (validCompanies.length === 0) {
      toast.error('No companies with valid email addresses');
      return;
    }
    
    // Create queue items for each valid company
    const queueItems = validCompanies.map(company => {
      const contact = company?.primary_contact || company?.contacts?.[0];
      return {
        company_id: company.id,
        company: { name: company.name || company.company_name },
        recipient_email: contact?.email,
        recipient_name: contact?.name,
        subject: replaceVariables(subject, company),
        body: replaceVariables(body, company),
        from_email: selectedAccount,
        scheduled_date: scheduledDate,
        scheduled_time: scheduledTime,
      };
    });
    
    onSendToQueue(queueItems);
    onClose();
  };
  
  // Preview company for template
  const previewCompany = validCompanies[previewCompanyIdx] || companies[0];
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <SendHorizonal className="text-cyan-400" size={24} />
              Bulk Email Queue
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Send to {validCompanies.length} {validCompanies.length === 1 ? 'company' : 'companies'}
              {invalidCompanies.length > 0 && (
                <span className="text-yellow-400 ml-2">
                  ({invalidCompanies.length} skipped - no email)
                </span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        
        <div className="flex-1 overflow-hidden flex">
          {/* Left: Editor */}
          <div className="w-1/2 p-6 border-r border-gray-700 overflow-y-auto">
            {/* Sender Account */}
            {nylasAccounts?.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">Send From</label>
                <select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                >
                  {nylasAccounts.map((account, idx) => (
                    <option key={idx} value={account.email}>{account.email}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Schedule */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Scheduled Date</label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Time</label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                />
              </div>
            </div>
            
            {/* Subject */}
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">Subject Line</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Quick question for {{company_name}}"
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              />
            </div>
            
            {/* Template Variables */}
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">Insert Variable</label>
              <div className="flex flex-wrap gap-2">
                {templateVars.map((v, idx) => (
                  <button
                    key={idx}
                    onClick={() => insertVariable(v.var)}
                    className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-xs font-mono transition-colors"
                    title={v.desc}
                  >
                    {v.var}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Body */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-gray-400">Email Body</label>
                <button
                  onClick={handleGenerateAI}
                  disabled={isGenerating}
                  className="flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300 disabled:opacity-50"
                >
                  {isGenerating ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Sparkles size={14} />
                  )}
                  Generate with AI
                </button>
              </div>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Hi {{contact_name}},&#10;&#10;Your message here..."
                className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none resize-none font-mono text-sm"
                rows={12}
              />
            </div>
          </div>
          
          {/* Right: Preview */}
          <div className="w-1/2 p-6 overflow-y-auto bg-gray-800/30">
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm text-gray-400">Preview</label>
              {validCompanies.length > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPreviewCompanyIdx(prev => Math.max(0, prev - 1))}
                    disabled={previewCompanyIdx === 0}
                    className="p-1 hover:bg-gray-700 rounded disabled:opacity-30"
                  >
                    <ChevronLeft size={16} className="text-gray-400" />
                  </button>
                  <span className="text-xs text-gray-500">
                    {previewCompanyIdx + 1} / {validCompanies.length}
                  </span>
                  <button
                    onClick={() => setPreviewCompanyIdx(prev => Math.min(validCompanies.length - 1, prev + 1))}
                    disabled={previewCompanyIdx >= validCompanies.length - 1}
                    className="p-1 hover:bg-gray-700 rounded disabled:opacity-30"
                  >
                    <ChevronRight size={16} className="text-gray-400" />
                  </button>
                </div>
              )}
            </div>
            
            {/* Preview Card */}
            <div className="bg-black border border-gray-700 rounded-xl overflow-hidden">
              {/* Email Header */}
              <div className="p-4 border-b border-gray-700 bg-gray-900/50">
                <div className="flex items-center gap-2 text-sm mb-2">
                  <span className="text-gray-500">To:</span>
                  <span className="text-cyan-400">
                    {previewCompany?.primary_contact?.email || previewCompany?.contacts?.[0]?.email || 'No email'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm mb-2">
                  <span className="text-gray-500">From:</span>
                  <span className="text-white">{selectedAccount || 'Select account'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">Subject:</span>
                  <span className="text-white font-medium">
                    {replaceVariables(subject, previewCompany) || '(No subject)'}
                  </span>
                </div>
              </div>
              
              {/* Email Body */}
              <div className="p-4">
                <div className="text-sm text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">
                  {replaceVariables(body, previewCompany) || '(No content)'}
                </div>
              </div>
            </div>
            
            {/* Company Info */}
            {previewCompany && (
              <div className="mt-4 p-3 bg-gray-800/50 rounded-lg">
                <div className="text-xs text-gray-500 mb-2">Preview for:</div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded flex items-center justify-center text-white text-sm font-bold">
                    {(previewCompany.name || previewCompany.company_name)?.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm text-white font-medium">{previewCompany.name || previewCompany.company_name}</div>
                    <div className="text-xs text-gray-400">
                      {previewCompany?.primary_contact?.name || previewCompany?.contacts?.[0]?.name || 'No contact'}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Skipped Companies Warning */}
            {invalidCompanies.length > 0 && (
              <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm text-yellow-400 font-medium">
                      {invalidCompanies.length} {invalidCompanies.length === 1 ? 'company' : 'companies'} will be skipped
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      Missing contact email: {invalidCompanies.slice(0, 3).map(c => c.name || c.company_name).join(', ')}
                      {invalidCompanies.length > 3 && ` +${invalidCompanies.length - 3} more`}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-700 bg-gray-900">
          <div className="text-sm text-gray-500">
            <Clock size={14} className="inline mr-1" />
            All {validCompanies.length} emails will be sent at {scheduledTime} on {scheduledDate || 'selected date'}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSendToQueue}
              disabled={validCompanies.length === 0 || !subject.trim() || !body.trim()}
              className="px-6 py-2 bg-cyan-500 text-black font-medium rounded-lg hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Send size={18} />
              Add {validCompanies.length} to Queue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Quick Send to Queue Modal - For single company
const QuickSendToQueueModal = ({ 
  isOpen, 
  onClose, 
  company, 
  nylasAccounts, 
  onSendToQueue 
}) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState('short');
  const [draft, setDraft] = useState(null);
  
  const contact = company?.primary_contact || company?.contacts?.[0];
  
  useEffect(() => {
    if (isOpen && company) {
      setSelectedAccount(nylasAccounts?.[0]?.email || '');
      setScheduledDate(new Date().toISOString().split('T')[0]);
      setScheduledTime('09:00');
      setSubject('');
      setBody(`Hi ${contact?.name || 'there'},\n\n`);
      setDraft(null);
    }
  }, [isOpen, company, nylasAccounts, contact?.name]);
  
  const handleGenerateAI = async () => {
    setIsGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/generate-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_data: company,
          contact_name: contact?.name || 'there'
        })
      });
      const data = await response.json();
      if (response.ok && data.draft) {
        setDraft(data.draft);
        setSubject(data.draft.subject_options?.[0] || '');
        setBody(data.draft.short_version);
        toast.success('Email draft generated!');
      } else {
        // Fallback
        setSubject(`Quick question for ${company?.name || 'your company'}`);
        setBody(`Hi ${contact?.name || 'there'},\n\nI came across ${company?.name || 'your company'} and was impressed by what you're building.\n\nI'd love to explore potential synergies. Would you be open to a brief 15-minute call this week?\n\nBest regards`);
        toast.success('Template ready!');
      }
    } catch (error) {
      toast.error('Failed to generate');
    } finally {
      setIsGenerating(false);
    }
  };
  
  useEffect(() => {
    if (draft) {
      setBody(selectedVersion === 'short' ? draft.short_version : draft.long_version);
    }
  }, [selectedVersion, draft]);
  
  const handleSendToQueue = () => {
    if (!contact?.email) {
      toast.error('No contact email available');
      return;
    }
    if (!subject.trim() || !body.trim()) {
      toast.error('Please fill in subject and body');
      return;
    }
    
    onSendToQueue([{
      company_id: company.id,
      company: { name: company.name || company.company_name },
      recipient_email: contact.email,
      recipient_name: contact.name,
      subject,
      body,
      from_email: selectedAccount,
      scheduled_date: scheduledDate,
      scheduled_time: scheduledTime,
    }]);
    onClose();
  };
  
  if (!isOpen || !company) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Send className="text-cyan-400" size={24} />
              Send to Queue
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {company?.name || company?.company_name} • {contact?.name || 'No contact'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          {/* No Email Warning */}
          {!contact?.email && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
              <AlertCircle className="text-red-400" size={20} />
              <span className="text-red-400">No contact email available for this company</span>
            </div>
          )}
          
          {/* Sender Account */}
          {nylasAccounts?.length > 0 && (
            <div>
              <label className="block text-sm text-gray-400 mb-2">Send From</label>
              <select
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              >
                {nylasAccounts.map((account, idx) => (
                  <option key={idx} value={account.email}>{account.email}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Recipient */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">To</label>
            <div className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-cyan-400">
              {contact?.email || 'No email'}
            </div>
          </div>
          
          {/* Schedule */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Date</label>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Time</label>
              <input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              />
            </div>
          </div>
          
          {/* Subject */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-gray-400">Subject</label>
              {draft && (
                <div className="flex gap-1">
                  {draft.subject_options?.map((subj, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSubject(subj)}
                      className={`px-2 py-0.5 text-xs rounded ${subject === subj ? 'bg-purple-500 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                    >
                      Option {idx + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject line..."
              className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
            />
          </div>
          
          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-gray-400">Message</label>
              <div className="flex items-center gap-2">
                {draft && (
                  <div className="flex gap-1 mr-2">
                    <button
                      onClick={() => setSelectedVersion('short')}
                      className={`px-2 py-0.5 text-xs rounded ${selectedVersion === 'short' ? 'bg-purple-500 text-white' : 'bg-gray-800 text-gray-400'}`}
                    >
                      Short
                    </button>
                    <button
                      onClick={() => setSelectedVersion('long')}
                      className={`px-2 py-0.5 text-xs rounded ${selectedVersion === 'long' ? 'bg-purple-500 text-white' : 'bg-gray-800 text-gray-400'}`}
                    >
                      Long
                    </button>
                  </div>
                )}
                <button
                  onClick={handleGenerateAI}
                  disabled={isGenerating}
                  className="flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300 disabled:opacity-50"
                >
                  {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Generate AI Draft
                </button>
              </div>
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message..."
              className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none resize-none font-mono text-sm"
              rows={10}
            />
          </div>
        </div>
        
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSendToQueue}
            disabled={!contact?.email || !subject.trim() || !body.trim()}
            className="px-6 py-2 bg-cyan-500 text-black font-medium rounded-lg hover:bg-cyan-400 disabled:opacity-50 flex items-center gap-2"
          >
            <Send size={18} />
            Add to Queue
          </button>
        </div>
      </div>
    </div>
  );
};


// Lead Detail Modal - Shows full lead and contact information
const LeadDetailModal = ({ isOpen, onClose, lead, onEdit, onGenerateEmail }) => {
  if (!isOpen || !lead) return null;
  
  const companyName = lead.company?.name || lead.company?.company_name || 'Unknown Company';
  const contact = lead.contact;
  const stageConfig = PIPELINE_STAGES.find(s => s.id === lead.stage) || PIPELINE_STAGES[0];
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
              {companyName.charAt(0)}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{companyName}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded ${stageConfig.color} text-white`}>
                  {stageConfig.label}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  lead.company?.priority === 'A' ? 'bg-red-500/20 text-red-400' :
                  lead.company?.priority === 'B' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  Priority {lead.company?.priority || 'B'}
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="close-lead-modal">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Company Info */}
          <div className="bg-gray-800/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
              <Building size={16} />
              Company Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {lead.company?.website && (
                <div>
                  <div className="text-xs text-gray-500">Website</div>
                  <a 
                    href={lead.company.website.startsWith('http') ? lead.company.website : `https://${lead.company.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                  >
                    <Globe size={12} />
                    {lead.company.website}
                  </a>
                </div>
              )}
              {lead.company?.industry && (
                <div>
                  <div className="text-xs text-gray-500">Industry</div>
                  <div className="text-sm text-white">{lead.company.industry}</div>
                </div>
              )}
              {lead.company?.location && (
                <div>
                  <div className="text-xs text-gray-500">Location</div>
                  <div className="text-sm text-white flex items-center gap-1">
                    <MapPin size={12} />
                    {lead.company.location}
                  </div>
                </div>
              )}
              {(lead.company?.size || lead.company?.company_size) && (
                <div>
                  <div className="text-xs text-gray-500">Company Size</div>
                  <div className="text-sm text-white">{lead.company.size || lead.company.company_size}</div>
                </div>
              )}
              {lead.company?.lead_source && (
                <div>
                  <div className="text-xs text-gray-500">Lead Source</div>
                  <div className="text-sm text-white">{lead.company.lead_source}</div>
                </div>
              )}
              {lead.deal_value > 0 && (
                <div>
                  <div className="text-xs text-gray-500">Deal Value</div>
                  <div className="text-sm text-green-400 font-medium">${lead.deal_value.toLocaleString()}</div>
                </div>
              )}
            </div>
          </div>

          {/* Contact Info */}
          {contact && (
            <div className="bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border border-cyan-500/30 rounded-lg p-4">
              <h3 className="text-sm font-medium text-cyan-400 mb-3 flex items-center gap-2">
                <Users size={16} />
                Contact Information
              </h3>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                  {contact.name?.split(' ').map(n => n[0]).join('').substring(0, 2) || '?'}
                </div>
                <div className="flex-1 space-y-2">
                  <div>
                    <div className="text-lg font-medium text-white">{contact.name}</div>
                    {contact.title && (
                      <div className="text-sm text-gray-400">{contact.title}</div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {contact.email && (
                      <a 
                        href={`mailto:${contact.email}`}
                        className="flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300"
                      >
                        <Mail size={14} />
                        {contact.email}
                      </a>
                    )}
                    {contact.phone && (
                      <a 
                        href={`tel:${contact.phone}`}
                        className="flex items-center gap-2 text-sm text-gray-300 hover:text-white"
                      >
                        <Phone size={14} />
                        {contact.phone}
                      </a>
                    )}
                    {contact.linkedin_url && (
                      <a 
                        href={contact.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300"
                      >
                        <ExternalLink size={14} />
                        LinkedIn Profile
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* No Contact Message */}
          {!contact && (
            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
              <Users size={32} className="mx-auto text-gray-600 mb-2" />
              <p className="text-gray-400">No contact information available</p>
              <p className="text-xs text-gray-500 mt-1">Add a contact to this lead to see details here</p>
            </div>
          )}

          {/* Lead Notes */}
          {lead.notes && (
            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                <FileText size={16} />
                Notes
              </h3>
              <p className="text-sm text-gray-300">{lead.notes}</p>
            </div>
          )}

          {/* Timeline Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            {lead.created_at && (
              <div className="text-gray-500">
                <span className="text-gray-600">Created:</span> {new Date(lead.created_at).toLocaleDateString()}
              </div>
            )}
            {lead.next_followup_date && (
              <div className="text-yellow-400">
                <Clock size={12} className="inline mr-1" />
                Follow-up: {new Date(lead.next_followup_date).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={() => { onClose(); onEdit(lead); }}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 flex items-center gap-2"
          >
            <Edit size={16} />
            Edit Lead
          </button>
          {contact?.email && (
            <button
              onClick={() => { onClose(); onGenerateEmail(lead); }}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 flex items-center gap-2"
            >
              <Mail size={16} />
              Generate Email
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Lead Card for Pipeline
const LeadCard = ({ lead, onEdit, onGenerateEmail, onDelete, onViewDetails, provided }) => {
  const stageConfig = PIPELINE_STAGES.find(s => s.id === lead.stage) || PIPELINE_STAGES[0];
  const companyName = lead.company?.name || lead.company?.company_name || 'Unknown Company';
  const contact = lead.contact;
  
  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      onClick={() => onViewDetails(lead)}
      className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-3 hover:border-gray-600 transition-all cursor-pointer group"
      data-testid="pipeline-lead-card"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <GripVertical size={14} className="text-gray-600 opacity-0 group-hover:opacity-100" />
          <div className={`w-2 h-2 rounded-full ${stageConfig.color}`} />
          <span className="font-medium text-white truncate max-w-[150px]">
            {companyName}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {lead.deal_value > 0 && (
            <span className="text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded">
              ${(lead.deal_value / 1000).toFixed(0)}k
            </span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded ${
            lead.company?.priority === 'A' ? 'bg-red-500/20 text-red-400' :
            lead.company?.priority === 'B' ? 'bg-yellow-500/20 text-yellow-400' :
            'bg-gray-500/20 text-gray-400'
          }`}>
            {lead.company?.priority || 'B'}
          </span>
        </div>
      </div>

      {/* Contact Info Section */}
      {contact && (
        <div className="mb-2 space-y-1">
          <div className="text-sm text-white flex items-center gap-1">
            <Users size={12} className="text-cyan-400" />
            {contact.name}
            {contact.title && <span className="text-gray-500">• {contact.title}</span>}
          </div>
          {contact.email && (
            <div className="text-xs text-cyan-400 flex items-center gap-1">
              <Mail size={10} />
              {contact.email}
            </div>
          )}
          {contact.phone && (
            <div className="text-xs text-gray-400 flex items-center gap-1">
              <Phone size={10} />
              {contact.phone}
            </div>
          )}
        </div>
      )}

      {lead.company?.industry && (
        <div className="text-xs text-gray-500 mb-2">{lead.company.industry}</div>
      )}

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-800">
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(lead); }}
            className="p-1.5 hover:bg-cyan-500/20 rounded text-gray-400 hover:text-cyan-400 transition-colors"
            title="Edit lead"
          >
            <Edit size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onGenerateEmail(lead); }}
            className="p-1.5 hover:bg-purple-500/20 rounded text-gray-400 hover:text-purple-400 transition-colors"
            title="Generate email"
          >
            <Mail size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(lead); }}
            className="p-1.5 hover:bg-red-500/20 rounded text-gray-400 hover:text-red-400 transition-colors"
            title="Delete lead"
          >
            <Trash2 size={14} />
          </button>
        </div>
        {lead.next_followup_date && (
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <Clock size={12} />
            {new Date(lead.next_followup_date).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
};

// Pipeline Board
const PipelineBoard = ({ pipeline, onDragEnd, onEditLead, onGenerateEmail, onDeleteLead, onViewLeadDetails }) => {
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {PIPELINE_STAGES.map(stage => (
          <div key={stage.id} className="flex-shrink-0 w-72">
            <div className="bg-black border border-gray-700 rounded-xl overflow-hidden">
              <div className={`px-4 py-3 border-b border-gray-700 flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                  <span className="font-medium text-white">{stage.label}</span>
                </div>
                <span className="text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded">
                  {pipeline[stage.id]?.length || 0}
                </span>
              </div>
              <Droppable droppableId={stage.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`p-3 min-h-[300px] max-h-[60vh] overflow-y-auto ${
                      snapshot.isDraggingOver ? 'bg-gray-900/50' : ''
                    }`}
                  >
                    {pipeline[stage.id]?.map((lead, index) => (
                      <Draggable key={lead.id} draggableId={lead.id} index={index}>
                        {(provided) => (
                          <LeadCard
                            lead={lead}
                            onEdit={onEditLead}
                            onGenerateEmail={onGenerateEmail}
                            onDelete={onDeleteLead}
                            onViewDetails={onViewLeadDetails}
                            provided={provided}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {(!pipeline[stage.id] || pipeline[stage.id].length === 0) && (
                      <div className="text-center py-8 text-gray-600 text-sm">
                        No leads
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        ))}
      </div>
    </DragDropContext>
  );
};


// RocketReach Search Modal - Find contacts and companies from RocketReach database
const RocketReachModal = ({ isOpen, onClose, onImportContact, onImportCompany }) => {
  const [searchType, setSearchType] = useState('people'); // 'people' or 'companies'
  const [isSearching, setIsSearching] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(null);
  const [results, setResults] = useState([]);
  const [accountStatus, setAccountStatus] = useState(null);
  const [error, setError] = useState(null);
  
  // Search filters
  const [searchName, setSearchName] = useState('');
  const [searchCompany, setSearchCompany] = useState('');
  const [searchTitle, setSearchTitle] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  
  // Check RocketReach connection status on mount
  useEffect(() => {
    if (isOpen) {
      checkStatus();
    }
  }, [isOpen]);
  
  const checkStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/rocketreach/status`);
      const data = await response.json();
      setAccountStatus(data);
    } catch (err) {
      setAccountStatus({ connected: false, message: 'Failed to connect' });
    }
  };
  
  const handleSearch = async () => {
    if (!searchName && !searchCompany && !searchKeyword) {
      toast.error('Enter at least one search term');
      return;
    }
    
    setIsSearching(true);
    setError(null);
    setResults([]);
    
    try {
      if (searchType === 'people') {
        const response = await fetch(`${API_URL}/api/rocketreach/person/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: searchName || undefined,
            current_employer: searchCompany || undefined,
            current_title: searchTitle || undefined,
            location: searchLocation || undefined,
            keyword: searchKeyword || undefined,
            page_size: 20
          })
        });
        const data = await response.json();
        if (data.success) {
          setResults(data.profiles || []);
          if (data.profiles?.length === 0) {
            toast.info('No results found');
          }
        } else {
          setError(data.message || 'Search failed');
        }
      } else {
        const response = await fetch(`${API_URL}/api/rocketreach/company/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: searchCompany || searchName || undefined,
            keyword: searchKeyword || undefined,
            page_size: 20
          })
        });
        const data = await response.json();
        if (data.success) {
          setResults(data.companies || []);
          if (data.companies?.length === 0) {
            toast.info('No results found');
          }
        } else {
          setError(data.message || 'Search failed');
        }
      }
    } catch (err) {
      setError('Search failed: ' + err.message);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Lookup full contact details (uses 1 credit)
  const handleLookup = async (profile) => {
    setIsLookingUp(profile.id);
    try {
      const response = await fetch(`${API_URL}/api/rocketreach/person/lookup?profile_id=${profile.id}`);
      const data = await response.json();
      
      if (data.success && data.person) {
        // Import the full contact
        const person = data.person;
        onImportContact({
          name: person.name || `${person.first_name || ''} ${person.last_name || ''}`.trim(),
          email: person.emails?.[0]?.email || person.current_professional_email || '',
          phone: person.phones?.[0]?.number || '',
          title: person.current_title || '',
          company: person.current_employer || '',
          linkedin_url: person.linkedin_url || '',
          location: person.location || ''
        });
        toast.success(`Imported ${person.name || 'contact'} with full details!`);
      } else {
        toast.error(data.message || 'Failed to lookup contact');
      }
    } catch (err) {
      toast.error('Lookup failed');
    } finally {
      setIsLookingUp(null);
    }
  };
  
  // Quick import without lookup (no credit used, limited data)
  const handleQuickImport = (profile) => {
    if (searchType === 'people') {
      onImportContact({
        name: profile.name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
        email: '', // Need lookup for email
        phone: '',
        title: profile.current_title || '',
        company: profile.current_employer || '',
        linkedin_url: profile.linkedin_url || '',
        location: profile.location || ''
      });
      toast.success(`Imported ${profile.name} (use Lookup for email/phone)`);
    } else {
      onImportCompany({
        name: profile.name || '',
        website: profile.domain || profile.website_url || '',
        industry: profile.industry || '',
        location: profile.city && profile.state ? `${profile.city}, ${profile.state}` : profile.country || '',
        size: profile.employee_count ? `${profile.employee_count} employees` : '',
        notes: profile.description?.slice(0, 500) || ''
      });
      toast.success(`Imported company: ${profile.name}`);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <Rocket className="text-orange-400" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">RocketReach Search</h2>
              <p className="text-sm text-gray-400">Find contacts and companies</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {accountStatus?.connected && (
              <span className="text-xs text-green-400 flex items-center gap-1">
                <CheckCircle size={12} />
                Connected
              </span>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X size={24} />
            </button>
          </div>
        </div>
        
        {/* Search Type Toggle */}
        <div className="px-6 pt-4">
          <div className="flex gap-2">
            <button
              onClick={() => { setSearchType('people'); setResults([]); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                searchType === 'people' 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              <Users size={16} className="inline mr-2" />
              People
            </button>
            <button
              onClick={() => { setSearchType('companies'); setResults([]); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                searchType === 'companies' 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              <Building size={16} className="inline mr-2" />
              Companies
            </button>
          </div>
        </div>
        
        {/* Search Filters */}
        <div className="p-6 border-b border-gray-700">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                {searchType === 'people' ? 'Name' : 'Company Name'}
              </label>
              <input
                type="text"
                value={searchType === 'people' ? searchName : searchCompany}
                onChange={(e) => searchType === 'people' ? setSearchName(e.target.value) : setSearchCompany(e.target.value)}
                placeholder={searchType === 'people' ? 'John Smith' : 'Google, Microsoft...'}
                className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm focus:border-orange-500 outline-none"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            {searchType === 'people' && (
              <>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Company</label>
                  <input
                    type="text"
                    value={searchCompany}
                    onChange={(e) => setSearchCompany(e.target.value)}
                    placeholder="Works at..."
                    className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm focus:border-orange-500 outline-none"
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Job Title</label>
                  <input
                    type="text"
                    value={searchTitle}
                    onChange={(e) => setSearchTitle(e.target.value)}
                    placeholder="CEO, Manager..."
                    className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm focus:border-orange-500 outline-none"
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Keyword</label>
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                placeholder="Sales, Marketing..."
                className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm focus:border-orange-500 outline-none"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            {searchType === 'people' && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Location</label>
                <input
                  type="text"
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                  placeholder="New York, CA..."
                  className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm focus:border-orange-500 outline-none"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
            )}
            <div className="flex items-end">
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className="w-full px-4 py-2 bg-orange-500 hover:bg-orange-400 disabled:bg-gray-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isSearching ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Search size={16} />
                )}
                Search
              </button>
            </div>
          </div>
        </div>
        
        {/* Results */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 mb-4">
              {error}
            </div>
          )}
          
          {results.length === 0 && !isSearching && !error && (
            <div className="text-center py-12 text-gray-500">
              <Rocket size={48} className="mx-auto mb-4 opacity-30" />
              <p>Search RocketReach to find contacts</p>
              <p className="text-sm mt-1">Enter name, company, or keywords above</p>
            </div>
          )}
          
          <div className="space-y-3">
            {results.map((item, idx) => (
              <div 
                key={item.id || idx}
                className="p-4 bg-gray-800/50 hover:bg-gray-800 rounded-xl border border-gray-700 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Avatar */}
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden">
                      {item.profile_pic ? (
                        <img src={item.profile_pic} alt="" className="w-full h-full object-cover" />
                      ) : (
                        (item.name || item.first_name || '?').charAt(0)
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-white truncate">
                          {item.name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || 'Unknown'}
                        </span>
                        {item.linkedin_url && (
                          <a 
                            href={item.linkedin_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <ExternalLink size={14} />
                          </a>
                        )}
                      </div>
                      
                      {searchType === 'people' ? (
                        <>
                          {item.current_title && (
                            <div className="text-sm text-gray-400 truncate">
                              {item.current_title}
                            </div>
                          )}
                          {item.current_employer && (
                            <div className="text-sm text-cyan-400 truncate">
                              <Building size={12} className="inline mr-1" />
                              {item.current_employer}
                            </div>
                          )}
                          {item.location && (
                            <div className="text-xs text-gray-500 mt-1">
                              <MapPin size={10} className="inline mr-1" />
                              {item.location}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {item.industry && (
                            <div className="text-sm text-gray-400">{item.industry}</div>
                          )}
                          {item.domain && (
                            <div className="text-sm text-cyan-400">
                              <Globe size={12} className="inline mr-1" />
                              {item.domain}
                            </div>
                          )}
                          {item.employee_count && (
                            <div className="text-xs text-gray-500 mt-1">
                              <Users size={10} className="inline mr-1" />
                              {item.employee_count.toLocaleString()} employees
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {searchType === 'people' && (
                      <button
                        onClick={() => handleLookup(item)}
                        disabled={isLookingUp === item.id}
                        className="px-3 py-1.5 bg-orange-500 hover:bg-orange-400 disabled:bg-gray-700 text-white text-sm rounded-lg font-medium transition-colors flex items-center gap-1"
                        title="Get full contact details (uses 1 credit)"
                      >
                        {isLookingUp === item.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Mail size={14} />
                        )}
                        Lookup
                      </button>
                    )}
                    <button
                      onClick={() => handleQuickImport(item)}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-sm rounded-lg font-medium transition-colors flex items-center gap-1"
                      title={searchType === 'people' ? 'Quick import (no email/phone)' : 'Import company'}
                    >
                      <UserPlus size={14} />
                      Import
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {accountStatus?.account?.credit_usage?.[0] && (
              <span>
                Credits used: {accountStatus.account.credit_usage[0].used} / {accountStatus.account.credit_usage[0].allocated === 'inf' ? '∞' : accountStatus.account.credit_usage[0].allocated}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};



// Companies List — Grid (card) view. Mirrors the same data + actions as CompaniesTable.
const CompaniesGrid = ({
  companies,
  onEdit,
  onCreateLead,
  onEnrich,
  onSendToQueue,
  onAddToFolder,
  companyFolders,
}) => {
  if (!companies || companies.length === 0) {
    return (
      <div className="bg-black border border-gray-700 rounded-xl p-12 text-center text-gray-500">
        No companies match your filters.
      </div>
    );
  }
  const formatDate = (s) => {
    if (!s) return '';
    try {
      return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return ''; }
  };
  return (
    <div
      data-testid="lead-companies-grid"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
    >
      {companies.map((company) => {
        const pc = company.primary_contact || {};
        const folderCount = (companyFolders && companyFolders[company.id]?.length) || 0;
        return (
          <div
            key={company.id}
            data-testid={`lead-company-card-${company.id}`}
            className="group bg-black border border-gray-700 hover:border-cyan-500/50 rounded-xl p-4 transition-all hover:shadow-lg hover:shadow-cyan-500/10 flex flex-col"
          >
            {/* Header: logo + name */}
            <div className="flex items-start gap-3 mb-3">
              <div className="w-11 h-11 bg-white rounded-lg flex items-center justify-center text-gray-700 font-bold overflow-hidden flex-shrink-0">
                {company.logo_url ? (
                  <img
                    src={company.logo_url}
                    alt={company.name || company.company_name}
                    className="w-full h-full object-contain p-1"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML = (company.name || company.company_name)?.charAt(0) || '?';
                    }}
                  />
                ) : (
                  (company.name || company.company_name)?.charAt(0) || '?'
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-white font-medium truncate" title={company.name || company.company_name}>
                  {company.name || company.company_name}
                </div>
                {company.website && (
                  <a
                    href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-400 hover:text-cyan-400 flex items-center gap-1 truncate"
                  >
                    <Globe size={10} />
                    <span className="truncate">{company.website}</span>
                  </a>
                )}
              </div>
              {company.industry && (
                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-300 whitespace-nowrap">
                  {company.industry}
                </span>
              )}
            </div>

            {/* Primary contact */}
            {pc.name ? (
              <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-2.5 mb-3 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                    {pc.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-white truncate">{pc.name}</div>
                    {pc.title && <div className="text-xs text-gray-400 truncate">{pc.title}</div>}
                  </div>
                </div>
                {pc.email && (
                  <a
                    href={`mailto:${pc.email}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-cyan-400 hover:text-cyan-300 text-xs flex items-center gap-1 truncate"
                  >
                    <Mail size={11} />
                    <span className="truncate">{pc.email}</span>
                  </a>
                )}
                {pc.phone && (
                  <a
                    href={`tel:${pc.phone}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-gray-300 hover:text-white text-xs flex items-center gap-1"
                  >
                    <Phone size={11} />
                    {pc.phone}
                  </a>
                )}
              </div>
            ) : (
              <div className="text-xs text-gray-500 italic mb-3">No primary contact</div>
            )}

            {/* Meta row */}
            <div className="flex items-center justify-between text-xs text-gray-500 mb-3 mt-auto">
              {company.location ? (
                <span className="flex items-center gap-1 truncate">
                  <MapPin size={11} />
                  <span className="truncate">{company.location}</span>
                </span>
              ) : <span />}
              <span>{formatDate(company.created_at)}</span>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-1 pt-2 border-t border-gray-800">
              <button
                onClick={() => onSendToQueue && onSendToQueue(company)}
                className="p-1.5 hover:bg-cyan-500/20 rounded-lg text-gray-400 hover:text-cyan-400 transition-colors"
                title="Send to email queue"
              >
                <Send size={15} />
              </button>
              <div className="relative">
                <button
                  onClick={() => onAddToFolder && onAddToFolder(company)}
                  className={`p-1.5 hover:bg-gray-800 rounded-lg transition-colors ${
                    folderCount > 0 ? 'text-yellow-400 hover:text-yellow-300' : 'text-gray-400 hover:text-yellow-400'
                  }`}
                  title={folderCount > 0 ? `In ${folderCount} folder(s)` : 'Add to folder'}
                >
                  <Folder size={15} />
                </button>
                {folderCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-yellow-500 text-black text-[9px] font-bold rounded-full flex items-center justify-center">
                    {folderCount}
                  </span>
                )}
              </div>
              <button
                onClick={() => onEdit(company)}
                className="p-1.5 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
                title="Edit company"
              >
                <Edit size={15} />
              </button>
              <button
                onClick={() => onEnrich(company)}
                className="p-1.5 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-purple-400 transition-colors"
                title="Enrich data"
              >
                <Sparkles size={15} />
              </button>
              <button
                onClick={() => onCreateLead(company)}
                className="p-1.5 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-green-400 transition-colors"
                title="Create lead"
              >
                <Target size={15} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};


// Companies List
const CompaniesTable = ({ 
  companies, 
  onEdit, 
  onCreateLead, 
  onEnrich, 
  onBulkDelete, 
  sortField, 
  sortDirection, 
  onSort, 
  onAddToFolder, 
  companyFolders, 
  folders,
  categories,
  onBulkMoveToFolder,
  onBulkMoveToCategory,
  onSendToQueue,
  onBulkSendToQueue
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [showBulkFolderMenu, setShowBulkFolderMenu] = useState(false);
  const [showBulkCategoryMenu, setShowBulkCategoryMenu] = useState(false);

  // Get selected companies
  const selectedCompanies = companies.filter(c => selectedIds.includes(c.id));

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(companies.map(c => c.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedIds, id];
      setSelectedIds(newSelected);
      if (newSelected.length === companies.length) {
        setSelectAll(true);
      }
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    if (window.confirm(`Delete ${selectedIds.length} selected companies and all their related data?`)) {
      onBulkDelete(selectedIds);
      setSelectedIds([]);
      setSelectAll(false);
    }
  };

  const handleBulkFolderSelect = (folderId) => {
    if (onBulkMoveToFolder && selectedIds.length > 0) {
      onBulkMoveToFolder(selectedIds, folderId);
      toast.success(`${selectedIds.length} companies added to folder`);
    }
    setShowBulkFolderMenu(false);
  };

  const handleBulkCategorySelect = (categoryId) => {
    if (onBulkMoveToCategory && selectedIds.length > 0) {
      onBulkMoveToCategory(selectedIds, categoryId);
      toast.success(`${selectedIds.length} companies moved to category`);
    }
    setShowBulkCategoryMenu(false);
  };

  const handleBulkEmailQueue = () => {
    if (onBulkSendToQueue && selectedCompanies.length > 0) {
      onBulkSendToQueue(selectedCompanies);
    }
  };

  const toggleRowExpansion = (companyId) => {
    setExpandedRows(prev => ({
      ...prev,
      [companyId]: !prev[companyId]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Sortable column header component
  const SortableHeader = ({ field, label }) => {
    const isActive = sortField === field;
    return (
      <th 
        className="text-left px-4 py-3 text-sm text-gray-400 font-medium cursor-pointer hover:text-white transition-colors group"
        onClick={() => onSort && onSort(field)}
      >
        <div className="flex items-center gap-1">
          <span>{label}</span>
          <div className="flex flex-col">
            {isActive && sortDirection === 'asc' && (
              <ChevronUp size={14} className="text-cyan-400" />
            )}
            {isActive && sortDirection === 'desc' && (
              <ChevronDown size={14} className="text-cyan-400" />
            )}
            {!isActive && (
              <ChevronDown size={14} className="text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </div>
        </div>
      </th>
    );
  };

  return (
    <div className="bg-black border border-gray-700 rounded-xl overflow-hidden">
      {/* Enhanced Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div className="px-4 py-3 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border-b border-cyan-500/30 flex items-center justify-between">
          <span className="text-cyan-400 font-medium">
            {selectedIds.length} {selectedIds.length === 1 ? 'company' : 'companies'} selected
          </span>
          <div className="flex items-center gap-2">
            {/* Bulk Move to Folder */}
            {folders && folders.length > 1 && (
              <div className="relative">
                <button
                  onClick={() => { setShowBulkFolderMenu(!showBulkFolderMenu); setShowBulkCategoryMenu(false); }}
                  className="px-3 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-lg flex items-center gap-2 font-medium transition-colors text-sm"
                >
                  <Folder size={16} />
                  Move to Folder
                  <ChevronDown size={14} />
                </button>
                {showBulkFolderMenu && (
                  <div className="absolute top-full right-0 mt-1 w-48 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-20 py-1">
                    {folders.filter(f => f.id !== 'all').map(folder => (
                      <button
                        key={folder.id}
                        onClick={() => handleBulkFolderSelect(folder.id)}
                        className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-800 flex items-center gap-2"
                      >
                        <div className={`w-3 h-3 rounded ${folder.color}`}></div>
                        {folder.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Bulk Move to Category */}
            {categories && categories.length > 1 && (
              <div className="relative">
                <button
                  onClick={() => { setShowBulkCategoryMenu(!showBulkCategoryMenu); setShowBulkFolderMenu(false); }}
                  className="px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg flex items-center gap-2 font-medium transition-colors text-sm"
                >
                  <Tag size={16} />
                  Move to Category
                  <ChevronDown size={14} />
                </button>
                {showBulkCategoryMenu && (
                  <div className="absolute top-full right-0 mt-1 w-48 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-20 py-1">
                    {categories.filter(c => c.id !== 'all').map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => handleBulkCategorySelect(cat.id)}
                        className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-800 flex items-center gap-2"
                      >
                        <div className={`w-3 h-3 rounded ${cat.color}`}></div>
                        {cat.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Bulk Send to Email Queue */}
            <button
              onClick={handleBulkEmailQueue}
              className="px-3 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-lg flex items-center gap-2 font-medium transition-colors text-sm"
            >
              <Send size={16} />
              Send to Queue
            </button>
            
            {/* Bulk Delete */}
            <button
              onClick={handleBulkDelete}
              className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg flex items-center gap-2 font-medium transition-colors text-sm"
            >
              <Trash2 size={16} />
              Delete
            </button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1200px]">
          <thead>
            <tr className="border-b border-gray-700 bg-gray-900/50">
              <th className="px-2 py-3 w-8"></th>
              <th className="px-4 py-3 w-12">
                <input
                  type="checkbox"
                  checked={selectAll && companies.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded bg-gray-800 border-gray-600 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0"
                />
              </th>
              <SortableHeader field="name" label="Company" />
              <SortableHeader field="first_name" label="First Name" />
              <SortableHeader field="last_name" label="Last Name" />
              <SortableHeader field="role" label="Role" />
              <SortableHeader field="email" label="Email" />
              <SortableHeader field="phone" label="Phone" />
              <SortableHeader field="location" label="Location" />
              <SortableHeader field="industry" label="Category" />
              <SortableHeader field="created_at" label="Date Added" />
              <th className="text-right px-4 py-3 text-sm text-gray-400 font-medium min-w-[180px]">Actions</th>
            </tr>
          </thead>
        <tbody>
          {companies.map(company => {
            const hasMultipleContacts = (company.contact_count || 0) > 1;
            const isExpanded = expandedRows[company.id];
            const additionalContacts = company.contacts?.slice(1) || [];
            
            return (
              <React.Fragment key={company.id}>
                <tr 
                  className={`border-b border-gray-800 hover:bg-gray-900/50 transition-colors cursor-pointer ${selectedIds.includes(company.id) ? 'bg-cyan-500/5' : ''}`}
                  onClick={(e) => {
                    // Don't trigger on checkbox, expand arrow, buttons, or links
                    if (
                      e.target.tagName === 'INPUT' || 
                      e.target.tagName === 'BUTTON' || 
                      e.target.tagName === 'A' ||
                      e.target.closest('button') ||
                      e.target.closest('a') ||
                      e.target.closest('input')
                    ) {
                      return;
                    }
                    onEdit(company);
                  }}
                >
                  {/* Expand Arrow */}
                  <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                    {hasMultipleContacts ? (
                      <button
                        onClick={() => toggleRowExpansion(company.id)}
                        className="p-1 hover:bg-gray-800 rounded text-gray-400 hover:text-white transition-colors"
                      >
                        <ChevronRight 
                          size={16} 
                          className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      </button>
                    ) : (
                      <span className="w-6"></span>
                    )}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(company.id)}
                      onChange={() => handleSelectOne(company.id)}
                      className="w-4 h-4 rounded bg-gray-800 border-gray-600 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-gray-600 font-bold overflow-hidden">
                        {company.logo_url ? (
                          <img 
                            src={company.logo_url} 
                            alt={company.name || company.company_name}
                            className="w-full h-full object-contain p-1"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentNode.innerHTML = (company.name || company.company_name)?.charAt(0) || '?';
                            }}
                          />
                        ) : (
                          (company.name || company.company_name)?.charAt(0) || '?'
                        )}
                      </div>
                      <div>
                        <div className="text-white font-medium">{company.name || company.company_name}</div>
                        <div className="flex items-center gap-2">
                          {company.website && (
                            <a 
                              href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-gray-400 hover:text-cyan-400 flex items-center gap-1"
                            >
                              <Globe size={10} />
                              {company.website}
                            </a>
                          )}
                          {company.contact_count > 0 && (
                            <span className="text-xs text-gray-500">
                              • {company.contact_count} contact{company.contact_count > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {company.primary_contact ? (
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {company.primary_contact.name?.split(' ').map(n => n[0]).join('').substring(0, 2) || '?'}
                        </div>
                        <div>
                          <div className="text-white text-sm">
                            {company.primary_contact.name?.split(' ')[0] || '-'}
                          </div>
                          {company.primary_contact.linkedin_url && (
                            <a 
                              href={company.primary_contact.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-400 hover:text-blue-300"
                            >
                              LinkedIn
                            </a>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {company.primary_contact?.name ? (
                      <span className="text-white text-sm">
                        {company.primary_contact.name.split(' ').slice(1).join(' ') || '-'}
                      </span>
                    ) : (
                      <span className="text-gray-500 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {company.primary_contact?.title ? (
                      <span className="text-gray-300 text-sm">{company.primary_contact.title}</span>
                    ) : (
                      <span className="text-gray-500 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {company.primary_contact?.email ? (
                      <a 
                        href={`mailto:${company.primary_contact.email}`}
                        className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1"
                      >
                        <Mail size={12} />
                        {company.primary_contact.email}
                      </a>
                    ) : (
                      <span className="text-gray-500 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {company.primary_contact?.phone ? (
                      <a 
                        href={`tel:${company.primary_contact.phone}`}
                        className="text-gray-300 hover:text-white text-sm flex items-center gap-1"
                      >
                        <Phone size={12} />
                        {company.primary_contact.phone}
                      </a>
                    ) : (
                      <span className="text-gray-500 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {company.location ? (
                      <span className="text-gray-300 text-sm flex items-center gap-1">
                        <MapPin size={12} className="text-gray-500" />
                        {company.location}
                      </span>
                    ) : (
                      <span className="text-gray-500 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {company.industry ? (
                      <span className="px-2 py-1 rounded text-xs font-medium bg-purple-500/20 text-purple-400">
                        {company.industry}
                      </span>
                    ) : (
                      <span className="text-gray-500 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-gray-400 text-sm">
                      {formatDate(company.created_at)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {/* Send to Queue button */}
                      <button
                        onClick={() => onSendToQueue && onSendToQueue(company)}
                        className="p-2 hover:bg-cyan-500/20 rounded-lg text-gray-400 hover:text-cyan-400 transition-colors"
                        title="Send to email queue"
                      >
                        <Send size={16} />
                      </button>
                      {/* Folder indicator/button */}
                      <div className="relative group">
                        <button
                          onClick={() => onAddToFolder && onAddToFolder(company)}
                          className={`p-2 hover:bg-gray-800 rounded-lg transition-colors ${
                            companyFolders && companyFolders[company.id]?.length > 0
                              ? 'text-yellow-400 hover:text-yellow-300'
                              : 'text-gray-400 hover:text-yellow-400'
                          }`}
                          title={companyFolders && companyFolders[company.id]?.length > 0 
                            ? `In ${companyFolders[company.id].length} folder(s)` 
                            : 'Add to folder'}
                        >
                          <Folder size={16} />
                        </button>
                        {companyFolders && companyFolders[company.id]?.length > 0 && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 text-black text-[10px] font-bold rounded-full flex items-center justify-center">
                            {companyFolders[company.id].length}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => onEdit(company)}
                        className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
                        title="Edit company"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => onEnrich(company)}
                        className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-purple-400 transition-colors"
                        title="Enrich data"
                      >
                        <Sparkles size={16} />
                      </button>
                      <button
                        onClick={() => onCreateLead(company)}
                        className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-green-400 transition-colors"
                        title="Create lead"
                      >
                        <Target size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
                
                {/* Expanded Contacts Rows */}
                {isExpanded && additionalContacts.map((contact, idx) => (
                  <tr 
                    key={`${company.id}-contact-${idx}`}
                    className="bg-gray-900/30 border-b border-gray-800/50"
                  >
                    {/* Expand Arrow */}
                    <td className="px-2 py-2"></td>
                    {/* Checkbox */}
                    <td className="px-4 py-2"></td>
                    {/* Company - show "Additional Contact" label */}
                    <td className="px-4 py-2 pl-12">
                      <span className="text-xs text-gray-500">Additional Contact</span>
                    </td>
                    {/* First Name */}
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {contact.name?.split(' ').map(n => n[0]).join('').substring(0, 2) || '?'}
                        </div>
                        <span className="text-gray-300 text-sm">
                          {contact.name?.split(' ')[0] || '-'}
                        </span>
                      </div>
                    </td>
                    {/* Last Name */}
                    <td className="px-4 py-2">
                      <span className="text-gray-300 text-sm">
                        {contact.name?.split(' ').slice(1).join(' ') || '-'}
                      </span>
                    </td>
                    {/* Role/Title */}
                    <td className="px-4 py-2">
                      <span className="text-gray-400 text-sm">{contact.title || '-'}</span>
                    </td>
                    {/* Email */}
                    <td className="px-4 py-2">
                      {contact.email ? (
                        <a 
                          href={`mailto:${contact.email}`}
                          className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1"
                        >
                          <Mail size={12} />
                          {contact.email}
                        </a>
                      ) : (
                        <span className="text-gray-500 text-sm">-</span>
                      )}
                    </td>
                    {/* Phone */}
                    <td className="px-4 py-2">
                      {contact.phone ? (
                        <a 
                          href={`tel:${contact.phone}`}
                          className="text-gray-400 hover:text-white text-sm flex items-center gap-1"
                        >
                          <Phone size={12} />
                          {contact.phone}
                        </a>
                      ) : (
                        <span className="text-gray-500 text-sm">-</span>
                      )}
                    </td>
                    {/* Location - empty for additional contacts */}
                    <td className="px-4 py-2">
                      <span className="text-gray-500 text-sm">-</span>
                    </td>
                    {/* Category - empty for additional contacts */}
                    <td className="px-4 py-2">
                      <span className="text-gray-500 text-sm">-</span>
                    </td>
                    {/* Date Added - empty for additional contacts */}
                    <td className="px-4 py-2">
                      <span className="text-gray-500 text-sm">-</span>
                    </td>
                    {/* Actions - empty for additional contacts */}
                    <td className="px-4 py-2"></td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
      {companies.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Building className="mx-auto mb-3 text-gray-600" size={40} />
          <p>No companies found</p>
          <p className="text-sm mt-1">Add your first company to get started</p>
        </div>
      )}
    </div>
  );
};

// TheOrg Lead Finder Modal
const TheOrgLeadFinderModal = ({ 
  isOpen, 
  onClose, 
  theOrgSearch,
  setTheOrgSearch,
  theOrgIndustry,
  setTheOrgIndustry,
  theOrgResults,
  theOrgLoading,
  searchTheOrg,
  theOrgSelectedCompany,
  setTheOrgSelectedCompany,
  theOrgOrgChart,
  theOrgOrgChartLoading,
  fetchTheOrgOrgChart,
  importTheOrgLead,
  importingLead,
  theOrgCredits,
  fetchTheOrgCredits,
  theOrgExecutiveSearch,
  setTheOrgExecutiveSearch,
  enrichTheOrgContact,
  enrichingContact,
  enrichedContacts
}) => {
  useEffect(() => {
    if (isOpen) {
      fetchTheOrgCredits();
    }
  }, [isOpen, fetchTheOrgCredits]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Building className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">TheOrg Lead Finder</h2>
              <p className="text-sm text-gray-400">Search companies and import executive contacts</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {theOrgCredits && (
              <div className="text-sm text-gray-400">
                Credits: <span className="text-cyan-400 font-medium">{theOrgCredits.data?.planCredits ?? theOrgCredits.credits_remaining ?? 'N/A'}</span>
              </div>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-800 bg-gray-900/50">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
              <input
                type="text"
                value={theOrgSearch}
                onChange={(e) => setTheOrgSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchTheOrg()}
                placeholder="Search company name (e.g., Salesforce, Google, Stripe)"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
              />
            </div>
            <select
              value={theOrgIndustry}
              onChange={(e) => setTheOrgIndustry(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500"
            >
              {INDUSTRIES.map(ind => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
            </select>
            <button
              onClick={searchTheOrg}
              disabled={theOrgLoading || !theOrgSearch.trim()}
              className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2"
            >
              {theOrgLoading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              Search
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Company List */}
          <div className={`${theOrgSelectedCompany ? 'w-1/3 border-r border-gray-800' : 'w-full'} overflow-y-auto`}>
            {theOrgLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={32} className="animate-spin text-cyan-400" />
              </div>
            ) : theOrgResults.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                <Building size={48} className="mb-4 opacity-50" />
                <p>Search for companies to find leads</p>
                <p className="text-sm mt-1">Try searching "Salesforce" or "Tech startups"</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {theOrgResults.map((company, idx) => {
                  const companyDomain = company.domain || company.domains?.[0] || '';
                  const companyLogo = company.logo_url || company.logoUrl;
                  
                  return (
                    <div
                      key={company.id || idx}
                      onClick={() => {
                        setTheOrgSelectedCompany({...company, domain: companyDomain, logo_url: companyLogo});
                        setTheOrgExecutiveSearch(''); // Reset search when selecting new company
                        if (companyDomain) {
                          fetchTheOrgOrgChart(companyDomain);
                        }
                      }}
                      className={`p-4 cursor-pointer hover:bg-gray-800 transition-colors ${
                        theOrgSelectedCompany?.id === company.id ? 'bg-gray-800 border-l-2 border-cyan-500' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {companyLogo ? (
                          <img src={companyLogo} alt="" className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center">
                            <Building size={20} className="text-gray-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-white truncate">{company.name}</h4>
                          {company.industry && (
                            <p className="text-xs text-gray-400">{company.industry}</p>
                          )}
                          {companyDomain && (
                            <p className="text-xs text-cyan-400">{companyDomain}</p>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            importTheOrgLead({...company, domain: companyDomain});
                          }}
                          disabled={importingLead === companyDomain}
                          className="text-cyan-400 hover:text-cyan-300 p-1"
                          title="Import as Lead"
                        >
                          {importingLead === companyDomain ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Plus size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Org Chart / Executives Panel */}
          {theOrgSelectedCompany && (
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 border-b border-gray-800 bg-gray-900/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {theOrgSelectedCompany.logo_url ? (
                      <img src={theOrgSelectedCompany.logo_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center">
                        <Building size={24} className="text-gray-500" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-white text-lg">{theOrgSelectedCompany.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        {theOrgSelectedCompany.industry && <span>{theOrgSelectedCompany.industry}</span>}
                        {theOrgSelectedCompany.employee_count && (
                          <>
                            <span>•</span>
                            <span>{theOrgSelectedCompany.employee_count} employees</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => importTheOrgLead(theOrgSelectedCompany)}
                    disabled={importingLead === theOrgSelectedCompany.domain}
                    className="bg-cyan-600 hover:bg-cyan-500 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                  >
                    {importingLead === theOrgSelectedCompany.domain ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Plus size={14} />
                    )}
                    Import Company
                  </button>
                </div>
              </div>

              {/* Executives List */}
              <div className="p-4">
                <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                  <Users size={14} />
                  Executives & Team ({theOrgOrgChart?.total_positions || 0})
                </h4>

                {/* Search Input for Executives */}
                {theOrgOrgChart?.executives?.length > 0 && (
                  <div className="mb-3">
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input
                        type="text"
                        value={theOrgExecutiveSearch}
                        onChange={(e) => setTheOrgExecutiveSearch(e.target.value)}
                        placeholder="Search by name or role..."
                        className="w-full pl-9 pr-8 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                        data-testid="theorg-executive-search"
                      />
                      {theOrgExecutiveSearch && (
                        <button
                          onClick={() => setTheOrgExecutiveSearch('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    {theOrgExecutiveSearch && (
                      <p className="text-xs text-gray-500 mt-1">
                        Showing {theOrgOrgChart.executives.filter(exec => {
                          const search = theOrgExecutiveSearch.toLowerCase();
                          const name = (exec.full_name || exec.fullName || '').toLowerCase();
                          const title = (exec.title || '').toLowerCase();
                          return name.includes(search) || title.includes(search);
                        }).length} of {theOrgOrgChart.total_positions} results
                      </p>
                    )}
                  </div>
                )}

                {theOrgOrgChartLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 size={24} className="animate-spin text-cyan-400" />
                  </div>
                ) : theOrgOrgChart?.executives?.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {theOrgOrgChart.executives
                      .filter(exec => {
                        if (!theOrgExecutiveSearch) return true;
                        const search = theOrgExecutiveSearch.toLowerCase();
                        const name = (exec.full_name || exec.fullName || '').toLowerCase();
                        const title = (exec.title || '').toLowerCase();
                        return name.includes(search) || title.includes(search);
                      })
                      .slice(0, 100) // Limit to 100 results for performance
                      .map((exec, idx) => {
                        const execKey = `${theOrgSelectedCompany?.domain}-${exec.id}`;
                        const enrichedData = enrichedContacts[execKey];
                        const hasEmail = enrichedData?.email || exec.email;
                        const hasPhone = enrichedData?.phone;
                        
                        return (
                      <div
                        key={exec.id || idx}
                        className="p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden">
                              {enrichedData?.profile_photo ? (
                                <img src={enrichedData.profile_photo} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Users size={14} className="text-gray-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium text-sm">{exec.full_name || exec.fullName}</p>
                              <p className="text-xs text-gray-400">{exec.title}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Unlock Contact Button */}
                            {!enrichedData && (
                              <button
                                onClick={() => enrichTheOrgContact(exec, theOrgSelectedCompany?.domain)}
                                disabled={enrichingContact === execKey}
                                className="text-yellow-400 hover:text-yellow-300 p-1 flex items-center gap-1"
                                title="Unlock contact info (uses 1 credit)"
                              >
                                {enrichingContact === execKey ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <>
                                    <Lock size={12} />
                                    <span className="text-xs">Unlock</span>
                                  </>
                                )}
                              </button>
                            )}
                            {(exec.linkedin_url || exec.linkedInUrl || enrichedData?.linkedin_url) && (
                              <a
                                href={enrichedData?.linkedin_url || exec.linkedin_url || exec.linkedInUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300 p-1"
                                title="View LinkedIn"
                              >
                                <ExternalLink size={14} />
                              </a>
                            )}
                            <button
                              onClick={() => importTheOrgLead(theOrgSelectedCompany, {
                                ...exec,
                                email: enrichedData?.email || exec.email,
                                phone: enrichedData?.phone
                              })}
                              disabled={importingLead === execKey}
                              className="text-cyan-400 hover:text-cyan-300 p-1"
                              title="Import as Lead"
                            >
                              {importingLead === execKey ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <Plus size={14} />
                              )}
                            </button>
                          </div>
                        </div>
                        
                        {/* Enriched Contact Info */}
                        {enrichedData && (hasEmail || hasPhone) && (
                          <div className="mt-2 pt-2 border-t border-gray-700 flex flex-wrap gap-3">
                            {hasEmail && (
                              <a 
                                href={`mailto:${enrichedData.email || exec.email}`}
                                className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1"
                              >
                                <Mail size={12} />
                                {enrichedData.email || exec.email}
                              </a>
                            )}
                            {hasPhone && (
                              <a 
                                href={`tel:${enrichedData.phone}`}
                                className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1"
                              >
                                <Phone size={12} />
                                {enrichedData.phone}
                              </a>
                            )}
                          </div>
                        )}
                        
                        {/* No contact info message */}
                        {enrichedData && !hasEmail && !hasPhone && (
                          <div className="mt-2 pt-2 border-t border-gray-700">
                            <p className="text-xs text-gray-500">No email or phone available for this contact</p>
                          </div>
                        )}
                      </div>
                    )})}
                    {theOrgOrgChart.executives.filter(exec => {
                      if (!theOrgExecutiveSearch) return true;
                      const search = theOrgExecutiveSearch.toLowerCase();
                      const name = (exec.full_name || exec.fullName || '').toLowerCase();
                      const title = (exec.title || '').toLowerCase();
                      return name.includes(search) || title.includes(search);
                    }).length > 100 && (
                      <p className="text-xs text-gray-500 text-center py-2">
                        Showing first 100 results. Use search to find specific people.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Users size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No executives found</p>
                    <p className="text-sm mt-1">Org chart data may not be available for this company</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Data provided by TheOrg.com • Unlock contact = 1 credit • Org charts cost varies
          </p>
          <button
            onClick={onClose}
            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Lead Finder Modal
const LeadFinderModal = ({ isOpen, onClose, onLeadsFound }) => {
  const [step, setStep] = useState(1);
  const [searchMode, setSearchMode] = useState('companies'); // 'companies' or 'people'
  const [industry, setIndustry] = useState('');
  const [location, setLocation] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [keywords, setKeywords] = useState('');
  const [numLeads, setNumLeads] = useState(50);
  const [isSearching, setIsSearching] = useState(false);
  const [foundLeads, setFoundLeads] = useState([]);
  const [foundPeople, setFoundPeople] = useState([]);
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [error, setError] = useState('');
  
  // People search fields
  const [personName, setPersonName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [numPeople, setNumPeople] = useState(10);

  // Toggle person selection
  const togglePersonSelection = (idx) => {
    if (selectedPeople.includes(idx)) {
      setSelectedPeople(selectedPeople.filter(i => i !== idx));
    } else {
      setSelectedPeople([...selectedPeople, idx]);
    }
  };

  // Add selected people to pipeline
  const handleAddSelectedToPipeline = async () => {
    if (selectedPeople.length === 0) return;
    
    let added = 0;
    for (const idx of selectedPeople) {
      const person = foundPeople[idx];
      if (person.company_id) {
        try {
          const response = await fetch(`${API_URL}/api/lead-gen/leads`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              company_id: person.company_id,
              contact_id: person.id,
              stage: 'new',
              deal_value: 0,
              notes: `Added from People Finder - ${person.name} (${person.title})`
            })
          });
          if (response.ok) added++;
        } catch (err) {
          console.error('Failed to add lead:', err);
        }
      }
    }
    
    if (added > 0) {
      toast.success(`Added ${added} people to pipeline!`);
      onLeadsFound();
      setSelectedPeople([]);
    }
  };

  const industries = [
    'Technology', 'Healthcare', 'Finance', 'Real Estate', 'Retail',
    'Manufacturing', 'Education', 'Marketing', 'Legal', 'Consulting',
    'Construction', 'Transportation', 'Hospitality', 'Energy', 'Media',
    'Telecommunications', 'Insurance', 'Pharmaceutical', 'Automotive', 'Entertainment', 'Other'
  ];

  const companySizes = [
    { value: '', label: 'Any Size' },
    { value: '1-10', label: '1-10 employees (Startup)' },
    { value: '10-50', label: '10-50 employees (Small)' },
    { value: '50-200', label: '50-200 employees (Medium)' },
    { value: '200+', label: '200+ employees (Enterprise)' },
  ];

  const targetRoles = [
    'CEO / Founder', 'CTO / Technical Lead', 'CMO / Marketing Director',
    'CFO / Finance Director', 'VP of Sales', 'Head of Operations',
    'HR Director', 'Procurement Manager', 'IT Director', 'Other'
  ];

  const handleSearch = async () => {
    if (!industry) {
      setError('Please select an industry');
      return;
    }
    
    setIsSearching(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/find-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          industry,
          location: location || null,
          company_size: companySize || null,
          target_role: targetRole || null,
          keywords: keywords || null,
          num_leads: numLeads
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setFoundLeads(data.companies || []);
        setStep(3);
        toast.success(`Found ${data.total_added} companies!`);
      } else {
        setError(data.detail || 'Failed to find leads');
      }
    } catch (err) {
      setError('Failed to connect to lead finder');
    } finally {
      setIsSearching(false);
    }
  };

  const handlePeopleSearch = async () => {
    if (!personName && !companyName && !jobTitle && !industry) {
      setError('Please provide at least one search criteria');
      return;
    }
    
    setIsSearching(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/find-people`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_name: personName || null,
          company_name: companyName || null,
          job_title: jobTitle || null,
          industry: industry || null,
          location: location || null,
          num_results: numPeople
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setFoundPeople(data.people || []);
        setStep(3);
        toast.success(`Found ${data.total_found} people with emails!`);
      } else {
        setError(data.detail || 'Failed to find people');
      }
    } catch (err) {
      setError('Failed to connect to people finder');
    } finally {
      setIsSearching(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSearchMode('companies');
    setIndustry('');
    setLocation('');
    setCompanySize('');
    setTargetRole('');
    setKeywords('');
    setPersonName('');
    setCompanyName('');
    setJobTitle('');
    setFoundLeads([]);
    setFoundPeople([]);
    setSelectedPeople([]);
    setError('');
    onClose();
    if (foundLeads.length > 0 || foundPeople.length > 0) {
      onLeadsFound();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Sparkles className="text-purple-400" size={24} />
              AI Lead Finder
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {searchMode === 'companies' ? (
                <>
                  {step === 1 && 'Select an industry to search'}
                  {step === 2 && 'Narrow your search criteria'}
                  {step === 3 && `Found ${foundLeads.length} companies`}
                </>
              ) : (
                <>
                  {step === 1 && 'Search for specific people'}
                  {step === 2 && 'Add more details'}
                  {step === 3 && `Found ${foundPeople.length} people with emails`}
                </>
              )}
            </p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Search Mode Toggle */}
        {step === 1 && (
          <div className="px-6 pt-4">
            <div className="flex bg-gray-800 rounded-lg p-1 gap-1">
              <button
                onClick={() => setSearchMode('companies')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  searchMode === 'companies'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Building size={16} />
                Find Companies
              </button>
              <button
                onClick={() => setSearchMode('people')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  searchMode === 'people'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Users size={16} />
                Find People & Emails
              </button>
            </div>
          </div>
        )}

        {/* Step Indicator */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s ? 'bg-purple-500 text-white' : 'bg-gray-800 text-gray-500'
                }`}>
                  {step > s ? <Check size={16} /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-1 mx-1 rounded ${step > s ? 'bg-purple-500' : 'bg-gray-800'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2 px-1">
            {searchMode === 'companies' ? (
              <>
                <span>Industry</span>
                <span>Details</span>
                <span>Results</span>
              </>
            ) : (
              <>
                <span>Search</span>
                <span>Refine</span>
                <span>Results</span>
              </>
            )}
          </div>
        </div>

        {/* COMPANIES MODE - Step 1: Industry Selection */}
        {searchMode === 'companies' && step === 1 && (
          <div className="p-6">
            <label className="block text-sm text-gray-400 mb-3">Select Industry *</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {industries.map((ind) => (
                <button
                  key={ind}
                  onClick={() => { setIndustry(ind); setStep(2); }}
                  className={`p-4 rounded-xl border text-left transition-all ${
                    industry === ind
                      ? 'border-purple-500 bg-purple-500/20 text-purple-400'
                      : 'border-gray-700 hover:border-gray-600 text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  <span className="font-medium">{ind}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* PEOPLE MODE - Step 1: Search Criteria */}
        {searchMode === 'people' && step === 1 && (
          <div className="p-6 space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <Mail className="text-blue-400" size={18} />
                <span className="text-blue-400 text-sm">Find specific people and their business email addresses</span>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm text-gray-400 mb-2">Person's Name</label>
              <input
                type="text"
                value={personName}
                onChange={(e) => setPersonName(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
                placeholder="e.g., John Smith"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Company Name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
                placeholder="e.g., Microsoft, Tesla, or any company"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Job Title</label>
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
                placeholder="e.g., CEO, Marketing Director, VP of Sales"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Industry (Optional)</label>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
                >
                  <option value="">Any Industry</option>
                  {industries.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Location (Optional)</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
                  placeholder="e.g., New York"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Number of Results</label>
              <div className="flex gap-2">
                {[5, 10, 20, 50].map((num) => (
                  <button
                    key={num}
                    onClick={() => setNumPeople(num)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      numPeople === num
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:text-white'
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handlePeopleSearch}
                disabled={isSearching || (!personName && !companyName && !jobTitle && !industry)}
                className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isSearching ? (
                  <>
                    <RefreshCw className="animate-spin" size={18} />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search size={18} />
                    Find People & Emails
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* COMPANIES MODE - Step 2: Search Criteria */}
        {searchMode === 'companies' && step === 2 && (
          <div className="p-6 space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}
            
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <Tag className="text-purple-400" size={18} />
                <span className="text-purple-400 font-medium">{industry}</span>
                <button 
                  onClick={() => setStep(1)}
                  className="ml-auto text-xs text-gray-400 hover:text-white"
                >
                  Change
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
                placeholder="e.g., Dallas, TX or Texas or United States"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Company Size</label>
              <select
                value={companySize}
                onChange={(e) => setCompanySize(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
              >
                {companySizes.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Target Decision Maker Role</label>
              <select
                value={targetRole}
                onChange={(e) => setTargetRole(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
              >
                <option value="">Any Role</option>
                {targetRoles.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Keywords / Focus Area</label>
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
                placeholder="e.g., SaaS, e-commerce, B2B, healthcare tech"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Number of Leads to Find</label>
              <div className="flex items-center gap-4">
                {[25, 50, 100].map((n) => (
                  <button
                    key={n}
                    onClick={() => setNumLeads(n)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      numLeads === n
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Results - Companies */}
        {step === 3 && searchMode === 'companies' && (
          <div className="p-6">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-400" size={20} />
                <span className="text-green-400 font-medium">
                  Successfully added {foundLeads.length} companies to your Target List!
                </span>
              </div>
            </div>

            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {foundLeads.slice(0, 10).map((company, idx) => (
                <div key={idx} className="bg-gray-800 rounded-lg p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white font-bold">
                    {company.name?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate">{company.name}</div>
                    <div className="text-xs text-gray-400 truncate">
                      {company.location} • {company.size}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    company.priority === 'A' ? 'bg-red-500/20 text-red-400' :
                    company.priority === 'B' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {company.priority}
                  </span>
                </div>
              ))}
              {foundLeads.length > 10 && (
                <div className="text-center text-gray-500 text-sm py-2">
                  +{foundLeads.length - 10} more companies added
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Results - People */}
        {step === 3 && searchMode === 'people' && (
          <div className="p-6">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-400" size={20} />
                <div>
                  <span className="text-green-400 font-medium">
                    Found {foundPeople.length} people with email addresses!
                  </span>
                  <p className="text-green-400/70 text-sm mt-1">
                    Companies and contacts have been added to your Target List automatically.
                  </p>
                </div>
              </div>
            </div>

            {/* Selection bar */}
            {selectedPeople.length > 0 && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 mb-4 flex items-center justify-between">
                <span className="text-purple-400 font-medium">
                  {selectedPeople.length} {selectedPeople.length === 1 ? 'person' : 'people'} selected
                </span>
                <button
                  onClick={handleAddSelectedToPipeline}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 font-medium transition-colors"
                >
                  <Plus size={16} />
                  Add to Pipeline
                </button>
              </div>
            )}

            <div className="max-h-[400px] overflow-y-auto space-y-3">
              {foundPeople.map((person, idx) => (
                <div 
                  key={idx} 
                  className={`bg-gray-800 rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedPeople.includes(idx) ? 'ring-2 ring-purple-500 bg-purple-500/10' : 'hover:bg-gray-750'
                  }`}
                  onClick={() => togglePersonSelection(idx)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedPeople.includes(idx)}
                        onChange={() => togglePersonSelection(idx)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                      />
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {person.name?.split(' ').map(n => n[0]).join('').substring(0, 2) || '?'}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-white">{person.name}</div>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          person.confidence === 'high' ? 'bg-green-500/20 text-green-400' :
                          person.confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {person.confidence} confidence
                        </span>
                      </div>
                      <div className="text-sm text-purple-400">{person.title}</div>
                      <div className="text-sm text-gray-400">{person.company_name}</div>
                      
                      <div className="mt-2 flex flex-wrap gap-2">
                        <a 
                          href={`mailto:${person.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30 transition-colors"
                        >
                          <Mail size={14} />
                          {person.email}
                        </a>
                        {person.linkedin_url && (
                          <a 
                            href={person.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600 transition-colors"
                          >
                            <ExternalLink size={14} />
                            LinkedIn
                          </a>
                        )}
                      </div>
                      
                      {person.background && (
                        <div className="mt-2 text-xs text-gray-500 line-clamp-2">
                          {person.background}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Select All / View Target List */}
            <div className="mt-4 flex items-center justify-between">
              <button
                onClick={() => {
                  if (selectedPeople.length === foundPeople.length) {
                    setSelectedPeople([]);
                  } else {
                    setSelectedPeople(foundPeople.map((_, idx) => idx));
                  }
                }}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                {selectedPeople.length === foundPeople.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={() => { handleClose(); /* Switch to Target List tab */ }}
                className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
              >
                View in Target List
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-700">
          {step === 2 && searchMode === 'companies' && (
            <button
              onClick={() => setStep(1)}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Back
            </button>
          )}
          {(step !== 2 || searchMode === 'people') && <div />}
          
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
            >
              {step === 3 ? 'Done' : 'Cancel'}
            </button>
            {step === 2 && searchMode === 'companies' && (
              <button
                onClick={handleSearch}
                disabled={isSearching || !industry}
                className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {isSearching ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Finding Leads...
                  </>
                ) : (
                  <>
                    <Search size={18} />
                    Find {numLeads} Leads
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// CSV Import Modal
const CSVImportModal = ({ isOpen, onClose, onImport }) => {
  const [csvData, setCsvData] = useState('');
  const [parsedData, setParsedData] = useState([]);
  const [isImporting, setIsImporting] = useState(false);

  const parseCSV = () => {
    const lines = csvData.trim().split('\n');
    if (lines.length < 2) {
      toast.error('CSV must have headers and at least one data row');
      return;
    }
    
    // Better CSV parsing that handles quoted fields
    const parseCSVLine = (line) => {
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim().replace(/^"|"$/g, ''));
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim().replace(/^"|"$/g, ''));
      return values;
    };
    
    const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
    const data = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      const values = parseCSVLine(lines[i]);
      const row = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx]?.trim() || '';
      });
      // Only add rows that have at least one non-empty value
      if (Object.values(row).some(v => v)) {
        data.push(row);
      }
    }
    
    setParsedData(data);
    toast.success(`Parsed ${data.length} rows`);
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      toast.error('Please parse CSV first');
      return;
    }
    setIsImporting(true);
    try {
      await onImport(parsedData);
      setCsvData('');
      setParsedData([]);
      onClose();
    } finally {
      setIsImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Upload className="text-cyan-400" size={24} />
            Import Companies from CSV
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">
              Paste CSV Data (with headers: name, website, industry, location, size)
            </label>
            <textarea
              value={csvData}
              onChange={(e) => setCsvData(e.target.value)}
              className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none resize-none font-mono text-sm"
              rows={10}
              placeholder="name,website,industry,location,size&#10;Acme Inc,www.acme.com,Technology,Dallas TX,50-200&#10;..."
            />
          </div>

          {parsedData.length > 0 && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <div className="text-green-400 font-medium mb-2">
                ✓ {parsedData.length} companies ready to import
              </div>
              <div className="text-sm text-gray-400">
                First company: {parsedData[0].name || parsedData[0].company || 'Unknown'}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={parseCSV}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
          >
            Parse CSV
          </button>
          <button
            onClick={handleImport}
            disabled={parsedData.length === 0 || isImporting}
            className="px-6 py-2 bg-cyan-500 text-black font-medium rounded-lg hover:bg-cyan-400 disabled:opacity-50"
          >
            {isImporting ? 'Importing...' : `Import ${parsedData.length} Companies`}
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Lead Modal
const EditLeadModal = ({ isOpen, onClose, lead, onSave, onDelete }) => {
  const [form, setForm] = useState({
    deal_value: 0,
    notes: '',
    tags: [],
    next_followup_date: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setForm({
        deal_value: lead.deal_value || 0,
        notes: lead.notes || '',
        tags: lead.tags || [],
        next_followup_date: lead.next_followup_date ? lead.next_followup_date.split('T')[0] : ''
      });
    }
  }, [lead]);

  const handleAddTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tag) });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({ ...lead, ...form });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !lead) return null;

  const stageConfig = PIPELINE_STAGES.find(s => s.id === lead.stage) || PIPELINE_STAGES[0];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Edit className="text-cyan-400" size={24} />
              Edit Lead
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {lead.company?.name || 'Unknown Company'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Lead Info */}
          <div className="bg-gray-800/50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-3 h-3 rounded-full ${stageConfig.color}`} />
              <span className="font-medium text-white">{stageConfig.label}</span>
              {lead.company?.priority && (
                <span className={`text-xs px-2 py-0.5 rounded ${
                  lead.company.priority === 'A' ? 'bg-red-500/20 text-red-400' :
                  lead.company.priority === 'B' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  Priority {lead.company.priority}
                </span>
              )}
            </div>
            {lead.contact && (
              <div className="text-sm text-gray-400">
                Contact: {lead.contact.name} {lead.contact.email && `(${lead.contact.email})`}
              </div>
            )}
          </div>

          {/* Deal Value */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Deal Value ($)</label>
            <input
              type="number"
              value={form.deal_value}
              onChange={(e) => setForm({ ...form, deal_value: parseFloat(e.target.value) || 0 })}
              className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              placeholder="15000"
            />
          </div>

          {/* Next Follow-up */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Next Follow-up Date</label>
            <input
              type="date"
              value={form.next_followup_date}
              onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })}
              className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Tags</label>
            <div className="flex gap-2 mb-2 flex-wrap">
              {form.tags.map((tag, idx) => (
                <span 
                  key={idx} 
                  className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-sm flex items-center gap-1"
                >
                  {tag}
                  <button onClick={() => handleRemoveTag(tag)} className="hover:text-white">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                className="flex-1 px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                placeholder="Add a tag..."
              />
              <button
                onClick={handleAddTag}
                className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
              >
                Add
              </button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none resize-none"
              rows={4}
              placeholder="Add notes about this lead..."
            />
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-700">
          <button
            onClick={() => { onDelete(lead); onClose(); }}
            className="px-4 py-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors flex items-center gap-2"
          >
            <Trash2 size={16} />
            Delete Lead
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 bg-cyan-500 text-black font-medium rounded-lg hover:bg-cyan-400 disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Queue Item Modal
const EditQueueModal = ({ isOpen, onClose, queueItem, onSave }) => {
  const [form, setForm] = useState({
    subject: '',
    body: '',
    recipient_email: '',
    recipient_name: '',
    scheduled_date: '',
    scheduled_time: '09:00'
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (queueItem) {
      setForm({
        subject: queueItem.subject || '',
        body: queueItem.body || '',
        recipient_email: queueItem.recipient_email || '',
        recipient_name: queueItem.recipient_name || '',
        scheduled_date: queueItem.scheduled_date || '',
        scheduled_time: queueItem.scheduled_time || '09:00'
      });
    }
  }, [queueItem]);

  const handleSave = async () => {
    if (!form.subject.trim() || !form.body.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    setIsSaving(true);
    try {
      await onSave({ ...queueItem, ...form });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !queueItem) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Edit className="text-cyan-400" size={24} />
              Edit Email
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {queueItem.company?.name || 'Unknown Company'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Recipient Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Recipient Name</label>
              <input
                type="text"
                value={form.recipient_name}
                onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Recipient Email *</label>
              <input
                type="email"
                value={form.recipient_email}
                onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                placeholder="john@company.com"
              />
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Subject Line *</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              placeholder="Quick idea for your growth..."
            />
            <p className="text-xs text-gray-500 mt-1">
              {form.subject.length}/50 characters (recommended max)
            </p>
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Email Body *</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none resize-none font-mono text-sm"
              rows={12}
              placeholder="Write your email content here..."
            />
            <p className="text-xs text-gray-500 mt-1">
              {form.body.split(/\s+/).filter(w => w).length} words
            </p>
          </div>

          {/* Schedule */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Scheduled Date</label>
              <input
                type="date"
                value={form.scheduled_date}
                onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Scheduled Time</label>
              <input
                type="time"
                value={form.scheduled_time}
                onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !form.subject.trim() || !form.body.trim()}
            className="px-6 py-2 bg-cyan-500 text-black font-medium rounded-lg hover:bg-cyan-400 disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check size={16} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== MAIN COMPONENT ==============

const UltimateLeadGeneration = () => {
  // Demo mode support
  const { isDemoMode, demoData, triggerViewOnlyWarning } = useDemoMode();
  
  // State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);

  // Nylas Email Connection State
  const [nylasStatus, setNylasStatus] = useState(null);
  const [nylasAccounts, setNylasAccounts] = useState([]);
  const [nylasLoading, setNylasLoading] = useState(true);
  const [emailQueue, setEmailQueue] = useState([]);
  const [emailQueueLoading, setEmailQueueLoading] = useState(false);
  const [sendingEmailId, setSendingEmailId] = useState(null);

  // Filters
  const [industryFilter, setIndustryFilter] = useState('All');
  const [sizeFilter, setSizeFilter] = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyModalMode, setCompanyModalMode] = useState('create');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [showEditQueueModal, setShowEditQueueModal] = useState(false);
  const [selectedQueueItem, setSelectedQueueItem] = useState(null);
  const [showEditLeadModal, setShowEditLeadModal] = useState(false);
  const [showLeadDetailModal, setShowLeadDetailModal] = useState(false);
  const [leadToView, setLeadToView] = useState(null);
  const [showLeadFinderModal, setShowLeadFinderModal] = useState(false);
  
  // Import File Modal State
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importMapping, setImportMapping] = useState({});
  const [importLoading, setImportLoading] = useState(false);
  const [importStep, setImportStep] = useState(1); // 1: upload, 2: map columns, 3: preview
  
  // Category Tabs State for Target List
  const [listCategoryTab, setListCategoryTab] = useState('all');
  // Grid vs Table view toggle for the Target List
  const [listViewMode, setListViewMode] = useState(() => {
    try { return localStorage.getItem('leadgen_list_view') || 'table'; } catch { return 'table'; }
  });
  useEffect(() => {
    try { localStorage.setItem('leadgen_list_view', listViewMode); } catch { /* ignore */ }
  }, [listViewMode]);
  
  // Custom Categories State with Keywords
  const defaultCategories = [
    { id: 'all', label: 'All Leads', color: 'bg-gray-500', isDefault: true, keywords: [] },
    { id: 'technology', label: 'Technology', color: 'bg-blue-500', isDefault: true, keywords: ['tech', 'software', 'saas', 'ai', 'machine learning', 'cloud', 'digital', 'app', 'platform', 'startup'] },
    { id: 'healthcare', label: 'Healthcare', color: 'bg-green-500', isDefault: true, keywords: ['health', 'medical', 'hospital', 'clinic', 'doctor', 'pharma', 'wellness', 'biotech', 'therapy'] },
    { id: 'finance', label: 'Finance', color: 'bg-yellow-500', isDefault: true, keywords: ['bank', 'financial', 'investment', 'insurance', 'capital', 'fund', 'fintech', 'crypto', 'trading'] },
    { id: 'real estate', label: 'Real Estate', color: 'bg-purple-500', isDefault: true, keywords: ['property', 'real estate', 'realty', 'housing', 'mortgage', 'commercial', 'residential', 'broker'] },
    { id: 'retail', label: 'Retail', color: 'bg-pink-500', isDefault: true, keywords: ['store', 'shop', 'ecommerce', 'retail', 'consumer', 'brand', 'fashion', 'product'] },
    { id: 'marketing', label: 'Marketing', color: 'bg-orange-500', isDefault: true, keywords: ['marketing', 'advertising', 'media', 'agency', 'creative', 'branding', 'pr', 'social media'] },
    { id: 'education', label: 'Education', color: 'bg-cyan-500', isDefault: true, keywords: ['school', 'university', 'college', 'learning', 'training', 'academy', 'tutor', 'course'] },
    { id: 'other', label: 'Other', color: 'bg-gray-600', isDefault: true, keywords: [] },
  ];
  
  const [categories, setCategories] = useState(() => {
    const saved = localStorage.getItem('leadGenCategories');
    if (saved) {
      // Ensure saved categories have keywords array
      const parsed = JSON.parse(saved);
      return parsed.map(cat => ({ ...cat, keywords: cat.keywords || [] }));
    }
    return defaultCategories;
  });
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('bg-blue-500');
  const [newCategoryKeywords, setNewCategoryKeywords] = useState('');
  const [isScanningCategory, setIsScanningCategory] = useState(null);
  const [draggedCategoryId, setDraggedCategoryId] = useState(null);
  
  // Save categories to localStorage when changed
  useEffect(() => {
    localStorage.setItem('leadGenCategories', JSON.stringify(categories));
  }, [categories]);
  
  // Listen for localStorage changes from other pages (e.g., Contacts page)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'leadGenCategories' && e.newValue) {
        try {
          const updatedCategories = JSON.parse(e.newValue);
          // Ensure keywords array exists
          const withKeywords = updatedCategories.map(cat => ({ ...cat, keywords: cat.keywords || [] }));
          setCategories(withKeywords);
        } catch (err) {
          console.error('Failed to parse categories from storage:', err);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  // Category management functions
  const handleAddCategory = () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    const keywords = newCategoryKeywords.split(',').map(k => k.trim().toLowerCase()).filter(k => k);
    const newCategory = {
      id: newCategoryName.toLowerCase().trim(),
      label: newCategoryName.trim(),
      color: newCategoryColor,
      isDefault: false,
      keywords: keywords
    };
    // Check for duplicate
    if (categories.some(c => c.id === newCategory.id)) {
      toast.error('Category already exists');
      return;
    }
    setCategories([...categories, newCategory]);
    setNewCategoryName('');
    setNewCategoryColor('bg-blue-500');
    setNewCategoryKeywords('');
    toast.success('Category added');
  };
  
  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategoryName(category.label);
    setNewCategoryColor(category.color);
    setNewCategoryKeywords((category.keywords || []).join(', '));
  };
  
  const handleUpdateCategory = () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    const keywords = newCategoryKeywords.split(',').map(k => k.trim().toLowerCase()).filter(k => k);
    setCategories(categories.map(c => 
      c.id === editingCategory.id 
        ? { ...c, label: newCategoryName.trim(), color: newCategoryColor, keywords: keywords }
        : c
    ));
    setEditingCategory(null);
    setNewCategoryName('');
    setNewCategoryColor('bg-blue-500');
    setNewCategoryKeywords('');
    toast.success('Category updated');
  };
  
  const handleDeleteCategory = (categoryId) => {
    if (categories.find(c => c.id === categoryId)?.isDefault) {
      toast.error('Cannot delete default categories');
      return;
    }
    setCategories(categories.filter(c => c.id !== categoryId));
    if (listCategoryTab === categoryId) {
      setListCategoryTab('all');
    }
    toast.success('Category deleted');
  };
  
  const handleResetCategories = () => {
    setCategories(defaultCategories);
    setListCategoryTab('all');
    localStorage.removeItem('leadGenCategories');
    toast.success('Categories reset to defaults');
  };

  // Drag and drop handlers for category reordering
  const handleCategoryDragStart = (e, categoryId) => {
    setDraggedCategoryId(categoryId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', categoryId);
  };

  const handleCategoryDragOver = (e, targetCategoryId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleCategoryDrop = (e, targetCategoryId) => {
    e.preventDefault();
    if (!draggedCategoryId || draggedCategoryId === targetCategoryId) {
      setDraggedCategoryId(null);
      return;
    }
    
    const draggedIndex = categories.findIndex(c => c.id === draggedCategoryId);
    const targetIndex = categories.findIndex(c => c.id === targetCategoryId);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedCategoryId(null);
      return;
    }
    
    // Reorder categories
    const newCategories = [...categories];
    const [draggedCategory] = newCategories.splice(draggedIndex, 1);
    newCategories.splice(targetIndex, 0, draggedCategory);
    
    setCategories(newCategories);
    setDraggedCategoryId(null);
    toast.success('Category order updated');
  };

  const handleCategoryDragEnd = () => {
    setDraggedCategoryId(null);
  };

  // Move category up/down with buttons
  const moveCategoryUp = (categoryId) => {
    const index = categories.findIndex(c => c.id === categoryId);
    if (index <= 0) return;
    
    const newCategories = [...categories];
    [newCategories[index - 1], newCategories[index]] = [newCategories[index], newCategories[index - 1]];
    setCategories(newCategories);
  };

  const moveCategoryDown = (categoryId) => {
    const index = categories.findIndex(c => c.id === categoryId);
    if (index === -1 || index >= categories.length - 1) return;
    
    const newCategories = [...categories];
    [newCategories[index], newCategories[index + 1]] = [newCategories[index + 1], newCategories[index]];
    setCategories(newCategories);
  };

  // Scan companies and auto-categorize based on keywords
  const handleScanCategoryKeywords = async (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category || !category.keywords || category.keywords.length === 0) {
      toast.error('No keywords set for this category. Add keywords in Manage Categories.');
      return;
    }
    
    setIsScanningCategory(categoryId);
    
    // Find companies that match any keyword
    const matchedCompanies = [];
    const keywords = category.keywords.map(k => k.toLowerCase());
    
    companies.forEach(company => {
      // Search in company name, industry, notes, website, and contacts
      const searchableText = [
        company.name || company.company_name || '',
        company.industry || '',
        company.notes || '',
        company.website || '',
        company.location || '',
        ...(company.contacts || []).map(c => `${c.name || ''} ${c.title || ''} ${c.email || ''}`),
        company.primary_contact?.name || '',
        company.primary_contact?.title || '',
      ].join(' ').toLowerCase();
      
      const matchedKeyword = keywords.find(keyword => searchableText.includes(keyword));
      if (matchedKeyword) {
        matchedCompanies.push({ company, keyword: matchedKeyword });
      }
    });
    
    if (matchedCompanies.length === 0) {
      toast.info(`No companies matched keywords: ${keywords.join(', ')}`);
      setIsScanningCategory(null);
      return;
    }
    
    // Update matched companies' industry to match the category
    let updated = 0;
    for (const { company } of matchedCompanies) {
      // Only update if not already in this category
      if ((company.industry || '').toLowerCase() !== categoryId) {
        try {
          const response = await fetch(`${API_URL}/api/lead-gen/companies/${company.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ industry: category.label })
          });
          if (response.ok) {
            updated++;
          }
        } catch (error) {
          console.error('Failed to update company:', error);
        }
      }
    }
    
    setIsScanningCategory(null);
    
    if (updated > 0) {
      toast.success(`Moved ${updated} companies to ${category.label}!`);
      fetchCompanies(); // Refresh the list
    } else {
      toast.info(`${matchedCompanies.length} companies already in ${category.label} or no updates needed`);
    }
  };
  
  // Folders State
  const defaultFolders = [
    { id: 'all', label: 'All Contacts', color: 'bg-gray-500', icon: 'folder', isDefault: true },
  ];
  
  const [folders, setFolders] = useState(() => {
    const saved = localStorage.getItem('leadGenFolders');
    return saved ? JSON.parse(saved) : defaultFolders;
  });
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('bg-blue-500');
  const [companyFolders, setCompanyFolders] = useState(() => {
    const saved = localStorage.getItem('leadGenCompanyFolders');
    return saved ? JSON.parse(saved) : {};
  });
  const [showAddToFolderModal, setShowAddToFolderModal] = useState(false);
  const [companyToAddToFolder, setCompanyToAddToFolder] = useState(null);
  
  // Save folders and company-folder assignments to localStorage
  useEffect(() => {
    localStorage.setItem('leadGenFolders', JSON.stringify(folders));
  }, [folders]);
  
  useEffect(() => {
    localStorage.setItem('leadGenCompanyFolders', JSON.stringify(companyFolders));
  }, [companyFolders]);
  
  // Folder management functions
  const handleAddFolder = () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }
    const newFolder = {
      id: `folder_${Date.now()}`,
      label: newFolderName.trim(),
      color: newFolderColor,
      icon: 'folder',
      isDefault: false
    };
    if (folders.some(f => f.label.toLowerCase() === newFolder.label.toLowerCase())) {
      toast.error('Folder already exists');
      return;
    }
    setFolders([...folders, newFolder]);
    setNewFolderName('');
    setNewFolderColor('bg-blue-500');
    toast.success('Folder created');
  };
  
  const handleEditFolder = (folder) => {
    setEditingFolder(folder);
    setNewFolderName(folder.label);
    setNewFolderColor(folder.color);
  };
  
  const handleUpdateFolder = () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }
    setFolders(folders.map(f => 
      f.id === editingFolder.id 
        ? { ...f, label: newFolderName.trim(), color: newFolderColor }
        : f
    ));
    setEditingFolder(null);
    setNewFolderName('');
    setNewFolderColor('bg-blue-500');
    toast.success('Folder updated');
  };
  
  const handleDeleteFolder = (folderId) => {
    if (folders.find(f => f.id === folderId)?.isDefault) {
      toast.error('Cannot delete default folder');
      return;
    }
    // Remove folder assignments for this folder
    const updatedAssignments = { ...companyFolders };
    Object.keys(updatedAssignments).forEach(companyId => {
      updatedAssignments[companyId] = updatedAssignments[companyId].filter(id => id !== folderId);
      if (updatedAssignments[companyId].length === 0) {
        delete updatedAssignments[companyId];
      }
    });
    setCompanyFolders(updatedAssignments);
    setFolders(folders.filter(f => f.id !== folderId));
    if (selectedFolder === folderId) {
      setSelectedFolder('all');
    }
    toast.success('Folder deleted');
  };
  
  const handleAddCompanyToFolder = (companyId, folderId) => {
    setCompanyFolders(prev => {
      const current = prev[companyId] || [];
      if (current.includes(folderId)) {
        // Remove from folder
        const updated = current.filter(id => id !== folderId);
        if (updated.length === 0) {
          const { [companyId]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [companyId]: updated };
      } else {
        // Add to folder
        return { ...prev, [companyId]: [...current, folderId] };
      }
    });
  };
  
  const openAddToFolderModal = (company) => {
    setCompanyToAddToFolder(company);
    setShowAddToFolderModal(true);
  };
  
  const getCompanyFolderCount = (folderId) => {
    if (folderId === 'all') return companies.length;
    return Object.entries(companyFolders).filter(([_, folderIds]) => folderIds.includes(folderId)).length;
  };

  // Sorting State for List
  const [sortField, setSortField] = useState('name'); // name, contact_name, email, industry
  const [sortDirection, setSortDirection] = useState('asc'); // asc, desc

  // Email Send Modal State
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [emailToSend, setEmailToSend] = useState(null);
  const [selectedFromAccount, setSelectedFromAccount] = useState('creatrhq');
  const [sendSchedule, setSendSchedule] = useState('now');
  const [scheduledTime, setScheduledTime] = useState('');
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');

  // Bulk Email Modal State
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [bulkEmailCompanies, setBulkEmailCompanies] = useState([]);
  
  // Quick Send to Queue Modal State
  const [showQuickSendModal, setShowQuickSendModal] = useState(false);
  const [quickSendCompany, setQuickSendCompany] = useState(null);
  // TheOrg Lead Finder State
  const [showTheOrgModal, setShowTheOrgModal] = useState(false);
  const [theOrgSearch, setTheOrgSearch] = useState('');
  const [theOrgIndustry, setTheOrgIndustry] = useState('All');
  const [theOrgResults, setTheOrgResults] = useState([]);
  const [theOrgLoading, setTheOrgLoading] = useState(false);
  const [theOrgSelectedCompany, setTheOrgSelectedCompany] = useState(null);
  const [theOrgOrgChart, setTheOrgOrgChart] = useState(null);
  const [theOrgOrgChartLoading, setTheOrgOrgChartLoading] = useState(false);
  const [theOrgCredits, setTheOrgCredits] = useState(null);
  const [importingLead, setImportingLead] = useState(null);
  const [theOrgExecutiveSearch, setTheOrgExecutiveSearch] = useState('');
  const [enrichingContact, setEnrichingContact] = useState(null);
  const [enrichedContacts, setEnrichedContacts] = useState({});

  // RocketReach Modal State
  const [showRocketReachModal, setShowRocketReachModal] = useState(false);

  // Bulk Queue Selection State
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [bulkSendingQueue, setBulkSendingQueue] = useState(false);

  // Fetch data
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/dashboard`);
      const data = await response.json();
      setDashboard(data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      // Get user_id from localStorage - use 'id' field (MongoDB _id)
      const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
      // Use 'id' first (MongoDB ObjectId), fallback to 'user_id', then '_id'
      const userId = authUser?.id || authUser?.user_id || authUser?._id;
      
      const params = new URLSearchParams();
      if (userId) params.append('user_id', userId);
      if (industryFilter !== 'All') params.append('industry', industryFilter);
      if (sizeFilter !== 'All') params.append('size', sizeFilter);
      if (priorityFilter !== 'All') params.append('priority', priorityFilter);
      
      const response = await fetch(`${API_URL}/api/lead-gen/companies?${params}`);
      const data = await response.json();
      setCompanies(data.companies || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  }, [industryFilter, sizeFilter, priorityFilter]);

  const fetchPipeline = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/pipeline`);
      const data = await response.json();
      setPipeline(data.pipeline || {});
    } catch (error) {
      console.error('Failed to fetch pipeline:', error);
    }
  }, []);

  // Fetch Nylas email connection status
  const fetchNylasStatus = useCallback(async () => {
    setNylasLoading(true);
    try {
      // Get auth token
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) {
        setNylasStatus({ connected: false });
        setNylasLoading(false);
        return;
      }
      
      // Fetch connected Nylas accounts for this user
      const response = await fetch(`${API_URL}/api/nylas/accounts`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (!response.ok) {
        console.error('Failed to fetch Nylas accounts:', response.status);
        setNylasStatus({ connected: false });
        setNylasLoading(false);
        return;
      }
      
      const data = await response.json();
      const accounts = data.accounts || [];
      setNylasAccounts(accounts);
      
      // Set status based on whether user has any connected accounts
      if (accounts.length > 0) {
        const primaryAccount = accounts[0];
        setNylasStatus({
          connected: true,
          email: primaryAccount.email,
          provider: primaryAccount.provider || 'email',
          account_id: primaryAccount.grant_id
        });
      } else {
        setNylasStatus({ connected: false });
      }
    } catch (error) {
      console.error('Failed to fetch Nylas status:', error);
      setNylasStatus({ connected: false });
    } finally {
      setNylasLoading(false);
    }
  }, []);
  
  // Fetch email queue
  const fetchEmailQueue = useCallback(async () => {
    setEmailQueueLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/email-queue`);
      const data = await response.json();
      setEmailQueue(data.queue || []);
    } catch (error) {
      console.error('Failed to fetch email queue:', error);
    } finally {
      setEmailQueueLoading(false);
    }
  }, []);
  
  // Check for Nylas OAuth redirect, tab params, and queue items from Contacts page
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const nylasConnected = urlParams.get('nylas_connected');
    const connectedEmail = urlParams.get('email');
    const tabParam = urlParams.get('tab');
    
    // Handle tab parameter from Contacts page
    if (tabParam === 'queue') {
      setActiveTab('queue');
      // Clear the URL param
      window.history.replaceState({}, '', window.location.pathname);
      
      // Check for queued items from Contacts page
      const queuedFromContacts = JSON.parse(localStorage.getItem('lead_email_queue') || '[]');
      if (queuedFromContacts.length > 0) {
        // Process the queued items
        const processQueuedItems = async () => {
          for (const item of queuedFromContacts) {
            for (const contact of item.contacts) {
              try {
                await fetch(`${API_URL}/api/lead-gen/email-queue`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    company_id: item.company_id,
                    company_name: item.company_name,
                    recipient_email: contact.email,
                    recipient_name: contact.name,
                    recipient_title: contact.title || '',
                    subject: '',
                    body: '',
                    status: 'draft'
                  })
                });
              } catch (err) {
                console.error('Failed to add contact to queue:', err);
              }
            }
          }
          // Clear the localStorage queue
          localStorage.removeItem('lead_email_queue');
          // Refresh the email queue
          fetchEmailQueue();
          toast.success(`Added ${queuedFromContacts.reduce((sum, item) => sum + item.contacts.length, 0)} contact(s) to email queue`);
        };
        processQueuedItems();
      }
    }
    
    if (nylasConnected === 'true') {
      // Clear the URL params
      window.history.replaceState({}, '', window.location.pathname);
      
      // Show success toast
      toast.success(`Email connected: ${connectedEmail || 'Successfully connected!'}`);
      
      // Refresh Nylas status
      fetchNylasStatus();
      
      // Switch to queue tab to show the connected account
      setActiveTab('queue');
    }
  }, [fetchNylasStatus, fetchEmailQueue]);

  // Initiate Nylas OAuth connection
  const initiateNylasConnect = async () => {
    try {
      const authToken = localStorage.getItem('auth_token');
      const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
      const userId = authUser?.id || authUser?.user_id;
      
      if (!userId) {
        toast.error('Please log in to connect an email account');
        return;
      }
      
      // Get the current frontend URL for redirect
      const redirectHost = window.location.origin;
      
      // Build connect URL with all necessary parameters
      const connectUrl = `${API_URL}/api/nylas/auth/connect?user_id=${userId}&account_name=Lead Gen Email&redirect_to=lead-generation&redirect_host=${encodeURIComponent(redirectHost)}`;
      
      // Redirect to Nylas OAuth
      window.location.href = connectUrl;
    } catch (error) {
      console.error('Failed to initiate Nylas connect:', error);
      toast.error('Failed to start email connection');
    }
  };

  // TheOrg Functions
  const searchTheOrg = async () => {
    if (!theOrgSearch.trim()) {
      toast.error('Please enter a company name to search');
      return;
    }
    
    setTheOrgLoading(true);
    setTheOrgResults([]);
    setTheOrgSelectedCompany(null);
    setTheOrgOrgChart(null);
    
    try {
      const params = new URLSearchParams({
        query: theOrgSearch,
        limit: '20'
      });
      if (theOrgIndustry !== 'All') {
        params.append('industry', theOrgIndustry);
      }
      
      const response = await fetch(`${API_URL}/api/theorg/search/companies?${params}`);
      const data = await response.json();
      
      if (response.ok) {
        setTheOrgResults(data.companies || []);
        if (data.companies?.length === 0) {
          toast.info('No companies found. Try a different search term.');
        }
      } else {
        toast.error(data.detail || 'Search failed');
      }
    } catch (error) {
      console.error('TheOrg search error:', error);
      toast.error('Failed to search TheOrg');
    } finally {
      setTheOrgLoading(false);
    }
  };

  const fetchTheOrgOrgChart = async (domain) => {
    setTheOrgOrgChartLoading(true);
    setTheOrgOrgChart(null);
    
    console.log('[TheOrg] Fetching org chart for domain:', domain);
    
    try {
      const url = `${API_URL}/api/theorg/company/${domain}/chart`;
      console.log('[TheOrg] API URL:', url);
      
      const response = await fetch(url);
      const status = response.status;
      console.log('[TheOrg] Response status:', status);
      
      // Handle non-OK responses first before trying to read body
      if (status === 402) {
        toast.error('TheOrg API credits exhausted. Please add more credits to your account.', {
          duration: 5000
        });
        fetchTheOrgCredits();
        return;
      }
      
      if (status === 404) {
        toast.error('Company org chart not found on TheOrg.com');
        return;
      }
      
      if (status === 401) {
        toast.error('TheOrg API key is invalid');
        return;
      }
      
      if (!response.ok) {
        // Try to get error detail, but don't fail if body already consumed
        try {
          const data = await response.json();
          toast.error(data.detail || `Failed to fetch org chart (${status})`);
        } catch {
          toast.error(`Failed to fetch org chart (${status})`);
        }
        return;
      }
      
      // Only try to parse JSON for successful responses
      const data = await response.json();
      console.log('[TheOrg] Response data:', data);
      
      setTheOrgOrgChart(data);
      toast.success(`Found ${data.total_positions || 0} executives`);
      
    } catch (error) {
      console.error('TheOrg org chart error:', error);
      // Check if it's a response-related error (body already used)
      if (error.message?.includes('Response body') || error.message?.includes('clone')) {
        // The API call likely succeeded but response was consumed by recorder
        // Check if we got a 402 status from the logs
        toast.error('TheOrg API credits may be exhausted. Please check your account.');
        fetchTheOrgCredits();
      } else {
        toast.error('Failed to fetch organization chart - Network error');
      }
    } finally {
      setTheOrgOrgChartLoading(false);
    }
  };

  const fetchTheOrgCredits = async () => {
    try {
      const response = await fetch(`${API_URL}/api/theorg/usage`);
      const data = await response.json();
      setTheOrgCredits(data);
    } catch (error) {
      console.error('Failed to fetch TheOrg credits:', error);
    }
  };

  const importTheOrgLead = async (company, executive = null) => {
    const importKey = executive ? `${company.domain}-${executive.id}` : company.domain;
    setImportingLead(importKey);
    
    try {
      const params = new URLSearchParams({
        company_domain: company.domain || company.name?.toLowerCase().replace(/\s+/g, '') + '.com',
        company_name: company.name || ''
      });
      
      if (executive) {
        params.append('executive_name', executive.full_name || executive.fullName || '');
        params.append('executive_title', executive.title || '');
        params.append('executive_linkedin', executive.linkedin_url || executive.linkedInUrl || '');
        params.append('executive_email', executive.email || executive.workEmail || '');
        params.append('executive_phone', executive.phone || '');
      }
      
      const response = await fetch(`${API_URL}/api/theorg/import-lead?${params}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`Lead imported: ${company.name || company.domain}`);
        fetchCompanies();
        fetchDashboard();
      } else {
        toast.error(data.detail || 'Failed to import lead');
      }
    } catch (error) {
      console.error('Import lead error:', error);
      toast.error('Failed to import lead');
    } finally {
      setImportingLead(null);
    }
  };

  // Enrich contact with detailed info (email, phone) using TheOrg credits
  const enrichTheOrgContact = async (executive, companyDomain) => {
    const execKey = `${companyDomain}-${executive.id}`;
    setEnrichingContact(execKey);
    
    try {
      const personName = executive.full_name || executive.fullName;
      const params = new URLSearchParams({
        person_name: personName
      });
      if (companyDomain) {
        params.append('company_domain', companyDomain);
      }
      
      const response = await fetch(`${API_URL}/api/theorg/contact/enrich?${params}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (response.ok && data.found) {
        // Store enriched contact data
        setEnrichedContacts(prev => ({
          ...prev,
          [execKey]: data.contact
        }));
        
        const email = data.contact.email;
        const phone = data.contact.phone;
        
        if (email || phone) {
          toast.success(`Contact info found! ${email ? `Email: ${email}` : ''} ${phone ? `Phone: ${phone}` : ''}`, {
            duration: 5000
          });
        } else {
          toast.info('Contact found but no email/phone available');
        }
        
        // Refresh credits display
        fetchTheOrgCredits();
      } else if (response.status === 402) {
        toast.error('Insufficient TheOrg credits. Please add more credits to your account.');
      } else if (!data.found) {
        toast.info('No contact details found for this person');
      } else {
        toast.error(data.detail || 'Failed to get contact details');
      }
    } catch (error) {
      console.error('Enrich contact error:', error);
      toast.error('Failed to get contact details');
    } finally {
      setEnrichingContact(null);
    }
  };

  const exportLeadsToCSV = () => {
    // Respect the current view: folder filter + category filter + search + sort.
    // `sortedCompanies` is computed below in the render path and is what the
    // user actually sees in the table, so exporting it matches expectations.
    const rowsSource = sortedCompanies;
    if (!rowsSource || rowsSource.length === 0) {
      toast.error('No leads in the current view to export');
      return;
    }

    // Build a folder-id → folder-name lookup once so we can stamp every row
    // with the folder labels it belongs to.
    const folderNameById = {};
    (folders || []).forEach(f => { if (f && f.id) folderNameById[f.id] = f.name || f.id; });
    const folderLabelFor = (companyId) => {
      const ids = companyFolders?.[companyId] || [];
      return ids.map(id => folderNameById[id] || id).join('; ');
    };

    // Strict RFC-4180-ish CSV escaping — quote every cell, double-up internal
    // quotes, and prefix dangerous leading chars to defeat Excel formula
    // injection (a real risk if any cell starts with =, +, -, or @).
    const esc = (val) => {
      if (val === undefined || val === null) return '""';
      let s = typeof val === 'string' ? val : String(val);
      if (/^[=+\-@]/.test(s)) s = `'${s}`;
      return `"${s.replace(/"/g, '""')}"`;
    };

    const headers = [
      'Company', 'Website', 'Industry', 'Location', 'Size',
      'Contact Name', 'Contact Title', 'Contact Email', 'Contact Phone',
      'Stage', 'Priority', 'Deal Value', 'Source',
      'Tags', 'Folders', 'Notes',
      'Created At', 'Updated At',
    ];

    const rows = rowsSource.map(c => {
      const contact = c.primary_contact || (c.contacts && c.contacts[0]) || {};
      const contactName = contact.name
        || [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim();
      return [
        c.name || c.company_name || '',
        c.website || c.domain || '',
        c.industry || '',
        c.location || '',
        c.size || c.company_size || '',
        contactName,
        contact.title || '',
        contact.email || '',
        contact.phone || '',
        c.stage || '',
        c.priority || '',
        c.deal_value || 0,
        c.source || c.lead_source || '',
        Array.isArray(c.tags) ? c.tags.join('; ') : (c.tags || ''),
        folderLabelFor(c.id),
        (c.notes || '').replace(/\r?\n/g, ' '),
        c.created_at || '',
        c.updated_at || '',
      ];
    });

    // Prepend BOM so Excel auto-detects UTF-8 (otherwise emoji + accents break).
    const csvContent = '\uFEFF' + [headers, ...rows]
      .map(r => r.map(esc).join(','))
      .join('\r\n');

    // Friendly filename — reflects exactly what the user exported.
    const folderName = selectedFolder !== 'all'
      ? (folderNameById[selectedFolder] || 'folder')
      : null;
    const catSegment = listCategoryTab && listCategoryTab !== 'all' ? listCategoryTab : null;
    const datestamp = new Date().toISOString().split('T')[0];
    const fileSlug = [
      'leads',
      folderName,
      catSegment,
      datestamp,
    ].filter(Boolean).join('_').replace(/[^a-z0-9_-]/gi, '_');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileSlug}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    const scopeLabel = folderName ? `folder "${folderName}"` : 'all leads';
    toast.success(`Exported ${rows.length} ${rows.length === 1 ? 'lead' : 'leads'} from ${scopeLabel}`);
  };

  useEffect(() => {
    const loadData = async () => {
      // Use demo data if in demo mode
      if (isDemoMode && demoData?.leads) {
        const demoLeads = demoData.leads.map((lead, idx) => {
          // For real estate, use contact name as company name if no company field exists
          const companyName = lead.company || lead.property_interest || lead.name || 'Demo Lead';
          return {
            id: lead.id || `demo-lead-${idx}`,
            name: companyName,
            company_name: companyName,
            company: { name: companyName, id: lead.id || `demo-company-${idx}` },
            industry: lead.industry || 'Real Estate',
            size: lead.size || '1-10',
            company_size: lead.size || '1-10',
            website: lead.website || '',
            location: lead.location || '',
            revenue: lead.budget || lead.revenue || 'N/A',
            priority: lead.status === 'Hot Lead' ? 'A' : (lead.status === 'Warm Lead' ? 'B' : 'C'),
            stage: lead.stage || (lead.status === 'Hot Lead' ? 'qualified' : 'new'),
            pipeline_stage: lead.stage || (lead.status === 'Hot Lead' ? 'qualified' : 'new'),
            created_at: lead.created_at || new Date().toISOString(),
            contacts: [{
              id: `demo-contact-${idx}`,
              name: lead.name || 'Contact',
              email: lead.email || '',
              phone: lead.phone || '',
              title: lead.property_interest || ''
            }],
            primary_contact: {
              id: `demo-contact-${idx}`,
              name: lead.name || 'Contact',
              email: lead.email || '',
              phone: lead.phone || '',
              title: lead.property_interest || ''
            },
            estimated_value: lead.value || (lead.budget ? parseInt(lead.budget.replace(/[^0-9]/g, '').slice(0, 6)) : 0) || 50000,
            activities: [],
            last_contact: lead.last_contact,
            notes: lead.notes || ''
          };
        });
        
        setCompanies(demoLeads);
        
        // Build pipeline from demo leads
        const demoPipeline = {};
        demoLeads.forEach(lead => {
          const stage = lead.stage || 'new';
          if (!demoPipeline[stage]) demoPipeline[stage] = [];
          demoPipeline[stage].push(lead);
        });
        setPipeline(demoPipeline);
        
        // Demo dashboard stats - complete object
        const totalValue = demoLeads.reduce((sum, l) => sum + (l.estimated_value || 0), 0);
        setDashboard({
          total_companies: demoLeads.length,
          total_contacts: demoLeads.filter(l => l.primary_contact).length,
          emails_sent_today: 5,
          emails_sent_week: 23,
          response_rate: 32,
          pipeline_value: totalValue,
          weekly_mission: {
            new_leads_this_week: demoLeads.length,
            leads_needed: Math.max(0, 10 - demoLeads.length),
            emails_scheduled: 12,
            replies: 4,
            calls_booked: 2,
            pipeline_value: totalValue,
            won_value: 15000
          },
          todays_actions: {
            followups_due: 2,
            followup_leads: demoLeads.slice(0, 2),
            leads_to_research: 3,
            research_leads: demoLeads.slice(2, 5),
            emails_to_send: 4,
            email_leads: demoLeads.slice(0, 4)
          },
          metrics: {
            total_leads: demoLeads.length,
            contacted: Math.floor(demoLeads.length * 0.6),
            replied: Math.floor(demoLeads.length * 0.3),
            reply_rate: 30,
            conversion_rate: 15
          }
        });
        
        setLoading(false);
        console.log(`🎯 Demo mode: Loaded ${demoLeads.length} demo leads`);
        return;
      }
      
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchCompanies(), fetchPipeline(), fetchNylasStatus(), fetchEmailQueue()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchCompanies, fetchPipeline, fetchNylasStatus, fetchEmailQueue, isDemoMode, demoData]);

  // Handlers
  const handleSaveCompany = async (formData) => {
    try {
      // Separate contact data from company data
      const { contact_first_name, contact_last_name, contact_email, contact_phone, contact_title, ...companyData } = formData;
      
      // Combine first and last name
      const contact_name = `${contact_first_name || ''} ${contact_last_name || ''}`.trim();
      
      // Get user_id for ownership
      const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
      const userId = authUser?.id || authUser?.user_id;
      
      // Add user_id to company data when creating
      if (companyModalMode === 'create' && userId) {
        companyData.user_id = userId;
      }
      
      const url = companyModalMode === 'create'
        ? `${API_URL}/api/lead-gen/companies`
        : `${API_URL}/api/lead-gen/companies/${selectedCompany.id}`;
      
      const response = await fetch(url, {
        method: companyModalMode === 'create' ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(companyData)
      });
      
      const data = await response.json();
      if (response.ok) {
        const companyId = data.company?.id || data.id || selectedCompany?.id;
        
        // If contact info provided, create/update contact
        if (contact_name || contact_email) {
          try {
            // Check if updating existing contact or creating new
            const existingContact = selectedCompany?.primary_contact || selectedCompany?.contacts?.[0];
            
            if (existingContact?.id && companyModalMode === 'edit') {
              // Update existing contact
              await fetch(`${API_URL}/api/lead-gen/contacts/${existingContact.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  name: contact_name,
                  first_name: contact_first_name,
                  last_name: contact_last_name,
                  email: contact_email,
                  phone: contact_phone,
                  title: contact_title
                })
              });
            } else if (companyId) {
              // Create new contact
              await fetch(`${API_URL}/api/lead-gen/contacts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  company_id: companyId,
                  name: contact_name,
                  first_name: contact_first_name,
                  last_name: contact_last_name,
                  email: contact_email,
                  phone: contact_phone,
                  title: contact_title
                })
              });
            }
          } catch (contactError) {
            console.error('Failed to save contact:', contactError);
            // Don't fail the whole operation if contact save fails
          }
        }
        
        // Auto-fetch logo for new companies (if no logo already set)
        if (companyModalMode === 'create' && companyId && !companyData.logo_url) {
          try {
            // Get domain from website or company name
            let domain = companyData.website || companyData.name;
            if (domain) {
              // Clean up domain
              domain = domain.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/[^a-z0-9.-]/g, '');
              if (!domain.includes('.')) {
                domain = domain.replace(/[^a-z0-9]/g, '') + '.com';
              }
              
              const logoResponse = await fetch(`${API_URL}/api/lead-gen/fetch-logo-by-domain?domain=${encodeURIComponent(domain)}`);
              const logoData = await logoResponse.json();
              
              if (logoData.success && logoData.logo_url) {
                // Update the company with the fetched logo
                await fetch(`${API_URL}/api/lead-gen/companies/${companyId}`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ logo_url: logoData.logo_url })
                });
              }
            }
          } catch (logoError) {
            console.error('Failed to auto-fetch logo:', logoError);
            // Don't fail if logo fetch fails
          }
        }
        
        toast.success(companyModalMode === 'create' ? 'Company added!' : 'Company updated!');
        fetchCompanies();
        fetchDashboard();
      } else {
        toast.error(data.detail || 'Failed to save company');
      }
    } catch (error) {
      toast.error('Failed to save company');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Delete this company and all related data?')) return;
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/companies/${companyId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Company deleted');
        fetchCompanies();
        fetchPipeline();
        fetchDashboard();
        setShowCompanyModal(false);
      }
    } catch (error) {
      toast.error('Failed to delete company');
    }
  };

  const handleBulkDeleteCompanies = async (companyIds) => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/companies/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_ids: companyIds })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Deleted ${data.deleted_count} companies`);
        fetchCompanies();
        fetchPipeline();
        fetchDashboard();
      } else {
        toast.error(data.detail || 'Failed to delete companies');
      }
    } catch (error) {
      toast.error('Failed to delete companies');
    }
  };

  const handleEnrichCompany = async (company) => {
    if (!company.website) {
      toast.error('Company has no website to enrich');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/companies/${company.id}/enrich`, {
        method: 'POST'
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Company enriched!');
        fetchCompanies();
      } else {
        toast.error(data.detail || 'Failed to enrich');
      }
    } catch (error) {
      toast.error('Failed to enrich company');
    }
  };

  const handleCreateLead = async (company) => {
    const companyName = company.name || company.company_name || 'Company';
    try {
      // Include the primary contact if available
      const leadData = {
        company_id: company.id,
        stage: 'new',
        deal_value: 0
      };
      
      // If company has a primary contact, link it to the lead
      if (company.primary_contact?.id) {
        leadData.contact_id = company.primary_contact.id;
      }
      
      const response = await fetch(`${API_URL}/api/lead-gen/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadData)
      });
      if (response.ok) {
        toast.success(`${companyName} added to pipeline!`);
        fetchPipeline();
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to create lead');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const { draggableId, source, destination } = result;
    
    if (source.droppableId === destination.droppableId) return;
    
    // Optimistic update
    const newPipeline = { ...pipeline };
    const sourceLead = newPipeline[source.droppableId].find(l => l.id === draggableId);
    newPipeline[source.droppableId] = newPipeline[source.droppableId].filter(l => l.id !== draggableId);
    sourceLead.stage = destination.droppableId;
    newPipeline[destination.droppableId] = [...(newPipeline[destination.droppableId] || []), sourceLead];
    setPipeline(newPipeline);
    
    // API call
    try {
      await fetch(`${API_URL}/api/lead-gen/leads/${draggableId}/stage?new_stage=${destination.droppableId}`, {
        method: 'PATCH'
      });
      toast.success(`Lead moved to ${PIPELINE_STAGES.find(s => s.id === destination.droppableId)?.label}`);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to move lead');
      fetchPipeline();
    }
  };

  const handleGenerateEmail = (lead) => {
    setSelectedLead(lead);
    setShowEmailModal(true);
  };

  // View lead details handler
  const handleViewLeadDetails = (lead) => {
    setLeadToView(lead);
    setShowLeadDetailModal(true);
  };

  // Edit lead handler
  const handleEditLead = (lead) => {
    setSelectedLead(lead);
    setShowEditLeadModal(true);
  };

  // Update lead handler
  const handleUpdateLead = async (updatedLead) => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/leads/${updatedLead.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deal_value: updatedLead.deal_value,
          notes: updatedLead.notes,
          tags: updatedLead.tags,
          next_followup_date: updatedLead.next_followup_date
        })
      });
      if (response.ok) {
        toast.success('Lead updated!');
        fetchPipeline();
        fetchDashboard();
        setShowEditLeadModal(false);
      } else {
        toast.error('Failed to update lead');
      }
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  // Delete lead handler
  const handleDeleteLead = async (lead) => {
    if (!window.confirm(`Delete lead for ${lead.company?.name || 'Unknown Company'}?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/leads/${lead.id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Lead deleted');
        fetchPipeline();
        fetchDashboard();
      } else {
        toast.error('Failed to delete lead');
      }
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleApproveEmail = async (emailData) => {
    if (!selectedLead) return;
    
    // Get recipient email from contact or company
    const recipientEmail = selectedLead.contact?.email || '';
    
    if (!recipientEmail) {
      toast.error('No email address found for this lead. Please add a contact with email.');
      return;
    }
    
    try {
      // Add to email queue
      const response = await fetch(`${API_URL}/api/lead-gen/email-queue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: selectedLead.id,
          subject: emailData.subject,
          body: emailData.body,
          scheduled_date: new Date().toISOString().split('T')[0],
          scheduled_time: '09:00',
          recipient_email: recipientEmail,
          recipient_name: selectedLead.contact?.name || selectedLead.company?.name
        })
      });
      
      if (response.ok) {
        toast.success('Email added to queue!');
        fetchEmailQueue();
        fetchDashboard();
      } else {
        toast.error('Failed to queue email');
      }
    } catch (error) {
      toast.error('Failed to queue email');
    }
  };

  // Open quick send to queue modal for single company
  const handleOpenQuickSendModal = (company) => {
    setQuickSendCompany(company);
    setShowQuickSendModal(true);
  };

  // Open bulk email modal for multiple companies
  const handleOpenBulkEmailModal = (companies) => {
    setBulkEmailCompanies(companies);
    setShowBulkEmailModal(true);
  };

  // Handler for importing a contact from RocketReach
  const handleRocketReachImportContact = async (contactData) => {
    try {
      // Get user_id for ownership
      const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
      const userId = authUser?.id || authUser?.user_id;
      
      // If company name is provided, try to find or create it
      let companyId = null;
      
      if (contactData.company) {
        // Search for existing company
        const existingCompany = companies.find(
          c => (c.name || c.company_name || '').toLowerCase() === contactData.company.toLowerCase()
        );
        
        if (existingCompany) {
          companyId = existingCompany.id;
        } else {
          // Create new company with user_id
          const companyResponse = await fetch(`${API_URL}/api/lead-gen/companies`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: contactData.company,
              location: contactData.location || '',
              source: 'RocketReach',
              user_id: userId
            })
          });
          const companyData = await companyResponse.json();
          if (companyResponse.ok) {
            companyId = companyData.company?.id || companyData.id;
            // Try to fetch logo
            try {
              const domain = contactData.company.toLowerCase().replace(/[^a-z0-9]/g, '') + '.com';
              await fetch(`${API_URL}/api/lead-gen/fetch-logo-by-domain?domain=${encodeURIComponent(domain)}`);
            } catch (e) { /* ignore logo errors */ }
          }
        }
      }
      
      // Create contact if we have a company
      if (companyId && (contactData.name || contactData.email)) {
        const contactResponse = await fetch(`${API_URL}/api/lead-gen/contacts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            company_id: companyId,
            name: contactData.name || '',
            email: contactData.email || '',
            phone: contactData.phone || '',
            title: contactData.title || '',
            linkedin_url: contactData.linkedin_url || '',
            source: 'RocketReach'
          })
        });
        
        if (contactResponse.ok) {
          fetchCompanies();
          fetchDashboard();
        }
      }
    } catch (error) {
      console.error('Failed to import contact from RocketReach:', error);
    }
  };

  // Handler for importing a company from RocketReach
  const handleRocketReachImportCompany = async (companyData) => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/companies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: companyData.name,
          website: companyData.website || '',
          industry: companyData.industry || '',
          location: companyData.location || '',
          size: companyData.size || '',
          notes: companyData.notes || '',
          source: 'RocketReach'
        })
      });
      
      const data = await response.json();
      if (response.ok) {
        const newCompanyId = data.company?.id || data.id;
        // Try to fetch logo
        if (newCompanyId && companyData.website) {
          try {
            const domain = companyData.website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
            const logoResponse = await fetch(`${API_URL}/api/lead-gen/fetch-logo-by-domain?domain=${encodeURIComponent(domain)}`);
            const logoData = await logoResponse.json();
            if (logoData.success && logoData.logo_url) {
              await fetch(`${API_URL}/api/lead-gen/companies/${newCompanyId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ logo_url: logoData.logo_url })
              });
            }
          } catch (e) { /* ignore logo errors */ }
        }
        fetchCompanies();
        fetchDashboard();
      } else {
        toast.error(data.detail || 'Failed to import company');
      }
    } catch (error) {
      console.error('Failed to import company from RocketReach:', error);
      toast.error('Failed to import company');
    }
  };

  // Handle bulk send to email queue
  const handleBulkSendToQueue = async (queueItems) => {
    if (!queueItems || queueItems.length === 0) return;
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const item of queueItems) {
      try {
        const response = await fetch(`${API_URL}/api/lead-gen/email-queue`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            company_id: item.company_id,
            subject: item.subject,
            body: item.body,
            scheduled_date: item.scheduled_date,
            scheduled_time: item.scheduled_time,
            recipient_email: item.recipient_email,
            recipient_name: item.recipient_name,
            from_email: item.from_email
          })
        });
        
        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`Added ${successCount} email${successCount > 1 ? 's' : ''} to queue!`);
      fetchEmailQueue();
      fetchDashboard();
      // Switch to queue tab to show results
      setActiveTab('queue');
    }
    if (errorCount > 0) {
      toast.error(`Failed to add ${errorCount} email${errorCount > 1 ? 's' : ''}`);
    }
  };

  // Bulk move companies to folder
  const handleBulkMoveToFolder = (companyIds, folderId) => {
    setCompanyFolders(prev => {
      const updated = { ...prev };
      companyIds.forEach(companyId => {
        const current = updated[companyId] || [];
        if (!current.includes(folderId)) {
          updated[companyId] = [...current, folderId];
        }
      });
      return updated;
    });
  };

  // Bulk move companies to category (update company industry)
  const handleBulkMoveToCategory = async (companyIds, categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;
    
    // Update each company's industry
    for (const companyId of companyIds) {
      try {
        await fetch(`${API_URL}/api/lead-gen/companies/${companyId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ industry: category.label })
        });
      } catch (error) {
        console.error('Failed to update company category:', error);
      }
    }
    
    // Refresh companies list
    fetchCompanies();
  };

  // Send email via Nylas
  // Open email send modal
  const openSendEmailModal = (queueItem) => {
    setEmailToSend(queueItem);
    setEditedSubject(queueItem.subject || '');
    setEditedBody(queueItem.body || '');
    // Use first connected Nylas account as default
    setSelectedFromAccount(nylasAccounts[0]?.grant_id || '');
    setSendSchedule('now');
    setScheduledTime('');
    setShowSendEmailModal(true);
  };

  // Send email immediately without opening modal (for Send Now button on queue)
  const handleSendNowDirect = async (queueItem) => {
    if (!queueItem) return;
    
    const selectedAccount = nylasAccounts[0]; // Use first connected account
    if (!selectedAccount) {
      toast.error('Please connect an email account first');
      return;
    }
    
    setSendingEmailId(queueItem.id);
    try {
      // Get user ID for Nylas
      const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
      const userId = authUser?.id || authUser?.user_id || 'default';
      const token = localStorage.getItem('auth_token');
      
      // Send immediately via Nylas
      const response = await fetch(`${API_URL}/api/nylas/send?user_id=${userId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          to: [queueItem.recipient_email],
          subject: queueItem.subject,
          body: queueItem.body
        })
      });
      
      const data = await response.json();
      
      // Check for success - Nylas returns {status: "sent"} on success
      if (response.ok && (data.success || data.status === 'sent')) {
        toast.success('Email sent successfully!');
        
        // Remove from queue
        await fetch(`${API_URL}/api/lead-gen/email-queue/${queueItem.id}`, {
          method: 'DELETE'
        });
        
        // Update lead stage to "contacted" if it was "new"
        if (queueItem.lead?.stage === 'new') {
          await fetch(`${API_URL}/api/lead-gen/leads/${queueItem.lead_id}/stage?new_stage=contacted`, {
            method: 'PATCH'
          });
        }
        
        fetchEmailQueue();
        fetchPipeline();
        fetchDashboard();
      } else {
        toast.error(data.detail || data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setSendingEmailId(null);
    }
  };

  // Bulk send all selected queue items
  const handleBulkSendQueue = async () => {
    if (selectedQueueIds.length === 0) {
      toast.error('Please select emails to send');
      return;
    }
    
    const selectedAccount = nylasAccounts[0];
    if (!selectedAccount) {
      toast.error('Please connect an email account first');
      return;
    }
    
    setBulkSendingQueue(true);
    const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
    const userId = authUser?.id || authUser?.user_id || 'default';
    const token = localStorage.getItem('auth_token');
    
    let successCount = 0;
    let failCount = 0;
    
    for (const queueId of selectedQueueIds) {
      const queueItem = emailQueue.find(q => q.id === queueId);
      if (!queueItem) continue;
      
      try {
        const response = await fetch(`${API_URL}/api/nylas/send?user_id=${userId}`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            to: [queueItem.recipient_email],
            subject: queueItem.subject,
            body: queueItem.body
          })
        });
        
        const data = await response.json();
        
        if (response.ok && (data.success || data.status === 'sent')) {
          // Remove from queue
          await fetch(`${API_URL}/api/lead-gen/email-queue/${queueItem.id}`, {
            method: 'DELETE'
          });
          
          // Update lead stage
          if (queueItem.lead?.stage === 'new') {
            await fetch(`${API_URL}/api/lead-gen/leads/${queueItem.lead_id}/stage?new_stage=contacted`, {
              method: 'PATCH'
            });
          }
          
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        console.error('Error sending email:', error);
        failCount++;
      }
      
      // Small delay between sends to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setBulkSendingQueue(false);
    setSelectedQueueIds([]);
    
    if (successCount > 0) {
      toast.success(`Successfully sent ${successCount} email${successCount > 1 ? 's' : ''}${failCount > 0 ? ` (${failCount} failed)` : ''}`);
    } else {
      toast.error(`Failed to send ${failCount} email${failCount > 1 ? 's' : ''}`);
    }
    
    fetchEmailQueue();
    fetchPipeline();
    fetchDashboard();
  };

  // Toggle queue item selection
  const toggleQueueSelection = (queueId) => {
    setSelectedQueueIds(prev => 
      prev.includes(queueId) 
        ? prev.filter(id => id !== queueId)
        : [...prev, queueId]
    );
  };

  // Select/deselect all queue items
  const toggleSelectAllQueue = () => {
    if (selectedQueueIds.length === emailQueue.length) {
      setSelectedQueueIds([]);
    } else {
      setSelectedQueueIds(emailQueue.map(q => q.id));
    }
  };

  const handleSendEmail = async () => {
    if (!emailToSend) return;
    
    const selectedAccount = nylasAccounts.find(a => a.grant_id === selectedFromAccount);
    if (!selectedAccount) {
      toast.error('Please connect an email account first');
      return;
    }
    
    setSendingEmailId(emailToSend.id);
    try {
      // If scheduled for later, update the queue item
      if (sendSchedule === 'later' && scheduledTime) {
        const scheduleResponse = await fetch(`${API_URL}/api/lead-gen/email-queue/${emailToSend.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            scheduled_time: scheduledTime,
            subject: editedSubject,
            body: editedBody,
            from_account: selectedFromAccount
          })
        });
        
        if (scheduleResponse.ok) {
          toast.success(`Email scheduled for ${new Date(scheduledTime).toLocaleString()}`);
          setShowSendEmailModal(false);
          fetchEmailQueue();
          return;
        }
      }
      
      // Get user ID for Nylas
      const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
      const userId = authUser?.id || authUser?.user_id || 'default';
      const token = localStorage.getItem('auth_token');
      
      // Send immediately via Nylas
      const response = await fetch(`${API_URL}/api/nylas/send?user_id=${userId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          to: [emailToSend.recipient_email],
          subject: editedSubject,
          body: editedBody
        })
      });
      
      const data = await response.json();
      
      // Check for success - Nylas returns {status: "sent"} on success
      if (response.ok && (data.success || data.status === 'sent')) {
        toast.success('Email sent successfully!');
        
        // Remove from queue
        await fetch(`${API_URL}/api/lead-gen/email-queue/${emailToSend.id}`, {
          method: 'DELETE'
        });
        
        // Update lead stage to "contacted" if it was "new"
        if (emailToSend.lead?.stage === 'new') {
          await fetch(`${API_URL}/api/lead-gen/leads/${emailToSend.lead_id}/stage?new_stage=contacted`, {
            method: 'PATCH'
          });
        }
        
        setShowSendEmailModal(false);
        fetchEmailQueue();
        fetchPipeline();
        fetchDashboard();
      } else {
        toast.error(data.detail || data.message || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendingEmailId(null);
    }
  };

  // Remove email from queue
  const handleRemoveFromQueue = async (queueId) => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/email-queue/${queueId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Removed from queue');
        fetchEmailQueue();
      }
    } catch (error) {
      toast.error('Failed to remove from queue');
    }
  };

  // Update email in queue
  const handleUpdateQueueItem = async (updatedItem) => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/email-queue/${updatedItem.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedItem)
      });
      if (response.ok) {
        toast.success('Email updated!');
        fetchEmailQueue();
        setShowEditQueueModal(false);
      } else {
        toast.error('Failed to update email');
      }
    } catch (error) {
      toast.error('Failed to update email');
    }
  };

  // Open edit modal for queue item
  const handleEditQueueItem = (item) => {
    setSelectedQueueItem(item);
    setShowEditQueueModal(true);
  };

  const handleImportCSV = async (data) => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/import-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data })
      });
      const result = await response.json();
      if (response.ok) {
        toast.success(`Imported ${result.imported} companies!`);
        fetchCompanies();
        fetchDashboard();
      } else {
        toast.error(result.detail || 'Import failed');
      }
    } catch (error) {
      toast.error('Import failed');
    }
  };

  // Filter companies by search, category, and folder
  const filteredCompanies = companies.filter(c => {
    // Folder filter
    if (selectedFolder !== 'all') {
      const companyFolderIds = companyFolders[c.id] || [];
      if (!companyFolderIds.includes(selectedFolder)) return false;
    }
    
    // Category filter
    if (listCategoryTab !== 'all') {
      const industry = (c.industry || '').toLowerCase();
      if (listCategoryTab === 'other') {
        // "Other" category matches industries not in our category list
        const categoryIds = categories.filter(ct => ct.id !== 'all' && ct.id !== 'other').map(ct => ct.id);
        if (categoryIds.includes(industry)) return false;
      } else if (listCategoryTab !== industry) {
        return false;
      }
    }
    
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const name = (c.name || c.company_name || '').toLowerCase();
    const website = (c.website || '').toLowerCase();
    const domain = (c.domain || '').toLowerCase();
    const contactName = (c.primary_contact?.name || '').toLowerCase();
    const email = (c.primary_contact?.email || '').toLowerCase();
    return name.includes(query) || website.includes(query) || domain.includes(query) || contactName.includes(query) || email.includes(query);
  });

  // Sort filtered companies
  const sortedCompanies = [...filteredCompanies].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortField) {
      case 'name':
        aVal = (a.name || a.company_name || '').toLowerCase();
        bVal = (b.name || b.company_name || '').toLowerCase();
        break;
      case 'contact_name':
        aVal = (a.primary_contact?.name || '').toLowerCase();
        bVal = (b.primary_contact?.name || '').toLowerCase();
        break;
      case 'email':
        aVal = (a.primary_contact?.email || '').toLowerCase();
        bVal = (b.primary_contact?.email || '').toLowerCase();
        break;
      case 'role':
        aVal = (a.primary_contact?.title || '').toLowerCase();
        bVal = (b.primary_contact?.title || '').toLowerCase();
        break;
      case 'phone':
        aVal = (a.primary_contact?.phone || '').toLowerCase();
        bVal = (b.primary_contact?.phone || '').toLowerCase();
        break;
      case 'industry':
        aVal = (a.industry || '').toLowerCase();
        bVal = (b.industry || '').toLowerCase();
        break;
      case 'created_at':
        aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
        bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
        break;
      default:
        aVal = (a.name || '').toLowerCase();
        bVal = (b.name || '').toLowerCase();
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Toggle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Parse CSV file
  const parseCSVFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length < 2) {
            reject(new Error('CSV file must have headers and at least one data row'));
            return;
          }
          
          // Helper function to parse a CSV line properly handling quoted fields
          const parseCSVLine = (line) => {
            const values = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                values.push(current.trim().replace(/^"|"$/g, ''));
                current = '';
              } else {
                current += char;
              }
            }
            values.push(current.trim().replace(/^"|"$/g, ''));
            return values;
          };
          
          // Parse headers using the same quote-aware parser
          const headers = parseCSVLine(lines[0]);
          
          // Parse data rows
          const rows = [];
          for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i]);
            
            if (values.some(v => v)) {
              const row = {};
              headers.forEach((h, idx) => {
                row[h] = values[idx] || '';
              });
              rows.push(row);
            }
          }
          
          resolve({ headers, rows });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  // Handle file import
  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImportFile(file);
    setImportLoading(true);
    
    try {
      if (file.name.endsWith('.csv')) {
        const { headers, rows } = await parseCSVFile(file);
        
        // Auto-map common column names
        const autoMapping = {};
        const columnMappings = {
          'company': ['company', 'company name', 'company_name', 'business', 'organization', 'org', 'companyname', 'brand', 'brand name'],
          'first_name': ['first name', 'first_name', 'firstname', 'given name', 'contact first name'],
          'last_name': ['last name', 'last_name', 'lastname', 'surname', 'family name', 'contact last name'],
          'contact_name': ['contact', 'contact name', 'contact_name', 'name', 'full name', 'fullname', 'person', 'contactname'],
          'email': ['email', 'email address', 'e-mail', 'mail', 'contact email', 'contactemail', 'work email'],
          'phone': ['phone', 'phone number', 'telephone', 'tel', 'mobile', 'cell', 'contact number', 'contactnumber'],
          'role': ['role', 'title', 'job title', 'position', 'job_title', 'jobtitle', 'job role'],
          'website': ['website', 'url', 'web', 'site', 'domain'],
          'industry': ['industry', 'sector', 'category', 'type', 'primary category', 'primary category/niche', 'niche'],
          'linkedin': ['linkedin', 'linkedin url', 'linkedin_url', 'li_url'],
          'notes': ['notes', 'note', 'comments', 'comment', 'description', 'value proposition', 'value proposition to sponsor'],
          'location': ['location', 'city', 'address', 'region', 'state', 'country']
        };
        
        headers.forEach(header => {
          const headerLower = header.toLowerCase().trim();
          for (const [field, aliases] of Object.entries(columnMappings)) {
            if (aliases.includes(headerLower)) {
              autoMapping[header] = field;
              break;
            }
          }
        });
        
        setImportMapping(autoMapping);
        setImportPreview(rows.slice(0, 5));
        setImportStep(2);
      } else if (file.name.endsWith('.pdf') || file.type === 'application/pdf') {
        // Parse PDF contact list using specialized endpoint
        toast.info('Extracting contacts from PDF... This may take a moment.');
        
        const reader = new FileReader();
        reader.onload = async (readerEvent) => {
          try {
            const base64 = readerEvent.target.result.split(',')[1];
            
            // Use the specialized PDF contact list endpoint
            const response = await fetch(`${API_URL}/api/lead-gen/parse-pdf-contacts`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                file_base64: base64
              })
            });
            
            const data = await response.json();
            
            if (response.ok && data.success && data.contacts && data.contacts.length > 0) {
              // Convert extracted contacts to row format for preview
              const extractedRows = data.contacts.map(contact => ({
                'Company': contact.company || '',
                'First Name': contact.first_name || (contact.name ? contact.name.split(' ')[0] : '') || '',
                'Last Name': contact.last_name || (contact.name ? contact.name.split(' ').slice(1).join(' ') : '') || '',
                'Email': contact.email || '',
                'Phone': contact.phone || '',
                'Job Title': contact.title || '',
                'Location': contact.location || '',
                'Website': contact.website || '',
                'Industry': contact.industry || '',
                'Notes': contact.notes || ''
              }));
              
              // Auto-map the fields
              const autoMapping = {
                'Company': 'company',
                'First Name': 'first_name',
                'Last Name': 'last_name',
                'Email': 'email',
                'Phone': 'phone',
                'Job Title': 'role',
                'Location': 'location',
                'Website': 'website',
                'Industry': 'industry',
                'Notes': 'notes'
              };
              
              setImportMapping(autoMapping);
              setImportPreview(extractedRows.slice(0, 5));
              // Store full data for import
              setImportFile({ name: file.name, fullData: extractedRows });
              setImportStep(2);
              toast.success(`Extracted ${extractedRows.length} contact(s) from PDF`);
            } else {
              toast.error(data.message || 'No contacts found in the PDF');
              setImportFile(null);
            }
          } catch (err) {
            console.error('PDF parsing error:', err);
            toast.error('Failed to parse PDF: ' + err.message);
            setImportFile(null);
          } finally {
            setImportLoading(false);
          }
        };
        reader.readAsDataURL(file);
        return; // Early return since we handle loading state in the reader callback
      } else {
        toast.error('Unsupported file format. Please use CSV or PDF.');
        setImportFile(null);
      }
    } catch (err) {
      console.error('Import error:', err);
      toast.error(err.message || 'Failed to parse file');
      setImportFile(null);
    } finally {
      setImportLoading(false);
    }
  };

  // Execute import
  const executeImport = async () => {
    if (!importFile || importPreview.length === 0) return;
    
    setImportLoading(true);
    try {
      // Handle both CSV files and PDF data (stored in importFile.fullData)
      let rows;
      if (importFile.fullData) {
        // PDF import - data already extracted
        rows = importFile.fullData;
      } else {
        // CSV file - parse it
        const parsed = await parseCSVFile(importFile);
        rows = parsed.rows;
      }
      
      let successCount = 0;
      let errorCount = 0;
      
      // Group rows by company name to merge duplicates
      const companyGroups = {};
      
      for (const row of rows) {
        // Map row data to fields
        const companyData = {
          name: '',
          website: '',
          industry: industryFilter !== 'All' ? industryFilter : 'Other',
          size: '1-10',
          priority: 'B',
          notes: '',
          location: ''
        };
        
        const contactData = {
          name: '',
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          role: '',
          linkedin_url: ''
        };
        
        // Apply mapping
        for (const [csvColumn, fieldName] of Object.entries(importMapping)) {
          const value = row[csvColumn] || '';
          switch (fieldName) {
            case 'company':
              companyData.name = value;
              break;
            case 'website':
              companyData.website = value;
              break;
            case 'industry':
              companyData.industry = value || companyData.industry;
              break;
            case 'location':
              companyData.location = value;
              break;
            case 'first_name':
              contactData.first_name = value;
              break;
            case 'last_name':
              contactData.last_name = value;
              break;
            case 'contact_name':
              // Full name - will be split later
              contactData.name = value;
              break;
            case 'email':
              contactData.email = value;
              break;
            case 'phone':
              contactData.phone = value;
              break;
            case 'role':
              contactData.role = value;
              break;
            case 'linkedin':
              contactData.linkedin_url = value;
              break;
            case 'notes':
              companyData.notes = value;
              break;
          }
        }
        
        // Build full name from first_name and last_name if separate
        if (contactData.first_name || contactData.last_name) {
          contactData.name = `${contactData.first_name} ${contactData.last_name}`.trim();
        }
        
        // Skip if no company name and no contact
        if (!companyData.name && !contactData.name && !contactData.first_name) {
          continue;
        }
        
        // Use contact name as company if company is empty
        if (!companyData.name && contactData.name) {
          companyData.name = `${contactData.name}'s Company`;
        }
        
        // Normalize company name for grouping
        const companyKey = companyData.name.toLowerCase().trim();
        
        if (!companyGroups[companyKey]) {
          companyGroups[companyKey] = {
            companyData,
            contacts: []
          };
        }
        
        // Add contact if we have contact info
        if (contactData.name || contactData.email) {
          companyGroups[companyKey].contacts.push(contactData);
        }
        
        // Merge company data (keep first non-empty values)
        const existing = companyGroups[companyKey].companyData;
        if (!existing.website && companyData.website) existing.website = companyData.website;
        if (!existing.location && companyData.location) existing.location = companyData.location;
        if (existing.industry === 'Other' && companyData.industry !== 'Other') existing.industry = companyData.industry;
        if (!existing.notes && companyData.notes) existing.notes = companyData.notes;
      }
      
      // Now create companies and their contacts
      const companyKeys = Object.keys(companyGroups);
      toast.info(`Found ${companyKeys.length} unique companies from ${rows.length} rows...`);
      
      // Get user_id for ownership
      const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
      const userId = authUser?.id || authUser?.user_id;
      
      for (const companyKey of companyKeys) {
        const { companyData, contacts } = companyGroups[companyKey];
        
        try {
          // Add user_id to company data
          if (userId) {
            companyData.user_id = userId;
          }
          
          // Create company via API
          const response = await fetch(`${API_URL}/api/lead-gen/companies`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(companyData)
          });
          
          if (response.ok) {
            const data = await response.json();
            const companyId = data.company?.id || data.id;
            
            // Add all contacts for this company
            for (const contactData of contacts) {
              if (contactData.name || contactData.email) {
                const contactResponse = await fetch(`${API_URL}/api/lead-gen/contacts`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    ...contactData,
                    company_id: companyId,
                    title: contactData.role,
                    user_id: userId  // Add user ownership
                  })
                });
                
                if (!contactResponse.ok) {
                  console.error('Failed to create contact for company:', companyId);
                }
              }
            }
            
            // Fetch company logo in background
            if (companyData.name && companyId) {
              let domain = companyData.website;
              if (!domain) {
                const cleanName = companyData.name.toLowerCase()
                  .replace(/[^a-z0-9]/g, '')
                  .replace(/\s+/g, '');
                domain = `${cleanName}.com`;
              } else {
                domain = domain.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
              }
              
              fetch(`${API_URL}/api/lead-gen/companies/${companyId}/fetch-logo`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ domain })
              }).catch(e => console.log('Logo fetch failed for', companyData.name));
            }
            
            successCount++;
          } else {
            errorCount++;
          }
        } catch (err) {
          errorCount++;
        }
      }
      
      toast.success(`Imported ${successCount} leads${errorCount > 0 ? ` (${errorCount} failed)` : ''}. Fetching logos...`);
      setShowImportModal(false);
      setImportFile(null);
      setImportPreview([]);
      setImportMapping({});
      setImportStep(1);
      
      // Wait a moment for logo fetches to complete, then refresh
      await new Promise(resolve => setTimeout(resolve, 2000));
      fetchCompanies();
      fetchDashboard();
      
      // Refresh again after more logos have loaded
      setTimeout(() => {
        fetchCompanies();
      }, 5000);
    } catch (err) {
      toast.error('Import failed: ' + err.message);
    } finally {
      setImportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header - Mobile Responsive */}
      <div className="border-b border-gray-800 bg-black/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex-shrink-0">
                <Target className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">Lead Generation</h1>
              </div>
            </div>
            
            {/* Action Buttons - Scrollable on mobile */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
              <button
                onClick={() => setShowTheOrgModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-90 rounded-lg text-white font-medium transition-colors whitespace-nowrap text-sm flex-shrink-0"
              >
                <Building size={16} />
                <span className="hidden sm:inline">TheOrg Search</span>
                <span className="sm:hidden">TheOrg</span>
              </button>
              <button
                onClick={() => setShowLeadFinderModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 rounded-lg text-white font-medium transition-colors whitespace-nowrap text-sm flex-shrink-0"
              >
                <Sparkles size={16} />
                <span className="hidden sm:inline">Find Leads</span>
                <span className="sm:hidden">Find</span>
              </button>
              <button
                onClick={() => setShowRocketReachModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-orange-500 to-red-500 hover:opacity-90 rounded-lg text-white font-medium transition-colors whitespace-nowrap text-sm flex-shrink-0"
                data-testid="rocketreach-search-btn"
              >
                <Rocket size={16} />
                <span className="hidden sm:inline">RocketReach</span>
                <span className="sm:hidden">Rocket</span>
              </button>
              <button
                onClick={exportLeadsToCSV}
                data-testid="leadgen-export-csv-btn"
                title={selectedFolder !== 'all'
                  ? `Export ${sortedCompanies.length} lead${sortedCompanies.length === 1 ? '' : 's'} from the selected folder`
                  : `Export ${sortedCompanies.length} lead${sortedCompanies.length === 1 ? '' : 's'} from the current view`}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-300 transition-colors whitespace-nowrap text-sm flex-shrink-0"
              >
                <Download size={16} />
                <span className="hidden sm:inline">Export CSV</span>
                {selectedFolder !== 'all' && (
                  <span className="hidden md:inline text-[10px] text-amber-300 ml-1">
                    (folder)
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white font-medium transition-colors whitespace-nowrap text-sm flex-shrink-0"
              >
                <Upload size={16} />
                <span className="hidden sm:inline">Import File</span>
                <span className="sm:hidden">Import</span>
              </button>
              <button
                onClick={() => {
                  setSelectedCompany(null);
                  setCompanyModalMode('create');
                  setShowCompanyModal(true);
                }}
                className="flex items-center gap-1.5 px-3 py-2 bg-cyan-500 hover:bg-cyan-400 rounded-lg text-black font-medium transition-colors whitespace-nowrap text-sm flex-shrink-0"
              >
                <Plus size={16} />
                <span className="hidden sm:inline">Add Company</span>
                <span className="sm:hidden">Add</span>
              </button>
            </div>
          </div>

          {/* Tabs - Mobile scrollable */}
          <div className="flex gap-1 mt-3 sm:mt-4 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'pipeline', label: 'Pipeline', icon: TrendingUp },
              { id: 'companies', label: 'Target List', icon: Building },
              { id: 'queue', label: 'Email Queue', icon: Mail },
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-sm sm:text-base flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-cyan-500/20 text-cyan-400'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  <Icon size={16} className="sm:w-[18px] sm:h-[18px]" />
                  <span className="hidden xs:inline">{tab.label}</span>
                  <span className="xs:hidden">{tab.id === 'dashboard' ? 'Dash' : tab.id === 'pipeline' ? 'Pipe' : tab.id === 'companies' ? 'List' : 'Queue'}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboard && (
          <>
            <WeeklyMissionPanel mission={dashboard.weekly_mission} />
            <TodaysActionsPanel actions={dashboard.todays_actions} onRefresh={fetchDashboard} />
            <MetricsPanel metrics={dashboard.metrics} />
          </>
        )}

        {/* Pipeline Tab */}
        {activeTab === 'pipeline' && (
          <PipelineBoard
            pipeline={pipeline}
            onDragEnd={handleDragEnd}
            onEditLead={handleEditLead}
            onGenerateEmail={handleGenerateEmail}
            onDeleteLead={handleDeleteLead}
            onViewLeadDetails={handleViewLeadDetails}
          />
        )}

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <>
            {/* Category Tabs with Scan buttons */}
            <div className="bg-black border border-gray-700 rounded-xl p-3 mb-4 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max">
                {categories.map(cat => {
                  const count = cat.id === 'all' 
                    ? companies.length 
                    : cat.id === 'other'
                      ? companies.filter(c => !categories.filter(ct => ct.id !== 'all' && ct.id !== 'other').map(ct => ct.id).includes((c.industry || '').toLowerCase())).length
                      : companies.filter(c => (c.industry || '').toLowerCase() === cat.id).length;
                  const hasKeywords = cat.keywords && cat.keywords.length > 0;
                  const isScanning = isScanningCategory === cat.id;
                  
                  return (
                    <div key={cat.id} className="flex items-center gap-1">
                      <button
                        onClick={() => setListCategoryTab(cat.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                          listCategoryTab === cat.id
                            ? `${cat.color} text-white shadow-lg`
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                        }`}
                      >
                        <span>{cat.label}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          listCategoryTab === cat.id ? 'bg-white/20' : 'bg-gray-700'
                        }`}>
                          {count}
                        </span>
                      </button>
                      {/* Scan button - only show for categories with keywords (not 'all' or 'other') */}
                      {cat.id !== 'all' && cat.id !== 'other' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleScanCategoryKeywords(cat.id);
                          }}
                          disabled={isScanning}
                          className={`p-1.5 rounded-lg transition-all ${
                            hasKeywords 
                              ? 'bg-purple-500/20 hover:bg-purple-500/40 text-purple-400' 
                              : 'bg-gray-700/50 text-gray-500 hover:bg-gray-700'
                          } ${isScanning ? 'animate-pulse' : ''}`}
                          title={hasKeywords 
                            ? `Scan & categorize: ${cat.keywords.slice(0, 3).join(', ')}${cat.keywords.length > 3 ? '...' : ''}` 
                            : 'No keywords set - click Manage to add'
                          }
                        >
                          {isScanning ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <ScanLine size={14} />
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
                {/* Manage Categories Button */}
                <button
                  onClick={() => setShowCategoryModal(true)}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg font-medium transition-all whitespace-nowrap bg-gray-800/50 text-gray-400 hover:bg-gray-700 hover:text-white border border-dashed border-gray-600"
                >
                  <Settings size={14} />
                  <span>Manage</span>
                </button>
              </div>
            </div>

            {/* Folder Tabs */}
            <div className="bg-black border border-gray-700 rounded-xl p-3 mb-4 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max">
                <div className="flex items-center gap-1 text-gray-500 mr-2">
                  <Folder size={14} />
                  <span className="text-xs font-medium">Folders:</span>
                </div>
                {folders.map(folder => {
                  const count = getCompanyFolderCount(folder.id);
                  return (
                    <button
                      key={folder.id}
                      onClick={() => setSelectedFolder(folder.id)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium transition-all whitespace-nowrap text-sm ${
                        selectedFolder === folder.id
                          ? `${folder.color} text-white shadow-lg`
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      {selectedFolder === folder.id ? (
                        <FolderOpen size={14} />
                      ) : (
                        <Folder size={14} />
                      )}
                      <span>{folder.label}</span>
                      <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                        selectedFolder === folder.id ? 'bg-white/20' : 'bg-gray-700'
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
                {/* Add Folder Button */}
                <button
                  onClick={() => setShowFolderModal(true)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg font-medium transition-all whitespace-nowrap text-sm bg-gray-800/50 text-gray-400 hover:bg-gray-700 hover:text-white border border-dashed border-gray-600"
                >
                  <FolderPlus size={14} />
                  <span>Add Folder</span>
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-black border border-gray-700 rounded-xl p-4 mb-6 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Search className="text-gray-400" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search companies, contacts, emails..."
                  className="bg-transparent border-none outline-none text-white placeholder-gray-500 w-64"
                />
              </div>
              <div className="h-6 w-px bg-gray-700" />
              <select
                value={industryFilter}
                onChange={(e) => setIndustryFilter(e.target.value)}
                className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
              <select
                value={sizeFilter}
                onChange={(e) => setSizeFilter(e.target.value)}
                className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                {COMPANY_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="All">All Priorities</option>
                {PRIORITIES.map(p => <option key={p} value={p}>Priority {p}</option>)}
              </select>
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white font-medium transition-colors"
              >
                <Upload size={16} />
                Import File
              </button>
              <div className="ml-auto flex items-center gap-3">
                <div
                  className="inline-flex items-center bg-gray-900 border border-gray-700 rounded-lg p-0.5"
                  data-testid="lead-list-view-toggle"
                  role="tablist"
                  aria-label="List view"
                >
                  <button
                    type="button"
                    onClick={() => setListViewMode('table')}
                    aria-pressed={listViewMode === 'table'}
                    data-testid="lead-list-view-table-btn"
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                      listViewMode === 'table'
                        ? 'bg-cyan-500/20 text-cyan-300'
                        : 'text-gray-400 hover:text-white'
                    }`}
                    title="Table view"
                  >
                    <ListIcon size={14} />
                    List
                  </button>
                  <button
                    type="button"
                    onClick={() => setListViewMode('grid')}
                    aria-pressed={listViewMode === 'grid'}
                    data-testid="lead-list-view-grid-btn"
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                      listViewMode === 'grid'
                        ? 'bg-cyan-500/20 text-cyan-300'
                        : 'text-gray-400 hover:text-white'
                    }`}
                    title="Grid view"
                  >
                    <LayoutGrid size={14} />
                    Grid
                  </button>
                </div>
                <div className="text-sm text-gray-400">
                  {sortedCompanies.length} companies
                </div>
              </div>
            </div>

            {listViewMode === 'grid' ? (
              <CompaniesGrid
                companies={sortedCompanies}
                onEdit={(company) => {
                  setSelectedCompany(company);
                  setCompanyModalMode('edit');
                  setShowCompanyModal(true);
                }}
                onCreateLead={handleCreateLead}
                onEnrich={handleEnrichCompany}
                onSendToQueue={handleOpenQuickSendModal}
                onAddToFolder={openAddToFolderModal}
                companyFolders={companyFolders}
              />
            ) : (
              <CompaniesTable
                companies={sortedCompanies}
                onEdit={(company) => {
                  setSelectedCompany(company);
                  setCompanyModalMode('edit');
                  setShowCompanyModal(true);
                }}
                onCreateLead={handleCreateLead}
                onEnrich={handleEnrichCompany}
                onBulkDelete={handleBulkDeleteCompanies}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                onAddToFolder={openAddToFolderModal}
                companyFolders={companyFolders}
                folders={folders}
                categories={categories}
                onBulkMoveToFolder={handleBulkMoveToFolder}
                onBulkMoveToCategory={handleBulkMoveToCategory}
                onSendToQueue={handleOpenQuickSendModal}
                onBulkSendToQueue={handleOpenBulkEmailModal}
              />
            )}
          </>
        )}

        {/* Email Queue Tab */}
        {activeTab === 'queue' && (
          <div className="space-y-6">
            {/* Nylas Email Connection Status */}
            <div className="bg-black border border-gray-700 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    nylasStatus?.connected ? 'bg-emerald-500/20' : 'bg-gray-800'
                  }`}>
                    <Mail className={nylasStatus?.connected ? 'text-emerald-400' : 'text-gray-500'} size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-white">Email Connection</h3>
                    {nylasLoading ? (
                      <p className="text-sm text-gray-400 flex items-center gap-2">
                        <Loader2 size={14} className="animate-spin" />
                        Checking connection...
                      </p>
                    ) : nylasStatus?.connected ? (
                      <p className="text-sm text-green-400 flex items-center gap-1">
                        <CheckCircle size={14} />
                        Connected as {nylasStatus.email}
                        {nylasStatus.provider && <span className="text-gray-400 ml-1">({nylasStatus.provider})</span>}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-400">Connect your Gmail, Outlook, or any email</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {nylasLoading ? (
                    <div className="px-4 py-2 bg-gray-700 text-gray-400 rounded-lg font-medium flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      Checking...
                    </div>
                  ) : nylasStatus?.connected ? (
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
                        Active
                      </span>
                      {nylasAccounts.length > 1 && (
                        <span className="text-xs text-gray-400">+{nylasAccounts.length - 1} more</span>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={initiateNylasConnect}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      <Mail size={18} />
                      Connect Email
                    </button>
                  )}
                  <button
                    onClick={fetchNylasStatus}
                    disabled={nylasLoading}
                    className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                    title="Refresh connection status"
                  >
                    <RefreshCw size={18} className={nylasLoading ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>
              
              {/* Connected Accounts List */}
              {nylasAccounts.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <p className="text-xs text-gray-500 mb-2">Connected Accounts:</p>
                  <div className="flex flex-wrap gap-2">
                    {nylasAccounts.map((account, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 rounded-lg text-sm"
                      >
                        <div className="w-2 h-2 rounded-full bg-green-400"></div>
                        <span className="text-white">{account.email}</span>
                        {account.provider && (
                          <span className="text-xs text-gray-500 capitalize">{account.provider}</span>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={initiateNylasConnect}
                      className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      <Plus size={14} />
                      Add Account
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Email Queue */}
            <div className="bg-black border border-gray-700 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-medium text-white flex items-center gap-2">
                  <Send className="text-cyan-400" size={20} />
                  Email Queue
                  <span className="text-sm text-gray-400 ml-2">({emailQueue.length} emails)</span>
                </h3>
                <div className="flex items-center gap-2">
                  {/* Bulk Actions */}
                  {selectedQueueIds.length > 0 && (
                    <button
                      onClick={handleBulkSendQueue}
                      disabled={bulkSendingQueue || !nylasStatus?.connected}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-black font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {bulkSendingQueue ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Sending {selectedQueueIds.length}...
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          Send {selectedQueueIds.length} Selected
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={fetchEmailQueue}
                    className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
                  >
                    <RefreshCw size={18} />
                  </button>
                </div>
              </div>

              {/* Select All Bar */}
              {emailQueue.length > 0 && (
                <div className="px-6 py-2 bg-gray-900/50 border-b border-gray-800 flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedQueueIds.length === emailQueue.length && emailQueue.length > 0}
                      onChange={toggleSelectAllQueue}
                      className="w-4 h-4 rounded bg-gray-800 border-gray-600 text-cyan-500 focus:ring-cyan-500"
                    />
                    <span className="text-sm text-gray-400">
                      {selectedQueueIds.length === emailQueue.length ? 'Deselect All' : 'Select All'}
                    </span>
                  </label>
                  {selectedQueueIds.length > 0 && (
                    <span className="text-sm text-cyan-400">
                      {selectedQueueIds.length} of {emailQueue.length} selected
                    </span>
                  )}
                </div>
              )}

              {emailQueueLoading ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mx-auto" />
                </div>
              ) : emailQueue.length === 0 ? (
                <div className="p-12 text-center">
                  <Mail className="mx-auto text-gray-600 mb-4" size={48} />
                  <h4 className="text-lg font-medium text-white mb-2">No emails in queue</h4>
                  <p className="text-gray-400 max-w-md mx-auto">
                    Generate and approve email drafts from the Pipeline to add them here.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-800">
                  {emailQueue.map((item) => (
                    <div 
                      key={item.id} 
                      className={`p-4 hover:bg-gray-900/50 transition-colors ${selectedQueueIds.includes(item.id) ? 'bg-cyan-500/10' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Checkbox */}
                        <div className="pt-1">
                          <input
                            type="checkbox"
                            checked={selectedQueueIds.includes(item.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleQueueSelection(item.id);
                            }}
                            className="w-4 h-4 rounded bg-gray-800 border-gray-600 text-cyan-500 focus:ring-cyan-500"
                          />
                        </div>
                        {/* Card Content */}
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => handleEditQueueItem(item)}
                        >
                          <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium text-white truncate">
                              {item.company?.name || item.company?.company_name || item.lead?.company?.name || item.recipient_name || 'Unknown Company'}
                            </span>
                            {(item.company?.priority || item.lead?.company?.priority) && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                (item.company?.priority || item.lead?.company?.priority) === 'A' ? 'bg-red-500/20 text-red-400' :
                                (item.company?.priority || item.lead?.company?.priority) === 'B' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {item.company?.priority || item.lead?.company?.priority}
                              </span>
                            )}
                            <span className="text-xs text-gray-500 ml-auto">Click to edit</span>
                          </div>
                          <div className="text-sm text-cyan-400 mb-1 truncate">
                            To: {item.recipient_email || 'No email set'}
                          </div>
                          <div className="text-sm text-gray-300 mb-2 font-medium">
                            Subject: {item.subject}
                          </div>
                          <div className="text-sm text-gray-500 line-clamp-2">
                            {item.body?.slice(0, 150)}...
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              Scheduled: {item.scheduled_date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {formatTimeTo12Hour(item.scheduled_time)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleEditQueueItem(item)}
                            className="p-2 hover:bg-cyan-500/20 text-gray-400 hover:text-cyan-400 rounded-lg transition-colors"
                            title="Edit email"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleSendNowDirect(item)}
                            disabled={!nylasStatus?.connected || sendingEmailId === item.id}
                            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {sendingEmailId === item.id ? (
                              <>
                                <Loader2 size={16} className="animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send size={16} />
                                Send Now
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleRemoveFromQueue(item.id)}
                            className="p-2 hover:bg-red-500/20 text-gray-400 hover:text-red-400 rounded-lg transition-colors"
                            title="Remove from queue"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Tips */}
            {nylasStatus?.connected && emailQueue.length > 0 && (
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-cyan-400 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <h4 className="text-cyan-400 font-medium mb-1">Tips for Cold Outreach</h4>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Best send times: Tuesday-Thursday, 9-11 AM recipient's timezone</li>
                      <li>• Keep subject lines under 50 characters for better open rates</li>
                      <li>• Follow up 3-5 days after initial outreach if no response</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <CompanyModal
        isOpen={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        company={selectedCompany}
        onSave={handleSaveCompany}
        onDelete={handleDeleteCompany}
        mode={companyModalMode}
        categoryOptions={categories}
      />

      <LeadDetailModal
        isOpen={showLeadDetailModal}
        onClose={() => setShowLeadDetailModal(false)}
        lead={leadToView}
        onEdit={handleEditLead}
        onGenerateEmail={handleGenerateEmail}
      />

      <EmailDraftModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        lead={selectedLead}
        company={selectedLead?.company}
        onApprove={handleApproveEmail}
      />

      <CSVImportModal
        isOpen={showCSVModal}
        onClose={() => setShowCSVModal(false)}
        onImport={handleImportCSV}
      />

      <EditQueueModal
        isOpen={showEditQueueModal}
        onClose={() => setShowEditQueueModal(false)}
        queueItem={selectedQueueItem}
        onSave={handleUpdateQueueItem}
      />

      <EditLeadModal
        isOpen={showEditLeadModal}
        onClose={() => setShowEditLeadModal(false)}
        lead={selectedLead}
        onSave={handleUpdateLead}
        onDelete={handleDeleteLead}
      />

      <LeadFinderModal
        isOpen={showLeadFinderModal}
        onClose={() => setShowLeadFinderModal(false)}
        onLeadsFound={() => {
          fetchCompanies();
          fetchDashboard();
        }}
      />

      {/* TheOrg Lead Finder Modal */}
      <TheOrgLeadFinderModal
        isOpen={showTheOrgModal}
        onClose={() => setShowTheOrgModal(false)}
        theOrgSearch={theOrgSearch}
        setTheOrgSearch={setTheOrgSearch}
        theOrgIndustry={theOrgIndustry}
        setTheOrgIndustry={setTheOrgIndustry}
        theOrgResults={theOrgResults}
        theOrgLoading={theOrgLoading}
        searchTheOrg={searchTheOrg}
        theOrgSelectedCompany={theOrgSelectedCompany}
        setTheOrgSelectedCompany={setTheOrgSelectedCompany}
        theOrgOrgChart={theOrgOrgChart}
        theOrgOrgChartLoading={theOrgOrgChartLoading}
        fetchTheOrgOrgChart={fetchTheOrgOrgChart}
        importTheOrgLead={importTheOrgLead}
        importingLead={importingLead}
        theOrgCredits={theOrgCredits}
        fetchTheOrgCredits={fetchTheOrgCredits}
        theOrgExecutiveSearch={theOrgExecutiveSearch}
        setTheOrgExecutiveSearch={setTheOrgExecutiveSearch}
        enrichTheOrgContact={enrichTheOrgContact}
        enrichingContact={enrichingContact}
        enrichedContacts={enrichedContacts}
      />

      {/* Send Email Modal */}
      {showSendEmailModal && emailToSend && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowSendEmailModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-cyan-500/20 rounded-lg">
                    <Send className="text-cyan-400" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Send Email</h3>
                    <p className="text-sm text-gray-400">Review and send your email</p>
                  </div>
                </div>
                <button onClick={() => setShowSendEmailModal(false)} className="p-2 hover:bg-gray-800 rounded-lg">
                  <X className="text-gray-400" size={20} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
              {/* From Account */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">From</label>
                <select
                  value={selectedFromAccount}
                  onChange={(e) => setSelectedFromAccount(e.target.value)}
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                >
                  {nylasAccounts.map(account => (
                    <option key={account.grant_id} value={account.grant_id}>
                      {account.email} {account.provider ? `(${account.provider})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* To */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">To</label>
                <div className="flex items-center gap-3 px-4 py-3 bg-black border border-gray-600 rounded-lg">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {emailToSend.recipient_name?.split(' ').map(n => n[0]).join('').substring(0, 2) || '?'}
                  </div>
                  <div>
                    <div className="text-white text-sm">{emailToSend.recipient_name}</div>
                    <div className="text-gray-400 text-xs">{emailToSend.recipient_email}</div>
                  </div>
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Subject</label>
                <input
                  type="text"
                  value={editedSubject}
                  onChange={(e) => setEditedSubject(e.target.value)}
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                  placeholder="Email subject..."
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                <textarea
                  value={editedBody}
                  onChange={(e) => setEditedBody(e.target.value)}
                  rows={8}
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none resize-none"
                  placeholder="Email body..."
                />
              </div>

              {/* Schedule Options */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">When to send</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setSendSchedule('now')}
                    className={`flex-1 px-4 py-3 rounded-lg border transition-colors flex items-center justify-center gap-2 ${
                      sendSchedule === 'now' 
                        ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400' 
                        : 'bg-black border-gray-600 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <Send size={16} />
                    Send Now
                  </button>
                  <button
                    onClick={() => setSendSchedule('later')}
                    className={`flex-1 px-4 py-3 rounded-lg border transition-colors flex items-center justify-center gap-2 ${
                      sendSchedule === 'later' 
                        ? 'bg-purple-500/20 border-purple-500 text-purple-400' 
                        : 'bg-black border-gray-600 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <Clock size={16} />
                    Schedule
                  </button>
                </div>
              </div>

              {/* Schedule Time Picker */}
              {sendSchedule === 'later' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Schedule for</label>
                  <input
                    type="datetime-local"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tip: Best times are Tuesday-Thursday, 9-11 AM in recipient's timezone
                  </p>
                </div>
              )}

              {/* Company Info */}
              {(emailToSend.company || emailToSend.recipient_name) && (
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold overflow-hidden">
                      {emailToSend.company?.logo_url ? (
                        <img src={emailToSend.company.logo_url} alt="" className="w-full h-full object-contain p-1" />
                      ) : (
                        (emailToSend.company?.name || emailToSend.recipient_name || '?')?.charAt(0)
                      )}
                    </div>
                    <div>
                      <div className="text-white text-sm font-medium">
                        {emailToSend.company?.name || emailToSend.company?.company_name || emailToSend.recipient_name || 'Unknown'}
                      </div>
                      <div className="text-gray-400 text-xs">
                        {emailToSend.company?.industry || emailToSend.recipient_email || ''}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => setShowSendEmailModal(false)}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmailId === emailToSend.id || (sendSchedule === 'later' && !scheduledTime)}
                className="flex-1 px-4 py-3 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {sendingEmailId === emailToSend.id ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending...
                  </>
                ) : sendSchedule === 'later' ? (
                  <>
                    <Clock size={18} />
                    Schedule Email
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import File Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <Upload className="text-emerald-400" size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Import Leads</h2>
                  <p className="text-sm text-gray-400">
                    {importStep === 1 && 'Upload a CSV file with your leads'}
                    {importStep === 2 && 'Map columns to fields'}
                    {importStep === 3 && 'Review and import'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportPreview([]);
                  setImportMapping({});
                  setImportStep(1);
                }}
                className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Step 1: Upload */}
              {importStep === 1 && (
                <div className="space-y-6">
                  <div 
                    className="border-2 border-dashed border-gray-600 rounded-xl p-12 text-center hover:border-emerald-500 transition-colors cursor-pointer"
                    onClick={() => document.getElementById('import-file-input').click()}
                  >
                    <Upload className="mx-auto text-gray-500 mb-4" size={48} />
                    <h3 className="text-lg font-medium text-white mb-2">
                      Drop your file here or click to browse
                    </h3>
                    <p className="text-gray-400 text-sm mb-4">
                      Supports CSV files with columns like: Company, Contact Name, Email, Phone, Role, etc.
                    </p>
                    <p className="text-emerald-400 text-xs">
                      PDF files with contact lists are also supported (AI extraction)
                    </p>
                    <input
                      id="import-file-input"
                      type="file"
                      accept=".csv,.pdf,text/csv,application/pdf"
                      onChange={handleFileImport}
                      className="hidden"
                    />
                  </div>

                  <div className="bg-gray-800/50 rounded-xl p-4">
                    <h4 className="text-sm font-medium text-white mb-3">Expected CSV Format</h4>
                    <div className="bg-black rounded-lg p-3 font-mono text-xs text-gray-400 overflow-x-auto">
                      <div>Company,Contact Name,Email,Phone,Role,Website,Industry</div>
                      <div className="text-emerald-400">Acme Corp,John Doe,john@acme.com,555-1234,CEO,acme.com,Technology</div>
                      <div className="text-emerald-400">Beta Inc,Jane Smith,jane@beta.io,555-5678,CTO,beta.io,Healthcare</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Map Columns */}
              {importStep === 2 && importPreview.length > 0 && (
                <div className="space-y-6">
                  <div className="bg-gray-800/50 rounded-xl p-4">
                    <h4 className="text-sm font-medium text-white mb-4">Map CSV Columns to Lead Fields</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.keys(importPreview[0] || {}).map(csvColumn => (
                        <div key={csvColumn} className="flex items-center gap-3">
                          <div className="flex-1">
                            <label className="text-xs text-gray-500 block mb-1">CSV Column</label>
                            <div className="px-3 py-2 bg-gray-900 rounded-lg text-white text-sm truncate">
                              {csvColumn}
                            </div>
                          </div>
                          <ArrowRight size={16} className="text-gray-500 flex-shrink-0" />
                          <div className="flex-1">
                            <label className="text-xs text-gray-500 block mb-1">Map to Field</label>
                            <select
                              value={importMapping[csvColumn] || ''}
                              onChange={(e) => setImportMapping({ ...importMapping, [csvColumn]: e.target.value })}
                              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
                            >
                              <option value="">Skip this column</option>
                              <option value="company">Company Name</option>
                              <option value="first_name">First Name</option>
                              <option value="last_name">Last Name</option>
                              <option value="contact_name">Full Name (will split)</option>
                              <option value="email">Email</option>
                              <option value="phone">Phone</option>
                              <option value="role">Role/Title</option>
                              <option value="website">Website</option>
                              <option value="industry">Industry</option>
                              <option value="location">Location</option>
                              <option value="linkedin">LinkedIn URL</option>
                              <option value="notes">Notes</option>
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Preview */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-white">Preview (first 5 rows)</h4>
                      {importFile?.fullData && importFile.fullData.length > 5 && (
                        <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">
                          +{importFile.fullData.length - 5} more rows will be imported
                        </span>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-700">
                            {Object.keys(importPreview[0] || {}).map(col => (
                              <th key={col} className="px-3 py-2 text-left text-gray-400 font-medium whitespace-nowrap">
                                {col}
                                {importMapping[col] && (
                                  <span className="ml-1 text-emerald-400 text-xs">→ {importMapping[col]}</span>
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {importPreview.map((row, idx) => (
                            <tr key={idx} className="border-b border-gray-800">
                              {Object.values(row).map((val, i) => (
                                <td key={i} className="px-3 py-2 text-white whitespace-nowrap max-w-[200px] truncate">
                                  {val || <span className="text-gray-600">-</span>}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
              <div className="text-sm text-gray-400">
                {importFile && (
                  <span>
                    File: {importFile.name}
                    {importFile.fullData && (
                      <span className="ml-2 text-emerald-400 font-medium">
                        ({importFile.fullData.length} contacts to import)
                      </span>
                    )}
                    {!importFile.fullData && importPreview.length > 0 && (
                      <span className="ml-2 text-emerald-400 font-medium">
                        (Showing preview of first 5 rows)
                      </span>
                    )}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {importStep > 1 && (
                  <button
                    onClick={() => setImportStep(importStep - 1)}
                    className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Back
                  </button>
                )}
                {importStep === 2 && (
                  <button
                    onClick={executeImport}
                    disabled={importLoading || Object.keys(importMapping).length === 0}
                    className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    {importLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Check size={16} />
                        Import Leads
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Tag className="text-purple-400" size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Manage Categories</h2>
                  <p className="text-sm text-gray-400">Add, edit, or remove lead categories with keywords for auto-scan</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowCategoryModal(false);
                  setEditingCategory(null);
                  setNewCategoryName('');
                  setNewCategoryColor('bg-blue-500');
                  setNewCategoryKeywords('');
                }}
                className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Add/Edit Category Form */}
              <div className="bg-gray-800/50 rounded-xl p-4 mb-6">
                <h3 className="text-sm font-medium text-white mb-4">
                  {editingCategory ? 'Edit Category' : 'Add New Category'}
                </h3>
                <div className="space-y-4">
                  <div className="flex flex-wrap items-end gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-xs text-gray-400 mb-2">Category Name</label>
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="e.g., Entertainment, Food & Beverage..."
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Color</label>
                      <div className="flex gap-2">
                        {[
                          'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500',
                          'bg-pink-500', 'bg-orange-500', 'bg-cyan-500', 'bg-red-500',
                          'bg-indigo-500', 'bg-teal-500', 'bg-lime-500', 'bg-amber-500'
                        ].map(color => (
                          <button
                            key={color}
                            onClick={() => setNewCategoryColor(color)}
                            className={`w-8 h-8 rounded-lg ${color} transition-transform ${
                              newCategoryColor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900 scale-110' : 'hover:scale-105'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Keywords Input */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-2">
                      <span className="flex items-center gap-1">
                        <ScanLine size={12} />
                        Keywords for Auto-Scan (comma-separated)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={newCategoryKeywords}
                      onChange={(e) => setNewCategoryKeywords(e.target.value)}
                      placeholder="e.g., tech, software, saas, startup, app"
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      These keywords will match against company name, notes, website, and contact info when you click Scan
                    </p>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    {editingCategory ? (
                      <>
                        <button
                          onClick={() => {
                            setEditingCategory(null);
                            setNewCategoryName('');
                            setNewCategoryColor('bg-blue-500');
                            setNewCategoryKeywords('');
                          }}
                          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleUpdateCategory}
                          className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                        >
                          <Check size={16} />
                          Update
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleAddCategory}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                      >
                        <Plus size={16} />
                        Add Category
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Categories List */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-medium text-white">Current Categories ({categories.length})</h3>
                    <p className="text-xs text-gray-500 mt-1">Drag to reorder or use arrows</p>
                  </div>
                  <button
                    onClick={handleResetCategories}
                    className="text-xs text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
                  >
                    <RotateCcw size={12} />
                    Reset to Defaults
                  </button>
                </div>
                <div className="space-y-2">
                  {categories.map((cat, index) => (
                    <div
                      key={cat.id}
                      draggable={true}
                      onDragStart={(e) => handleCategoryDragStart(e, cat.id)}
                      onDragOver={(e) => handleCategoryDragOver(e, cat.id)}
                      onDrop={(e) => handleCategoryDrop(e, cat.id)}
                      onDragEnd={handleCategoryDragEnd}
                      className={`p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-all cursor-move ${
                        draggedCategoryId === cat.id ? 'opacity-50 ring-2 ring-purple-500' : ''
                      } ${draggedCategoryId && draggedCategoryId !== cat.id ? 'border-t-2 border-transparent hover:border-purple-500' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {/* Drag Handle */}
                          <div className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-300">
                            <GripVertical size={16} />
                          </div>
                          <div className={`w-4 h-4 rounded ${cat.color}`}></div>
                          <span className="text-white font-medium">{cat.label}</span>
                          {cat.isDefault && (
                            <span className="text-xs px-2 py-0.5 bg-gray-700 rounded text-gray-400">Default</span>
                          )}
                          <span className="text-xs text-gray-500">
                            ({companies.filter(c => 
                              cat.id === 'all' ? true :
                              cat.id === 'other' ? !categories.filter(ct => ct.id !== 'all' && ct.id !== 'other').map(ct => ct.id).includes((c.industry || '').toLowerCase()) :
                              (c.industry || '').toLowerCase() === cat.id
                            ).length} leads)
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* Up/Down Arrows */}
                          <button
                            onClick={() => moveCategoryUp(cat.id)}
                            disabled={index === 0}
                            className="p-1.5 hover:bg-gray-700 rounded text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Move up"
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            onClick={() => moveCategoryDown(cat.id)}
                            disabled={index === categories.length - 1}
                            className="p-1.5 hover:bg-gray-700 rounded text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Move down"
                          >
                            <ChevronDown size={14} />
                          </button>
                          {cat.id !== 'all' && (
                            <>
                              <button
                                onClick={() => handleEditCategory(cat)}
                                className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
                                title="Edit category"
                              >
                                <Edit size={14} />
                              </button>
                              {!cat.isDefault && (
                                <button
                                  onClick={() => handleDeleteCategory(cat.id)}
                                  className="p-2 hover:bg-red-500/20 rounded-lg text-gray-400 hover:text-red-400 transition-colors"
                                  title="Delete category"
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {/* Show keywords if any */}
                      {cat.keywords && cat.keywords.length > 0 && (
                        <div className="mt-2 pl-7">
                          <div className="flex items-center gap-1 flex-wrap">
                            <ScanLine size={10} className="text-purple-400" />
                            {cat.keywords.slice(0, 8).map((kw, idx) => (
                              <span key={idx} className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded">
                                {kw}
                              </span>
                            ))}
                            {cat.keywords.length > 8 && (
                              <span className="text-xs text-gray-500">+{cat.keywords.length - 8} more</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-end">
              <button
                onClick={() => {
                  setShowCategoryModal(false);
                  setEditingCategory(null);
                  setNewCategoryName('');
                  setNewCategoryColor('bg-blue-500');
                  setNewCategoryKeywords('');
                }}
                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Folder Management Modal */}
      {showFolderModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <Folder className="text-yellow-400" size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Manage Folders</h2>
                  <p className="text-sm text-gray-400">Create folders to organize your contacts</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowFolderModal(false);
                  setEditingFolder(null);
                  setNewFolderName('');
                  setNewFolderColor('bg-blue-500');
                }}
                className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Add/Edit Folder Form */}
              <div className="bg-gray-800/50 rounded-xl p-4 mb-6">
                <h3 className="text-sm font-medium text-white mb-4">
                  {editingFolder ? 'Edit Folder' : 'Create New Folder'}
                </h3>
                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-xs text-gray-400 mb-2">Folder Name</label>
                    <input
                      type="text"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      placeholder="e.g., Hot Leads, Follow Up, VIP..."
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-2">Color</label>
                    <div className="flex gap-2">
                      {[
                        'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500',
                        'bg-pink-500', 'bg-orange-500', 'bg-cyan-500', 'bg-red-500'
                      ].map(color => (
                        <button
                          key={color}
                          onClick={() => setNewFolderColor(color)}
                          className={`w-7 h-7 rounded-lg ${color} transition-transform ${
                            newFolderColor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900 scale-110' : 'hover:scale-105'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {editingFolder ? (
                      <>
                        <button
                          onClick={() => {
                            setEditingFolder(null);
                            setNewFolderName('');
                            setNewFolderColor('bg-blue-500');
                          }}
                          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleUpdateFolder}
                          className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                        >
                          <Check size={16} />
                          Update
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleAddFolder}
                        className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                      >
                        <FolderPlus size={16} />
                        Create Folder
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Folders List */}
              <div>
                <h3 className="text-sm font-medium text-white mb-4">Your Folders ({folders.length})</h3>
                <div className="space-y-2">
                  {folders.map(folder => (
                    <div
                      key={folder.id}
                      className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg ${folder.color} flex items-center justify-center`}>
                          <Folder size={16} className="text-white" />
                        </div>
                        <span className="text-white font-medium">{folder.label}</span>
                        {folder.isDefault && (
                          <span className="text-xs px-2 py-0.5 bg-gray-700 rounded text-gray-400">Default</span>
                        )}
                        <span className="text-xs text-gray-500">
                          ({getCompanyFolderCount(folder.id)} contacts)
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {!folder.isDefault && (
                          <>
                            <button
                              onClick={() => handleEditFolder(folder)}
                              className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
                              title="Edit folder"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteFolder(folder.id)}
                              className="p-2 hover:bg-red-500/20 rounded-lg text-gray-400 hover:text-red-400 transition-colors"
                              title="Delete folder"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-end">
              <button
                onClick={() => {
                  setShowFolderModal(false);
                  setEditingFolder(null);
                  setNewFolderName('');
                  setNewFolderColor('bg-blue-500');
                }}
                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Folder Modal */}
      {showAddToFolderModal && companyToAddToFolder && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <FolderPlus className="text-yellow-400" size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Add to Folder</h2>
                  <p className="text-sm text-gray-400">{companyToAddToFolder.name || companyToAddToFolder.company_name}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAddToFolderModal(false);
                  setCompanyToAddToFolder(null);
                }}
                className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-sm text-gray-400 mb-4">Select folders for this contact:</p>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {folders.filter(f => f.id !== 'all').map(folder => {
                  const isInFolder = companyFolders[companyToAddToFolder.id]?.includes(folder.id);
                  return (
                    <button
                      key={folder.id}
                      onClick={() => handleAddCompanyToFolder(companyToAddToFolder.id, folder.id)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                        isInFolder
                          ? `${folder.color} text-white`
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {isInFolder ? (
                          <FolderOpen size={18} />
                        ) : (
                          <Folder size={18} />
                        )}
                        <span className="font-medium">{folder.label}</span>
                      </div>
                      {isInFolder && (
                        <CheckCircle size={18} />
                      )}
                    </button>
                  );
                })}
                {folders.filter(f => f.id !== 'all').length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Folder className="mx-auto mb-2" size={32} />
                    <p>No folders yet</p>
                    <button
                      onClick={() => {
                        setShowAddToFolderModal(false);
                        setShowFolderModal(true);
                      }}
                      className="mt-2 text-yellow-400 hover:text-yellow-300 text-sm"
                    >
                      Create your first folder
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowAddToFolderModal(false);
                  setShowFolderModal(true);
                }}
                className="text-sm text-yellow-400 hover:text-yellow-300 flex items-center gap-1"
              >
                <FolderPlus size={14} />
                Create New Folder
              </button>
              <button
                onClick={() => {
                  setShowAddToFolderModal(false);
                  setCompanyToAddToFolder(null);
                }}
                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Email Modal */}
      <BulkEmailModal
        isOpen={showBulkEmailModal}
        onClose={() => {
          setShowBulkEmailModal(false);
          setBulkEmailCompanies([]);
        }}
        companies={bulkEmailCompanies}
        nylasAccounts={nylasAccounts}
        onSendToQueue={handleBulkSendToQueue}
      />

      {/* Quick Send to Queue Modal */}
      <QuickSendToQueueModal
        isOpen={showQuickSendModal}
        onClose={() => {
          setShowQuickSendModal(false);
          setQuickSendCompany(null);
        }}
        company={quickSendCompany}
        nylasAccounts={nylasAccounts}
        onSendToQueue={handleBulkSendToQueue}
      />

      {/* RocketReach Search Modal */}
      <RocketReachModal
        isOpen={showRocketReachModal}
        onClose={() => setShowRocketReachModal(false)}
        onImportContact={handleRocketReachImportContact}
        onImportCompany={handleRocketReachImportCompany}
      />
    </div>
  );
};

export default UltimateLeadGeneration;
